import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, AlertTriangle, Ruler, Trash2, Check, Ban, Move, Download, Sparkles, ChevronLeft, ChevronRight, Maximize2, Minimize2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import writeThrough from "@/lib/write_through";
import MarksPanel from "@/components/estimate/phototakeoff/MarksPanel";
import { OpeningSizeFields } from "@/components/estimate/phototakeoff/MarksPanel";
// SEND-298b — the gutter line's own two COUNTS (drops, elbows), asked on the
// GUTTER step. One component, one `patchMark`, no step of their own.
import { GutterCountFields, GUTTER_COUNTS_BASIS } from "@/components/estimate/phototakeoff/MarksPanel";
// SEND-261 — the openings rail's STYLE row reads THE SEND-254 LIST ITSELF.
// One list, one place: no eleventh type and no second copy to drift.
import { OPENING_STYLES } from "@/components/estimate/phototakeoff/MarksPanel";
import QuantitiesPanel from "@/components/estimate/phototakeoff/QuantitiesPanel";
import ScalePanel from "@/components/estimate/phototakeoff/ScalePanel";
// SEND-143 — PHASE 2 LINEAR RUNS: J-channel from the drawn opening box,
// the gable rake from the lines drawn with the triangle, and a named
// refusal row for every trim whose mark does not exist yet.
// SEND-144 — the finished AI read hands off STARTING ZONES; the candidate
// edges of a body zone show what a trim run would follow, with no LF.
import CandidateEdges from "@/components/estimate/phototakeoff/CandidateEdges";
import TrimPanel from "@/components/estimate/phototakeoff/TrimPanel";
// SEND-204 — THE WALK MOVED HERE FROM THE RETIRED GUIDED CAPTURE WIZARD.
// One editor, one canvas, one step at a time, and each step writes its own
// layer. Every step is a tool this editor already had; a layer is the mark's
// own `kind`. No new mark kind, no new route, no second anchor schema.
import WalkBar from "@/components/estimate/phototakeoff/WalkBar";
import JumpRow from "@/components/estimate/phototakeoff/JumpRow";
// SEND-259 — THE SIDING COLOR PICKER ON THE PHOTO RAIL. Its lists come from
// the estimate page's own catalog module; this editor declares no color name.
// SEND-260 — the same row now serves Vertical B&B and Shakes, each writing its
// OWN estimate field.
// SEND-262 — and J / outside corner / inside corner / finish trim, all four
// writing the ONE accessories field.
// SEND-264 — COLOR FOLLOWS THE PRODUCT: the accessories row is the OUTSIDE
// CORNER'S alone, and the J rail carries one only for SOFFIT J.
// SEND-270 — and the WALK'S SOFFIT STEP carries its own COLOR row, filtered by
// the soffit PRODUCT picked on that step. It rides the mark, not the estimate.
import ProductColorRow, { AccessoryColorRow, ACCESSORY_FIELD,
  ACCESSORY_TOOLS, SoffitStepColorRow,
  soffitSeriesTiered, JStepColorRow, J_COLOR_LABEL,
  COLOR_FIELD_FOR_SERIES, COLOR_LABEL_FOR_FIELD,
  // SEND-291 — a rider READS ITS PARENT: the map from a J product to the
  // parent field it trims, and the one row the colourless riders now carry.
  J_PARENT_FIELD, SOFFIT_J_FIELD, RiderColorRow,
  // SEND-293 — the catalog's own answer to "which collection is this colour
  // in", for the siding side and the soffit side.
  tierForColor, soffitTierForColor }
  from "@/components/estimate/phototakeoff/SidingColorPicker";
// SEND-290 — THE COLOR ROW'S LISTS, from the estimate page's own dropdown
// source and nowhere else: no colour name is declared in this file.
import { vinylSidingColorGroupsForEstimate, accessoryColorGroupsForEstimate,
  SOFFIT_COLOR_GROUPS, BOARD_BATTEN_COLOR_GROUPS, SHAKE_COLOR_GROUPS,
  GUTTER_COLORS }
  from "@/lib/colorOptions";
// SEND-323 — the Gutter step's colours, both walks: the aluminum gutter palette
// (`GUTTER_COLORS` from the one color book) with its catch-all row dropped. Its
// own field (gutter_color), so a gutter pick never touches soffit or fascia. No
// colour NAME is declared here — the names come only from the color book.
const GUTTER_WALK_COLOR_GROUPS = [{
  label: "Aluminum gutter colors",
  colors: GUTTER_COLORS.filter((c) => c !== "Other"),
}];
import WalkPane from "@/components/estimate/phototakeoff/WalkPane";
import LayersPanel from "@/components/estimate/phototakeoff/LayersPanel";
import { WALK_STEPS as WALK_STEPS_ALL } from "@/components/estimate/phototakeoff/walk";
// SEND-310 follow-up (Howard ruled 2026-06) — ON AN LP SMARTSIDE ESTIMATE
// THESE FOUR STEPS ARE OFF THE WALK: siding starter, siding J-channel, soffit
// J-channel and finish trim do NOT show and do NOT fill. (Coil has no walk
// step — it was off-walk already.) The vinyl walk is NOT changed: this set is
// consulted only when the estimate's own kind is `lp_smart`, so every other
// job still walks all 14 steps in the same order.
const LP_OFF_WALK_STEPS = new Set(["starter", "j_channel", "finish_trim"]);
// The soffit J-channel step is matched by its own lock (jLock === "soffit"),
// never by a combined j-type token, so this screen stays free of the tokens
// the J chips forbid.
const isLpOffWalk = (s) => LP_OFF_WALK_STEPS.has(s.key) || s.jLock === "soffit";
import {
  CATEGORIES, BAND_BOARD, BAND_MATCHES, BAND_WIDTHS, COIL, DORMER, DOWNSPOUT, EAVE, FASCIA, FINISH_TRIM, GABLE, GUTTER, INSIDE_CORNER, J_CHANNEL, J_SIZES, J_TYPES, OPENING, OPENING_TYPES, OUTSIDE_CORNER, SIDING, SOFFIT, STARTER, TAP_ORDER, WALL_BASE, kindLabel, markColor,
  layerKeyOf, SIDING_J_PRODUCTS,
} from "@/components/estimate/phototakeoff/marks";
// SEND-139 — the gable and dormer tools MOVE HERE from the annotator.
// The annotator's own math module is REUSED, not re-implemented: same
// base/rise/pitch, same averaged dormer edges, same masking. Its gable
// area has always been the true triangle (½ × base × rise), which is
// exactly what SEND-137 ruled — no 0.70 exists in this path.
import {
  GABLE_PITCH_PRESETS, gableDims, dormerDims, pitchOutOfRange,
} from "@/lib/gableMath";

/* PHOTO TAKEOFF EDITOR — PHASE 1 (Howard ruled 2026-08-26, SEND-131A).
   The contractor marks THE PHOTO. Full-screen, one photo at a time,
   shaped after PdfOverlayEditor: same snap/zoom conventions, same
   colours (siding blue from the overlay editor, the annotator's own
   material colours), window-level vertex drag that tracks the pointer
   1:1 at any zoom, and pinch-zoom on a phone.

   Discipline, carried verbatim from the blueprint lane:
     · every mark lands PROVISIONAL and carries NO quantity until a
       human confirms it — unconfirmed marks are listed and named;
     · scale is LOCAL TO THIS PHOTO (two-tap anchor or typed tape;
       THE TAPE WINS). No scale → no quantity, and the refusal is named;
     · openings are REPORTED, never deducted (phase 1);
     · Apply writes ft²/counts only — no money, ever.  */

// SEND-142 — the shared mark vocabulary (colours, categories, tap order,
// labels) moved to ./phototakeoff/marks so the three rail panels and this
// editor read ONE declaration each.

const polyAreaPx = (pts) => {
  if (!pts || pts.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const q = pts[(i + 1) % pts.length];
    a += pts[i].x * q.y - q.x * pts[i].y;
  }
  return Math.abs(a) / 2;
};
const inPerPx = (scale) => {
  if (!scale || !scale.span_px) return null;
  if (scale.tape_inches) return { ipp: scale.tape_inches / scale.span_px, basis: "tape" };
  const inches = scale.anchor?.inches;
  if (!inches) return null;
  return { ipp: inches / scale.span_px, basis: "anchor" };
};
const ftin = (ft, inch) => {  const f = parseFloat(ft || 0) || 0;
  const i = parseFloat(inch || 0) || 0;
  const total = f * 12 + i;
  return total > 0 ? total : null;
};

// SEND-150 — THE TRIM LINES THE CONTRACTOR DRAWS. Two-tap, one photo, and
// unlike the two anchors they DO carry feet — measured as drawn at this
// photo's own scale. No AI proposes them; no price and no waste is applied.
const LINE_TOOLS = ["wall_base", "eave", "starter", "fascia", "soffit",
  "j_channel", "outside_corner", "inside_corner", "coil", "finish_trim",
  // SEND-298a — the GUTTER line, the same two-tap gesture as fascia.
  "gutter",
  // SEND-298c — the DOWNSPOUT line, the same two taps, drawn top to bottom.
  "downspout",
  // SEND-286 — the BAND BOARD line, the same two-tap gesture.
  "band_board"];
// SEND-208 (Howard ruled 2026-08-14) — THE THREE J SHAPES, his words for
// each. LINE runs along a wall or rake, BOX goes round an opening, POLYGON
// takes an odd shape. The LF is what he drew: a line as drawn, a box or a
// polygon round its CLOSED perimeter, both at this photo's own scale.
// SEND-208 — the four type names, his: siding J, shake J, B&B J, soffit J.
// SEND-241 (Howard ruled 2026-08-14) — HIS FOUR, IN HIS ORDER: SIDING J ·
// SOFFIT J · B&B J · SHAKE J, and the two sizes. BOTH LISTS MOVED to the ONE
// shared mark vocabulary in ./phototakeoff/marks, because the rail's marks
// list now re-names a drawn J with the same chips: one declaration, two
// places that render it, no drift. Nothing a chip writes changed.
// SEND-223 (Howard ruled 2026-08-14) — J MARKS → CATALOG QTY. TWO PICKS
// before a J goes down: the USE — which is the type chip he already picks,
// siding / shake / B&B J on the wall, soffit J at the soffit — and the SIZE,
// 3/4" or 1/2". Both sizes are sold for both jobs, and a siding J mark never
// fills a soffit J line (or the other way round). NO COLOUR IS PICKED HERE:
// the colour already on that J line keeps its tier.
// SEND-227 — TWO TIERS, NAMED. Standard and Architectural are the SAME
// physical row at two prices, so the tap is a TARGET and never a price.
const TIER_CHIPS = [
  { key: "standard", label: "Standard" },
  { key: "architectural", label: "Architectural" },
];
// SEND-263 — the two steps where the tap names the product's own catalog row,
// so the first-tap gate stands on them and nowhere else.
// SEND-293 — and where the colour DERIVES that tap, for the same reason: on
// these two steps the tap decides which row a mark lands on.
const TAP_GATED_TOOLS = ["siding_zone", "soffit"];
// SEND-249 (Howard ruled 2026-08-14) — HIDE THE LEFTOVER AI CHROME ON PHOTO
// TAKEOFF. DO NOT DELETE THE CODE. "Same pattern as 246: one flag or the same
// hide family. Code stays. One flag brings it back."
//
// WHAT THIS ONE FLAG HIDES on the finished-walk screen: the yellow Phase 1
// banner · the STAGE 1 — BEFORE AI banner with AI PROPOSALS and STARTING
// ZONES · the "AI proposals are off on this photo" line · and OTHER and CLEAR
// on the wall bar, because SEND-248 made that bar a JUMP and those two still
// TAGGED — "take them off that bar. Tagging stays in walk step 1 only." The
// grey OTHER sentence goes with the OTHER chip it explains (his word).
//
// WHAT IS UNTOUCHED: FRONT · LEFT · BACK · RIGHT still jump, WALK FINISHED,
// WALK IT AGAIN, PREV / NEXT, the jump-to-step links, the TAGGED BY HAND line
// (that is evidence, not chrome, and he kept it), and WALK STEP 1, which still
// carries all five chips and CLEAR and is the ONLY tagger.
//
// NOTHING IS DELETED: every block, every handler (`pullProposals`,
// `pullZones`, `tagElevation`), every testid and the whole stage machinery
// stay on disk and are still tested. Flip this to show them again.
const TAKEOFF_AI_CHROME_SHOWN = false;
// SEND-250 (Howard ruled 2026-08-14) — CLEAN UP THE WALL BAR LABEL. The label
// reads GO TO WALL, and the two lines of text beside the chips come off that
// bar: the FRONT · LEFT · BACK · RIGHT helper sentence and the TAGGED BY HAND
// line, because "FRONT already shows which wall you are on."
// HIDE, NOT DELETE, and the same one-flag pattern: both spans, both testids
// and their exact wording stay on disk, and walk step 1's tagging and the
// SEND-248 jump are not touched. THE REFUSAL LINE IS NOT HELPER TEXT and
// stays by his word — it is the answer to a click.
const WALL_BAR_HELP_SHOWN = false;
// SEND-257 (Howard ruled 2026-08-14) — HIDE GABLE AND DORMER ON THIS SCREEN.
// The two CHIPS come off the photo takeoff bar and off any jump list. HIDE,
// NOT DELETE: `gable` and `dormer` are still mark kinds end to end — the
// marks already on a photo still paint, still carry their pitch and their
// depth, still confirm and still COUNT in the quantities — and every handler,
// colour and label stays on disk. Flip this and the two chips are back.
const GABLE_DORMER_CHIPS_SHOWN = false;
// SEND-302b (Howard ruled 2026-06) — HIDE THE NO AI — YOU DRAW BADGE. "Hide
// it. Do not delete the door. The PULL AI OPENINGS button stays." HIDE, NOT
// DELETE, and the same one-flag pattern: the badge's span, its exact wording
// and the no-AI banner it sits in all stay on disk. The door itself (`noAi`),
// the OPENINGS pull beside it, the chips, the steps and Apply All are not
// touched. Flip this to show the badge again.
const NO_AI_BADGE_SHOWN = false;
// SEND-302c (Howard ruled 2026-06) — THE RIGHT SIDE KEEPS NO SCALE PANEL.
// "He does not need that panel. Scale is already a step on the walk. Move
// those controls onto the Scale this photo step. Two taps and the tape figure
// stay. They show only when that step is open." HIDE, NOT DELETE: the rail's
// copy sits behind this one flag, and the walk's scale step renders the SAME
// `ScalePanel` component it always did. No scale math moved and no printed
// scale is trusted. Flip this and the rail panel is back.
const RAIL_SCALE_PANEL_SHOWN = false;
// SEND-302d (Howard ruled 2026-06) — HIDE PULL AI OPENINGS. "Hide it. Do not
// delete the door." HIDE, NOT DELETE: the button, its wording, its testid and
// `pullProposals` all stay on disk and are still tested; the no-AI door
// (`noAi`) still opens the same editor, the chips, the steps and Apply All are
// untouched. The now-empty grey strip goes with it, behind its own flag, so no
// blank bar is left. Flip either and they are back.
const PULL_AI_OPENINGS_SHOWN = false;
const NO_AI_STRIP_SHOWN = false;
// SEND-241 — the two sizes moved to ./phototakeoff/marks with the four
// products, for the same reason: one declaration, two places that render it.
// SEND-235 (Howard ruled 2026-08-14) — THE TYPE CHIPS BELONG ON THE MARK.
// "Howard is on Step 4 of 12. The only chips on that panel are OPENING ·
// STANDARD · ARCHITECTURAL. Those are the vinyl tap. They are not the type."
// So the four opening types ride THE SELECTED OPENING ITSELF, on the photo,
// next to CONFIRM / DELETE — the same place the J type and size chips already
// sit. STANDARD / ARCHITECTURAL stay the vinyl tap and name no opening.
// SEND-236 (Howard ruled 2026-08-14) — THE ROWS WITH NO COLOUR TIER.
// "Those two products do not have a Standard / Architectural type." Fascia
// and starter are ONE catalog row each — `Fascia/rake or frieze` and
// `Starter` — with no Standard / Architectural pair to name, so the vinyl tap
// is not shown on their walk steps.
// SEND-237 (Howard ruled 2026-08-14) — OPENINGS JOINED THEM: "An opening is
// window / garage door / entry door / sliding glass door. IT IS NOT A VINYL
// TIER." So the tap comes off the openings step too, and the box keeps its
// own four TYPE chips. The tap is NOT removed from the app: siding, J, soffit
// and finish trim still carry it, and so does the free editor's full toolbar.
// NOTHING ABOUT ANY QUANTITY CHANGES.
// SEND-238 (Howard ruled 2026-08-14) — AND SCALE: "SCALE THIS PHOTO does not
// have a Standard / Architectural type." The tape is not a product, so the tap
// comes off that step too.
// SEND-244 (Howard ruled 2026-08-14) — TWO FAMILIES, AND THE TAP BELONGS TO A
// PRODUCT INSIDE ONE. SIDING carries the siding zones, the wall J, the two
// corners and the finish trim; SOFFIT carries the soffit and the soffit J.
// "Siding J, outside corners, inside corners, and finish trim FOLLOW the armed
// SIDING product's tap. Soffit J FOLLOWS the armed SOFFIT product's tap. It
// does not use clap's tap." A soffit J is a J on the soffit step's family, so
// the J tool reads its ARMED J PRODUCT to know which family it is in.
// the same two families, read off a MARK's kind (a soffit J is named by its
// own j_type, so the J kind is in here once and the lane reads the mark)
const TAP_KINDS_FE = {
  siding_zone: "siding", j_channel: "siding", outside_corner: "siding",
  inside_corner: "siding", finish_trim: "siding", soffit: "soffit",
};
const TAP_FAMILY_BY_TOOL = {
  siding_zone: "siding", outside_corner: "siding", inside_corner: "siding",
  finish_trim: "siding", soffit: "soffit",
};
// THE PRODUCT IS SERIES + PROFILE, never the exposure: "Clap stays clap across
// 4" and 4.5". Charter Oak clap Standard still counts. Dutch lap, Vertical B&B
// and Shakes each need their own tap." Soffit is its series alone: "Charter Oak
// soffit keeps one tap. Greenbriar needs its own."
const tapProduct = (fam, a) => {
  if (fam === "soffit") return a.soffit_series || null;
  if (fam !== "siding") return null;
  return a.siding_series && a.siding_zone
    ? `${a.siding_series}|${a.siding_zone}` : null;
};
const TAP_PRODUCT_FIRST = {
  siding: "Pick the siding product first — its series and its profile. The Standard / Architectural tap belongs to a product, and nothing is defaulted for you",
  soffit: "Pick the soffit series first. The Standard / Architectural tap belongs to a product, and nothing is defaulted for you",
};
const TAP_FIRST = {
  siding: "Tap Standard or Architectural for this siding product on this photo — nothing is drawn until you do, and a Standard row already on the estimate is not a tap",
  soffit: "Tap Standard or Architectural for this soffit product on this photo — nothing is drawn until you do, and clap's tap does not reach the soffit",
};
const TIER_FREE_TOOLS = ["fascia", "gutter", "downspout", "starter", "opening", "scale", "non_siding_zone", "band_board"];
const OPENING_TYPE_CHIPS = OPENING_TYPES;
// SEND-282 (Howard ruled 2026-06) — THE STARTER STEP'S TWO PRODUCTS. Same
// step, two chips: "Rename the current Starter step to Siding starter. Add
// Shake starter on that same step. Do not add a new page after it."
const STARTER_PRODUCTS = [
  { key: "siding", label: "Siding starter" },
  { key: "shake", label: "Shake starter" },
];
// SEND-299 (Howard ruled 2026-08-14) — THE FINISH TRIM STEP'S THREE PRODUCTS.
// "On the existing Finish Trim step, add two chips beside Siding finish trim:
// B&B finish trim and Shake finish trim. Same tool as siding finish trim. No
// new walk step." Built exactly like the starter pair above: a NAME on the
// mark, no chip is a SIDING finish trim, and nothing already drawn is
// re-asked.
const TRIM_PRODUCTS = [
  { key: "siding", label: "Siding finish trim" },
  { key: "bb", label: "B&B finish trim" },
  { key: "shake", label: "Shake finish trim" },
];
// SEND-241 (Howard ruled 2026-08-14) — THE PRODUCT OPENS THE TAP, THE SIZE
// MAY FOLLOW. "Same gate as siding. You must pick a Product before it draws.
// Size and shape may follow." SEND-223's both-picks gate is WITHDRAWN BY
// NAME: a J with no size still measures its LF as drawn, and it still fills
// NO catalog row until he says 3/4" or 1/2" — the server reports those feet
// under `j_no_pick_lf` with its own sentence and backfills no size.
const jPickMissing = (a) => {
  if (!a.j_channel) return TYPE_FIRST.j_channel;
  return null;
};
const J_SHAPES = [
  { key: "line", label: "J line",
    hint: "tap the two ends — runs along a wall or rake, measured as drawn" },
  { key: "rect", label: "J box",
    hint: "tap two opposite corners — round an opening; the LF is the closed perimeter" },
  { key: "poly", label: "J polygon",
    hint: "tap each corner, tap the first again to close — the LF is the closed perimeter" },
];
// SEND-306 (Howard ruled 2026-06) — THE SIDING ZONE'S TWO SHAPES. "Keep
// tap-the-corners. Add a box. Two taps, opposite corners. The rectangle is the
// siding zone. Square feet come off the box, same as a zone drawn by corners."
// CORNERS IS FIRST AND STARTS PICKED. A box is four corners he did not have to
// tap: the same four points, the same area reader, and NOTHING IS CUT OUT.
const ZONE_SHAPES = [
  { key: "poly", label: "Corners",
    hint: "tap each corner, tap the first again to close — the zone is what you outline" },
  { key: "rect", label: "Box",
    hint: "tap two opposite corners — the rectangle IS the zone, and its square feet come off the box exactly as a corner-drawn zone's do" },
];

const LINEAR_TOOLS = ["starter", "fascia", "gutter", "downspout", "soffit",
  "j_channel",
  "outside_corner", "inside_corner", "coil", "finish_trim", "band_board"];
// SEND-217 (Howard ruled 2026-08-14) — TYPE CHIPS ON DRAW. NAME ONLY. MUST
// PICK FIRST. "Siding and J need a type before the mark goes down." The six
// siding names are his. STONE, BRICK AND STUCCO ARE NOT HERE: they are not
// siding types and they stay non-siding masks with their own chip.
// SEND-221 (Howard ruled 2026-08-14) — SIDING MARKS → CATALOG QTY. "Clap is
// not a line. Conquest, Coventry, Odyssey and Charter Oak all have clap."
// So the SERIES is picked too, before the first click, and each series offers
// ONLY THE TYPES IT ACTUALLY HAS. The two series the catalog sells in two
// exposures carry a WIDTH pick as well, because 4" and 5" are different SKUs
// and nothing derives which one he bought. The Standard/Architectural pair is
// NOT a pick: it is one physical row whose tier follows the colour already on
// the estimate, so NO COLOUR IS EVER PICKED FOR HIM here.
const SIDING_SERIES = [
  { key: "conquest", label: "Conquest", types: ["clap", "dutch_lap"] },
  { key: "coventry", label: "Coventry", types: ["clap", "dutch_lap"], widths: ["4", "5"] },
  { key: "odyssey", label: "Odyssey", types: ["clap", "dutch_lap"], widths: ["4", "5"] },
  { key: "charter_oak", label: "Charter Oak", types: ["clap", "dutch_lap"] },
  { key: "vertical_bb", label: "Vertical B&B", types: ["bb", "vertical"] },
  { key: "shakes", label: "Shakes", types: ["shake"] },
];
const seriesOf = (key) => SIDING_SERIES.find((s) => s.key === key) || null;
// SEND-311 — THE LP WALK'S OWN PRODUCT CHIPS (lp_smart jobs). They arm the
// LP picks that name the row each lane lands on and, with it, the row's own
// ExpertFinish color list. The names mirror `lp_photo_landing.LP_SIDING_CHIPS`
// / `LP_TRIM_SERIES` / `LP_OSC_SIZES` — the catalog's own words, not invented.
// SEND-313 (Howard ruled 2026-06) — THE LP SIDING CHIPS GROUP BY PRODUCT. He
// picks one of FOUR products, then the chip under it; the chip's key is the
// `lp_product` that names the row (so the landing and the colors below are
// unchanged from SEND-311/312). Single-chip products still show their one
// chip to tap. The vertical panel still fills its row at 21.3 ft²/pc
// (SEND-312). The names are the catalog's words, not invented.
const LP_SIDING_PRODUCTS = [
  { key: "lap", label: "Lap", chips: [{ key: "lap", label: "Lap" }] },
  { key: "shake", label: "Shake", chips: [{ key: "shake", label: "Shake" }] },
  { key: "bb", label: "B&B", chips: [
    { key: "panel_4x8", label: "4' x 8' Panel" },
    { key: "panel_4x10", label: "4' x 10' Panel" },
    { key: "vertical_panel", label: "16\" x 16' Vertical Panel" }] },
  { key: "nickel_gap", label: "Nickel Gap",
    chips: [{ key: "nickel_gap", label: "Nickel Gap" }] },
];
const LP_TRIM_SERIES_LIST = [
  { key: "440", label: '440 (4/4")' },
  { key: "540", label: '540 (5/4")' },
];
const LP_OSC_SIZE_LIST = [{ key: "4", label: '4"' }, { key: "6", label: '6"' }];
// SEND-240 (Howard ruled 2026-08-14) — THE TWO PRODUCTS THAT ARE THEIR OWN
// PROFILE. "VERTICAL B&B or SHAKES selected → hide Exposure and Profile." A
// hidden row he could still be refused by would be a trap, so tapping one of
// THESE TWO products arms the profile that product already is — shakes has
// exactly one, and B&B is the product itself. NO LAP ROW IS EVER AUTO-PICKED:
// Conquest, Coventry, Odyssey and Charter Oak still arm nothing, and the
// first tap on one of them is still refused until he picks clap or Dutch lap.
const OWN_PROFILE = { vertical_bb: "bb", shakes: "shake" };
// SEND-222 (Howard ruled 2026-08-14) — SOFFIT MARKS → CATALOG QTY. Two picks
// before a soffit mark is drawn: the SERIES, which names the soffit line
// already on the estimate, and the VENT, which rides the mark as a NAME.
// HIS CATALOG NOTE: vented and solid are coming to the vinyl catalog for all
// three series; until those rows exist the vent names no row and NOTHING —
// no AMI number, no price — was invented to stand in for one.
const SOFFIT_SERIES = [
  { key: "charter_oak", label: "Charter Oak" },
  { key: "greenbriar", label: "Greenbriar" },
  { key: "two_t", label: "2T" },
];
const SOFFIT_VENTS = [
  { key: "vented", label: "vented" },
  { key: "solid", label: "solid" },
];
const soffitPickMissing = (a) => {
  if (!a.soffit_series) return "Pick a soffit series first — Charter Oak, Greenbriar or 2T. Nothing is drawn until you do";
  if (!a.soffit_vent) return "Pick vented or solid — it rides the mark as a name, and nothing is drawn until you do";
  return null;
};
const SIDING_TYPES = [
  { key: "clap", label: "clap" },
  { key: "dutch_lap", label: "Dutch lap" },
  { key: "bb", label: "B&B" },
  { key: "shake", label: "shake" },
  { key: "vertical", label: "vertical" },
  { key: "nickel_gap", label: "nickel gap" },
];
// THE ARMED TYPE, per tool. NOTHING IS PRE-PICKED — it starts null, so the
// first tap of a fresh session is REFUSED IN WORDS and no type is ever
// defaulted for him. Once he picks one it stays armed for the rest of the
// session (a remount does not un-pick it, the SEND-216 way), because
// re-picking for every one of 25 marks is the defect, not the rule. IT IS
// NOT A STORE: no collection, no route, nothing written to the server.
const ARMED_TYPE = { siding_zone: null, j_channel: null, opening: null,
  siding_series: null, siding_width: null,
  soffit_series: null, soffit_vent: null, j_size: null,
  // SEND-282 — the STARTER product he armed on the walk's "Siding starter"
  // step: siding or shake. A NAME that rides the next starter line, armed
  // exactly like the J product and NEVER pre-picked — no chip is a siding
  // starter, which is what that step has always written.
  starter: null,
  // SEND-299 — the FINISH TRIM product he armed on the walk's "Finish trim"
  // step: siding, bb or shake. A NAME that rides the next finish-trim line,
  // armed exactly like the starter chip and NEVER pre-picked — no chip is a
  // SIDING finish trim, which is what that step has always written.
  trim: null,
  // SEND-286 — the BAND WIDTH he armed on the band-board step: 8, 10 or 12.
  // REQUIRED before the first tap (it is the yield bucket, not a name) and
  // NEVER pre-picked — nothing is defaulted to 8".
  band_width: null,
  // SEND-288 — WHICH COIL the band batches with. ARMED TO MATCH FASCIA, his
  // 80% case: it is the only chip on this screen that starts picked, and he
  // said to default it. Re-tap MATCH SIDING and the next band lands on the
  // accessories coil instead. Per mark, never a step-wide tab.
  band_match: "fascia",
  // SEND-291 — THE ARMED SOFFIT COLOR AND THE ARMED J COLOR ARE GONE. They
  // were MARK-LOCAL names that started empty on every step and every photo
  // while the job already had a colour on the parent. Those two steps now
  // READ the parent field (SOFFIT & FASCIA for soffit and soffit J, the
  // siding family for siding / B&B / shake J) and a pick on the step writes
  // that parent — so there is nothing left here to arm, and no later step
  // keeps a disconnected colour of its own.
  // SEND-261 — the WINDOW STYLE he armed on the openings rail. A NAME that
  // rides the next box, armed exactly like the product chips and never
  // pre-picked. Skip it and the box stays WINDOW with no style.
  opening_style: null,
  // SEND-311 — THE LP PICKS (lp_smart jobs only). The siding product chip,
  // the trim board's 440/540 series, the outside corner's 4"/6" size, and the
  // LP ExpertFinish color that rides the mark beside them. None pre-picked;
  // the color stays off until the step's LP product is armed.
  lp_product: null, lp_trim_series: null, lp_osc_size: null, lp_color: null,
  // SEND-316 — the B&B batten on-center pick (sheets only: 12 or 16). Cleared
  // when the product or group changes; Vertical Panel ignores it (fixed 16").
  bb_oc: null,
  // SEND-313 — the LP SIDING PRODUCT GROUP (Lap / Shake / B&B / Nickel Gap).
  // The chip under it sets `lp_product`; this only chooses which chips show.
  lp_product_group: null };
const TYPE_FIRST = {
  siding_zone: "Pick a siding type first — clap, Dutch lap, B&B, shake, vertical or nickel gap. Nothing is drawn until you do",
  j_channel: "Pick a J type first — siding J, shake J, B&B J or soffit J. Nothing is drawn until you do",
  // SEND-242 (Howard ruled 2026-08-14) — "Same gate as J. You must pick a
  // Product before it draws. Two taps still make the box." The refusal names
  // the four, and NOTHING IS PRE-PICKED: no type is read off the box's size.
  opening: "Pick an opening type first — window, garage door, entry door or sliding glass door. Nothing is drawn until you do",
};
// SEND-221 — the series comes first, then the type, then the width where the
// catalog is genuinely two lines. Each refusal names what to pick.
const SERIES_FIRST = "Pick a siding series first — Conquest, Coventry, Odyssey, Charter Oak, Vertical B&B or Shakes. Clap is not a line on its own";
const sidingPickMissing = (a) => {
  if (!a.siding_series) return SERIES_FIRST;
  const s = seriesOf(a.siding_series);
  // null here means the TYPE is what is missing, and SEND-217's gate says so
  if (!a.siding_zone) return null;
  if (s?.widths && !a.siding_width) {
    return `Pick the exposure width — ${s.widths.map((w) => `${w}"`).join(" or ")}. ${s.label} ${SIDING_TYPES.find((x) => x.key === a.siding_zone)?.label} is two lines and nothing is drawn until you say which`;
  }
  return null;
};
// SEND-216 (Howard ruled 2026-08-14) — P0: "If the editor remounts, the
// current step is remembered. A remount opens the same step, not step 1."
// The step he is on, keyed by THIS estimate and THIS photo, for as long as
// the app is open. It is written by `goStep` and by nothing else, so only
// BACK / NEXT / SKIP / FINISHED / EXIT can move it. IT IS NOT A STORE: no
// collection, no route, no write to the server, nothing that outlives a
// reload — and because the key carries the estimate id, no estimate can read
// or move another's entry.
const STEP_MEMORY = new Map();
// SEND-219 (Howard ruled 2026-08-14) — AFTER THE WALK, A PHOTO OPENS THE FREE
// EDITOR. "He finishes the guided walk. He is on the photo list. He clicks a
// photo. It starts the 13-step walk again. It should open Photo Takeoff."
// THE CAUSE: switching photos REMOUNTS this editor (the photo key IS the
// React key, SEND-166's structural reset), and a fresh mount started the walk
// ON, so photo B walked again over a walk he had already handed back.
// A FINISHED WALK DOES NOT OWN THE NEXT CLICK: once he FINISHES or EXITS,
// this estimate is recorded as handed back and every later photo click opens
// the free editor on that photo. THE PHOTO IS NOT THE START-WALK CONTROL —
// the existing "walk me through it" / "walk it again" button is, and it only
// walks the photo he presses it on.
// SESSION MEMORY ONLY, keyed by estimate: no new store, nothing written to
// the server, no walk history, and no estimate can see another's entry.
const WALK_HANDED_BACK = new Set();
// SEND-209 (Howard ruled 2026-08-14) — THE PHOTO HAS TO BE VISIBLE. A label
// may never cover the thing it names, so it is parked OUTSIDE the shape: on
// the horizontal centre of the shape, just above its TOP edge. A shape drawn
// near the top of the frame would have its label clipped off the canvas, so
// there the label sits just below the BOTTOM edge instead. This reads the
// mark's own points and nothing else — it invents no size and moves no
// quantity.
const labelSpot = (pts) => {
  const xs = pts.map((q) => q.x);
  const ys = pts.map((q) => q.y);
  const top = Math.min(...ys);
  const above = top >= 0.1;
  return {
    cx: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: above ? top : Math.max(...ys),
    above,
  };
};

export default function PhotoTakeoffEditor({ est, photoUrl, photoKey, noAi = false,
                                            photos = [], onSwitch = null,
                                            onWrote = null, onClose }) {
  const [marks, setMarks] = useState([]);
  const [scale, setScale] = useState(null);
  const [qty, setQty] = useState(null);
  const [orderAll, setOrderAll] = useState(null);
  const [stage, setStage] = useState(null);          // {stage, ai_read, stage_note, proposals_refusal}
  const [products, setProducts] = useState([]);
  const [productsNote, setProductsNote] = useState(null);
  const [tool, setTool] = useState("siding_zone");   // siding_zone | non_siding_zone | opening | gable | dormer | scale
  // SEND-318 — the MEASURE tool. Ephemeral: two taps draw a line and read the
  // feet at this photo's scale. It saves no mark, fills no row, moves no qty.
  const [measuring, setMeasuring] = useState(false);
  const [measurePts, setMeasurePts] = useState([]);
  const [category, setCategory] = useState("brick");
  const [draft, setDraft] = useState(null);          // {points:[{x,y}] norm, cx, cy}
  const [scaleDraft, setScaleDraft] = useState(null);// {p1,p2} norm
  const [scaleAsk, setScaleAsk] = useState(null);    // {p1,p2}
  const [spanFt, setSpanFt] = useState("");
  const [spanIn, setSpanIn] = useState("");
  const [tapeFt, setTapeFt] = useState("");
  const [tapeIn, setTapeIn] = useState("");
  // SEND-217 — the armed type is read from the module map on mount, so a
  // remount keeps the pick and a fresh session has none.
  const [armed, setArmed] = useState({ ...ARMED_TYPE });
  // SEND-294 (Howard ruled 2026-08-14) — **THE ARMED PRODUCT IS A JOB
  // DEFAULT.** It was stored NOWHERE until now: a reload opened every photo
  // blank while the colour came back, which is the fault he photographed. The
  // job default and any photo's own override come back in the same payload the
  // taps ride, and NOTHING here invents a product for a job that has none.
  const [prodPref, setProdPref] = useState(null);
  const savePref = async (fam) => {
    const a = ARMED_TYPE;
    const body = fam === "soffit"
      ? { family: "soffit", series: a.soffit_series, profile: a.soffit_vent }
      : { family: "siding", series: a.siding_series, profile: a.siding_zone,
        width: a.siding_width };
    try {
      const { data } = await api.put(
        `/estimates/${est.id}/photo-takeoff/product-pref`,
        { ...body, photo_key: photoKey, clear: !body.series });
      setProdPref(data.product_prefs || null);
    } catch (e) {
      // HIS RULING: a protected job remembers nothing, and the armed product
      // still shows on screen for this sitting.
      toast.error(e?.response?.data?.detail
        || "That product is armed on screen, but it was not remembered");
    }
  };
  const armType = (kind, key) => {
    ARMED_TYPE[kind] = ARMED_TYPE[kind] === key ? null : key;
    // SEND-291 — there is no stale J colour to drop any more: changing the J
    // product changes WHICH PARENT the step reads, and that parent's own
    // colour is whatever the job already set.
    setArmed({ ...ARMED_TYPE });
    setDraft(null);
    // SEND-294 — the PROFILE is half the siding product, so it is remembered
    // with the series. Nothing else armed here is a product.
    if (kind === "siding_zone") savePref("siding");
  };
  // SEND-221 — arming a SERIES drops a type that series does not have and
  // drops the width, because a stale pick is worse than no pick.
  const armSeries = (key) => {
    const next = ARMED_TYPE.siding_series === key ? null : key;
    ARMED_TYPE.siding_series = next;
    ARMED_TYPE.siding_width = null;
    const has = seriesOf(next)?.types || [];
    if (!has.includes(ARMED_TYPE.siding_zone)) ARMED_TYPE.siding_zone = null;
    // SEND-240 — the two products that ARE their own profile arm it here, so
    // the walk step that hides the Profile row can still draw. A lap series
    // arms nothing: `OWN_PROFILE` has no key for one.
    if (next && OWN_PROFILE[next]) ARMED_TYPE.siding_zone = OWN_PROFILE[next];
    setArmed({ ...ARMED_TYPE });
    setDraft(null);
    savePref("siding");
  };
  const armSoffit = (field, key) => {
    ARMED_TYPE[field] = ARMED_TYPE[field] === key ? null : key;
    savePref("soffit");
    // SEND-291 — changing the soffit PRODUCT no longer drops a colour: the
    // colour is the estimate's SOFFIT & FASCIA field, one per job, and the
    // product only narrows the LIST the step offers.
    setArmed({ ...ARMED_TYPE });
    setDraft(null);
  };
  // SEND-244 — WHICH FAMILY IS THIS TOOL IN? The J is the only tool that can
  // be in either, and it says so itself: a SOFFIT J is soffit's, every other J
  // is siding's. Nothing else guesses.
  const famOf = (t, a = armed) => (t === "j_channel"
    ? (a.j_channel ? (a.j_channel === "soffit" ? "soffit" : "siding") : null)
    : TAP_FAMILY_BY_TOOL[t] || null);
  const tapProductLabel = (fam, a = armed) => {
    if (fam === "soffit") {
      return (SOFFIT_SERIES.find((x) => x.key === a.soffit_series) || {}).label
        || "this soffit product";
    }
    const ser = (SIDING_SERIES.find((x) => x.key === a.siding_series) || {}).label;
    const prof = (SIDING_TYPES.find((x) => x.key === a.siding_zone) || {}).label;
    return ser && prof ? `${ser} ${prof}` : "this siding product";
  };
  const tapOf = (fam, a = armed) => {
    const prod = tapProduct(fam, a);
    return prod ? (taps[`${fam}|${prod}`] || null) : null;
  };
  // SEND-227 — ONE TAP, ON THIS PHOTO. Tapping the tier again clears it, and
  // a cleared tier is honest: the paired lanes go back to refusing by name.
  // SEND-244 (Howard ruled 2026-08-14) — AND THE TAP IS PER PRODUCT. "Clap
  // Standard stays on clap. Switch to B&B → tap again. Switch back to clap →
  // the first clap Standard tap still counts." So the tap is stored under its
  // FAMILY and its PRODUCT — series|profile for siding, the series for soffit
  // — and the two families never lend each other a tap.
  // SEND-293 (Howard ruled 2026-06) — **THE COLOUR DECIDES THE TAP.** He set
  // the job colour, the step pulled it through and left the tap on STANDARD —
  // so the row read "on the estimate, not in this Standard list" while the
  // catalog puts that very colour in the product's ARCHITECTURAL collection.
  // The step now DERIVES the tap from the colour the moment it
  // reads it, on every photo whose tap for that family is untapped or
  // disagrees. ONE derive per photo · product · colour, remembered here, so a
  // tap HE makes afterwards wins for the list and is never flipped back — and
  // his own tap marks the same key, for the same reason. An ambiguous colour
  // (in both collections, or in neither) derives NOTHING.
  const TIER_DERIVED = useRef(new Set()).current;
  const colorOfTapFamily = (fam, a = armed) => {
    if (estColors === null) return "";
    return fam === "soffit"
      ? (estColors[SOFFIT_J_FIELD] || "")
      : (estColors[COLOR_FIELD_FOR_SERIES[a.siding_series]] || "");
  };
  const tapTier = async (want) => {
    const fam = famOf(tool);
    const prod = tapProduct(fam, armed);
    if (!prod) { toast.error(TAP_PRODUCT_FIRST[fam]); return; }
    const k = `${fam}|${prod}`;
    const next = taps[k] === want ? null : want;
    setBusy(true);
    try {
      await api.put(`/estimates/${est.id}/photo-takeoff/color-tier`,
        { photo_key: photoKey, tier: next, clear: !next, family: fam,
          product: prod });
      const nextTaps = { ...taps };
      if (next) nextTaps[k] = next; else delete nextTaps[k];
      setTaps(nextTaps);
      // HIS TAP WINS: the derive does not run again for this photo, this
      // product and this colour.
      TIER_DERIVED.add(`${photoKey}|${k}|${colorOfTapFamily(fam)}`);
      toast.success(next
        ? `${next === "standard" ? "Standard" : "Architectural"} on this photo for ${tapProductLabel(fam, armed)}`
        : `Color tier cleared for ${tapProductLabel(fam, armed)} on this photo — its paired rows will refuse until you tap one`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That color tier was not saved");
    } finally { setBusy(false); }
  };
  // SEND-259 (Howard ruled 2026-08-14) — SIDING COLOR, SET FROM THE PHOTO
  // RAIL, ON THE SAME FIELD THE ESTIMATE PAGE USES.
  // SEND-260 (Howard ruled 2026-08-14) — and BOARD & BATTEN COLOR and SHAKE
  // COLOR, each on ITS OWN field. One field per material family, one decision,
  // no second copy on the photo. The current values are READ from the estimate
  // (this editor is handed the id only), so an estimate that already carries a
  // color shows it selected; a read that fails stays NULL and the rail says so
  // rather than guessing.
  // SEND-310 follow-up — THE ESTIMATE'S OWN KIND, read off the same estimate
  // fetch (this editor is handed the id only). `lp_smart` is the LP gate the
  // walk already uses on the server; absent / anything else is the vinyl walk.
  const [estKind, setEstKind] = useState(null);
  const [estColors, setEstColors] = useState(null);
  // SEND-262 — the estimate's OWN lines, read once with the colors, so the
  // accessories list on the rail is the SAME narrowed list the estimate page's
  // ACCESSORIES COLOR dropdown builds. Nothing is guessed from the photo.
  const [estLines, setEstLines] = useState([]);
  // SEND-311 — THE LP COLORS THE WALK'S FOUR PICKERS SHOW, fetched once off
  // the server (the dealer-verified ExpertFinish matrix). `null` until it
  // lands and on every non-LP job — the vinyl pickers never read it.
  const [lpColors, setLpColors] = useState(null);
  const lpWalk = estKind === "lp_smart";
  useEffect(() => {
    let off = false;
    if (!lpWalk) { setLpColors(null); return () => { off = true; }; }
    (async () => {
      try {
        const { data } = await api.get(
          `/estimates/${est.id}/photo-takeoff/lp-colors`);
        if (!off) setLpColors(data?.is_lp ? data : null);
      } catch { if (!off) setLpColors(null); }
    })();
    return () => { off = true; };
  }, [est.id, lpWalk]);
  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const { data } = await api.get(`/estimates/${est.id}`);
        if (!off) {
          setEstKind(data?.kind || null);
          setEstColors({
            siding_color: data?.siding_color || "",
            board_batten_color: data?.board_batten_color || "",
            shake_color: data?.shake_color || "",
            accessories_color: data?.accessories_color || "",
            soffit_fascia_color: data?.soffit_fascia_color || "",
            gutter_color: data?.gutter_color || "",
          });
          setEstLines(data?.lines || []);
        }
      } catch { if (!off) setEstColors(null); }
    })();
    return () => { off = true; };
  }, [est.id]);

  // ONE FIELD, THE MERGE DOOR: PATCH writes the ONE field this product owns
  // and nothing else, so no line, mark, quantity, color tap or other family's
  // color is touched — and a protected estimate refuses it (423) in words.
  const pickProductColor = async (field, color) => {
    const prev = estColors;
    setBusy(true);
    await writeThrough({
      applyOptimistic: () => setEstColors({ ...prev, [field]: color }),
      rollback: () => setEstColors(prev),
      apiCall: () => api.patch(`/estimates/${est.id}`, { [field]: color }),
      reconcileFrom: (resp) => {
        setEstColors({
          siding_color: resp?.data?.siding_color || "",
          board_batten_color: resp?.data?.board_batten_color || "",
          shake_color: resp?.data?.shake_color || "",
          accessories_color: resp?.data?.accessories_color || "",
          soffit_fascia_color: resp?.data?.soffit_fascia_color || "",
          gutter_color: resp?.data?.gutter_color || "",
        });
        if (onWrote) onWrote();
        toast.success(`${(COLOR_LABEL_FOR_FIELD[field] || "Color").toUpperCase()} set to ${color} on this estimate — the same field the estimate page shows. No mark moved and no money was written`);
      },
      refusalMessage: (e) => (e?.response?.status === 423
        ? "Protected estimate — that color was not saved and nothing else changed"
        : (e?.response?.data?.detail
          || "That color was not saved — the estimate still shows what it had")),
    });
    setBusy(false);
  };
  // SEND-223 — the J SIZE, armed the same way and never pre-picked.
  const armJSize = (s) => {
    ARMED_TYPE.j_size = ARMED_TYPE.j_size === s ? null : s;
    setArmed({ ...ARMED_TYPE });
    setDraft(null);
  };
  const armWidth = (w) => {
    ARMED_TYPE.siding_width = ARMED_TYPE.siding_width === w ? null : w;
    setArmed({ ...ARMED_TYPE });
    setDraft(null);
    // SEND-294 — the EXPOSURE WIDTH is part of the product on the products
    // that ask for it, so it rides the same memory.
    savePref("siding");
  };
  // SEND-311 — THE LP PICKS, armed like the vinyl chips and never pre-picked.
  // Changing the product/size/series changes WHICH ROW the color is read from,
  // so the armed LP color is dropped — a color is never carried onto a row
  // that does not offer it.
  const armLp = (field, key) => {
    ARMED_TYPE[field] = ARMED_TYPE[field] === key ? null : key;
    ARMED_TYPE.lp_color = null;
    // SEND-316 — a new siding product clears any batten on-center pick.
    if (field === "lp_product") {
      ARMED_TYPE.bb_oc = null;
      // SEND-317 — leaving the sheet chips drops out of Place-sheet mode.
      if (key !== "panel_4x8" && key !== "panel_4x10"
          && ARMED_TYPE[field] !== "panel_4x8"
          && ARMED_TYPE[field] !== "panel_4x10") setZoneShape("poly");
    }
    setArmed({ ...ARMED_TYPE });
    setDraft(null);
  };
  // SEND-316 — pick the B&B batten on-center (sheets only). Tap again to clear.
  const armBbOc = (oc) => {
    ARMED_TYPE.bb_oc = ARMED_TYPE.bb_oc === oc ? null : oc;
    setArmed({ ...ARMED_TYPE });
    setDraft(null);
  };
  const pickLpColor = (c) => {
    ARMED_TYPE.lp_color = c || null;
    setArmed({ ...ARMED_TYPE });
  };
  // SEND-313 — pick the LP siding PRODUCT GROUP; it clears the chip and color
  // beneath it so a chip from a different product can never linger.
  const armLpProduct = (g) => {
    ARMED_TYPE.lp_product_group = ARMED_TYPE.lp_product_group === g ? null : g;
    ARMED_TYPE.lp_product = null;
    ARMED_TYPE.lp_color = null;
    ARMED_TYPE.bb_oc = null;   // SEND-316 — group change clears the batten pick
    setArmed({ ...ARMED_TYPE });
    setDraft(null);
  };
  const [selectedId, setSelectedId] = useState(null);
  const [dragVertex, setDragVertex] = useState(null); // {markId, index}
  const [imgNat, setImgNat] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  // SEND-175 item 2 — PHASE 1 FULL SCREEN. Windowed by default; the toggle
  // is in the header and it changes the SIZE and nothing else.
  const [full, setFull] = useState(false);
  const [busy, setBusy] = useState(false);
  // SEND-310 follow-up — THE WALK THIS JOB SHOWS. On an LP SmartSide estimate
  // the four off-walk steps are filtered out (they do not show and the server
  // holds their lanes back from the vinyl writer too); on every other kind
  // this is WALK_STEPS_ALL unchanged, so the vinyl walk is byte-for-byte what
  // it was. Every consumer below reads `WALK_STEPS`, so the step index, the
  // bar, the jump row and the "step N of …" count all follow the one list.
  const WALK_STEPS = useMemo(
    () => (estKind === "lp_smart"
      ? WALK_STEPS_ALL.filter((s) => !isLpOffWalk(s))
      : WALK_STEPS_ALL),
    [estKind]);
  // SEND-204 — THE WALK IS ON BY DEFAULT and it is never a gate: the bar
  // stays live, SKIP is allowed on any step that does not apply, and
  // "skip the walk" drops him into the free bar. `hidden` is the LAYER
  // SWITCH — a list of mark kinds this photo is not painting right now. It
  // starts EMPTY on every mount, so re-opening a photo shows every layer
  // already drawn.
  const [walkOn, setWalkOn] = useState(() => !WALK_HANDED_BACK.has(est.id));
  // SEND-216 (Howard ruled 2026-08-14) — P0: STAY ON THIS STEP AFTER A MARK.
  // "Click siding / openings, add anything, and the walk jumps back to STEP 1.
  // That is a remount or a reset." It was a RESET: every save re-reads the
  // photo, and the re-read called `setStepIdx(0)`. It does not any more.
  // ONLY BACK, NEXT, SKIP, FINISHED and EXIT change the step.
  //
  // AND A REMOUNT OPENS THE SAME STEP, NOT STEP 1: the step he is on is kept
  // in `STEP_MEMORY`, keyed by this estimate and this photo, for as long as
  // the app is open. No store was invented, nothing is written to the server
  // and no estimate can see another's entry.
  const stepKey = `${est.id}|${photoKey}`;
  const [stepIdx, setStepIdx] = useState(() => STEP_MEMORY.get(stepKey) || 0);
  const [skipped, setSkipped] = useState([]);
  const [walkDone, setWalkDone] = useState(false);
  const [hidden, setHidden] = useState([]);
  // SEND-208 (Howard ruled 2026-08-14) — J-CHANNEL IS THE ONE TRIM WITH A
  // SHAPE, AND HE PICKS IT: a LINE along a wall or rake, a BOX round an
  // opening, or a POLYGON for an odd shape. It is a drawing choice only —
  // it stores no product, invents no price and copies to no other photo.
  const [jShape, setJShape] = useState("line");
  // SEND-305 (Howard ruled 2026-06) — **THE TRIM BOARD DRAWS LIKE THE J.** "A
  // line stays. Add a box, two taps. Add a polygon, tap the corners." Its own
  // shape state so picking a box on the trim board never changes what the J
  // is set to, and the SAME `J_SHAPES` declaration and the SAME gestures —
  // there is no second walk and no second drawing path.
  const [bandShape, setBandShape] = useState("line");
  // SEND-306 (Howard ruled 2026-06) — **A BOX ON THE SIDING ZONE.** "On the
  // siding zone step, keep tap-the-corners. Add a box. Two taps, opposite
  // corners. The rectangle is the siding zone. Square feet come off the box,
  // same as a zone drawn by corners. Nothing is cut out of the photo."
  // CORNERS STARTS PICKED, so the gesture he has always used is the default
  // and nothing changes under him. A box is just four corners he did not have
  // to tap: it is stored as the same four points and read by the SAME area
  // reader, so no new measurer and no second walk.
  const [zoneShape, setZoneShape] = useState("poly");
  const shapeTool = tool === "j_channel" || tool === "band_board";
  const shapeNow = tool === "band_board" ? bandShape : jShape;

  const imgRef = useRef(null);
  const scrollRef = useRef(null);
  const wheelAnchor = useRef(null);
  const marksRef = useRef([]);
  const pinch = useRef(null);
  // SEND-165 item 1 — ELEVATION BY HAND. His call, and the sheet says so.
  const [elev, setElev] = useState(null);
  // SEND-303 (Howard ruled 2026-06) — **A PAGE THAT CAME IN SIDEWAYS.** One
  // tap turns the sheet 90° clockwise, another turns it again, four taps are
  // back to upright. The turn is the PAGE'S OWN, read back from the server on
  // open, so the next open is the same way up — and a blueprint page and a
  // photo use this one door, because a page IS a photo here.
  //
  // THE WHOLE SHEET TURNS AS ONE PICTURE. The image, every mark, every node
  // and every label are inside one box and that box is rotated, so the marks
  // ride the sheet without a single stored point being rewritten. Nothing is
  // recomputed: the ft², the scale and the plane basis cannot move when the
  // page turns, and the scale stays with the sheet. A tap is mapped BACK
  // through the turn (`normFromEvent`) so drawing still lands under his
  // finger at any turn.
  const [turn, setTurn] = useState(0);
  // SEND-304 (Howard ruled 2026-06) — **THE WORDS OFF WHILE HE DRAWS.** "The
  // name and the feet sit on the line and block the next tap." One toggle on
  // the right rail hides every mark's chip ON THIS PHOTO; the coloured line,
  // the box, the polygon and the drag handles all stay, and the words come
  // back with another tap. Saved on the page, so he closes it and comes back
  // to them still off, and NO OTHER PHOTO IS CHANGED.
  //
  // IT HIDES WORDS AND NOTHING ELSE. No mark, no point, no size, no scale and
  // no quantity moves with it — the rail, the order list and the estimate read
  // exactly the same either way.
  const [tagsHidden, setTagsHidden] = useState(false);
  // SEND-289 — the OTHER walls he added, each with its own name, and the
  // by-wall rollup that labels each wall's own figures with that name.
  const [walls, setWalls] = useState([]);

  // SEND-166 item 1 · SEND-168 addition 2 — WHAT WOULD BE DROPPED, NAMED,
  // AS ONE LIST. MID-DRAFT IS: a box with one corner placed, a polygon with
  // two vertices, a two-tap line with one end tapped (all three are `draft`),
  // a scale with one tap of two (`scaleDraft`), a tapped span still waiting
  // for its inches (`scaleAsk`), a corner under the finger (`dragVertex`),
  // and a save still in flight (`busy` — a typed size blurs into its PATCH
  // before a button's click fires, and this is what makes sure it lands).
  //
  // THE LIST IS THE WHOLE OF MID-DRAFT, NOT A HAND-KEPT SAMPLE OF IT: every
  // tool in `TOOLS` funnels its taps through `onPointerDown`, which writes
  // `setDraft`, `setScaleDraft` and `setScaleAsk` and NOTHING ELSE. A
  // fourteenth tool that grew a draft state of its own would introduce a
  // fourth setter there, and the SEND-168 pin reads that function and FAILS
  // on any setter that is not on this list. An enumerated catalog has been
  // the cause six times on this project; this one is checked against its
  // own source rather than trusted.
  //
  // EACH REFUSAL NAMES ITS REASON AND ITS WAY OUT — a NEXT that is merely
  // disabled looks broken, and he cannot tell which of fourteen tools is
  // holding it.
  const NAV_HOLDS = [
    [draft, "finish the shape you are drawing, or press ESC to cancel it — NEXT would drop it"],
    [scaleDraft, "finish the scale you are setting — tap the other end, or press ESC to cancel it — NEXT would drop it"],
    [scaleAsk, "type the inches for the span you tapped, or press ESC to cancel it — NEXT would drop it"],
    [dragVertex, "let go of the corner you are dragging first — NEXT would drop the move"],
    [busy, "saving this photo — one moment, then NEXT will move"],
  ];
  const navBlock = (NAV_HOLDS.find(([held]) => held) || [null, null])[1];
  const photoIdx = photos.findIndex((p) => p.photo_key === photoKey);
  const here = photoIdx >= 0 ? photos[photoIdx] : null;
  const goPhoto = (step) => {
    if (navBlock || !onSwitch) return;
    const nxt = photos[photoIdx + step];
    if (nxt) onSwitch(nxt.photo_key);
  };
  // SEND-204 — THE WALK'S OWN MOVES. A step SELECTS ITS TOOL and does
  // nothing else: it draws nothing, it writes nothing, and it cannot
  // confirm, refuse or price a mark. The mid-draft refusal that already
  // guards NEXT/PREV guards these too, and it names itself — a step change
  // may not drop a shape he is halfway through.
  const goStep = (i) => {
    const at = Math.max(0, Math.min(WALK_STEPS.length - 1, i));
    setStepIdx(at);
    // SEND-216 — the ONLY writer of the step memory, so the step can only
    // move through BACK / NEXT / SKIP / FINISHED / EXIT, which are the only
    // callers of this.
    STEP_MEMORY.set(stepKey, at);
    const t = WALK_STEPS[at].tool;
    if (t) { setTool(t); setDraft(null); setScaleDraft(null); }
    // SEND-272 — THE STEP NAMES THE J PRODUCT. Step 10 is soffit J, so the
    // step itself arms it (his ruling: "product locked to Soffit J") and the
    // chip shows on and dead. Step 9 carries siding, B&B and shake J, so a
    // soffit J left armed from the step before is DROPPED rather than drawn
    // on the wrong step. The color goes with the product either way: a list
    // belongs to the product it came from.
    const st = WALK_STEPS[at];
    if (st.jLock && ARMED_TYPE.j_channel !== st.jLock) {
      ARMED_TYPE.j_channel = st.jLock;
      setArmed({ ...ARMED_TYPE });
    } else if (st.jProducts && ARMED_TYPE.j_channel
               && !st.jProducts.includes(ARMED_TYPE.j_channel)) {
      ARMED_TYPE.j_channel = null;
      setArmed({ ...ARMED_TYPE });
    }
    // SEND-220 — and NOTHING STAYS SELECTED THAT HE CANNOT SEE. The step
    // change changes which kind is painted, so the selection goes with it.
    // The mark itself is untouched: no status is written, nothing is
    // removed, and selecting it again is one tap on the step that paints it.
    setSelectedId(null);
  };
  const setFinishedFlag = async (finished) => {
    try {
      await api.put(`/estimates/${est.id}/photo-takeoff/walk-finished`,
        { photo_key: photoKey, finished });
    } catch {
      toast.error("The walk is finished on screen, but this photo could not "
        + "record it — re-opening may show step 1 again");
    }
  };
  const walkNext = () => {
    if (navBlock) return;
    if (stepIdx >= WALK_STEPS.length - 1) {
      walkFinish();
      return;
    }
    goStep(stepIdx + 1);
  };
  // SEND-215 (Howard ruled 2026-08-14) — "FINISHED and EXIT both drop back to
  // today's grouped tool list. Do not stay in the walk after FINISHED." So
  // FINISHED records the walk as finished AND leaves the walk: the grouped
  // rail, the tool bar and the banners are all back on the next paint.
  const walkFinish = () => {
    if (navBlock) return;
    // SEND-219 — and THIS ESTIMATE IS HANDED BACK: the next photo he clicks
    // opens the free editor, not step 1.
    WALK_HANDED_BACK.add(est.id);
    setWalkDone(true);
    setWalkOn(false);
    setFinishedFlag(true);
  };
  // SEND-219 — EXIT GUIDED MODE DOES THE SAME. He said so: "the next photo
  // click is the free editor." Nothing about the marks or the walk_finished
  // flag changes — leaving the walk is not finishing it.
  const walkExit = () => {
    WALK_HANDED_BACK.add(est.id);
    setWalkOn(false);
  };
  const walkBack = () => { if (!navBlock) goStep(stepIdx - 1); };
  const walkSkip = () => {
    if (navBlock) return;
    const key = WALK_STEPS[stepIdx].key;
    setSkipped((cur) => (cur.includes(key) ? cur : [...cur, key]));
    walkNext();
  };
  const walkRestart = () => {
    // SEND-219 — the start-walk control walks THE PHOTO HE PRESSES IT ON. It
    // does not take the estimate back off handed-back, so the next photo he
    // clicks still opens the free editor: the photo is not this control.
    setWalkDone(false); setSkipped([]); goStep(0);
    setWalkOn(true);
    setFinishedFlag(false);
  };
  // SEND-245 (Howard ruled 2026-08-14) — JUMP-TO-STEP LINKS. "Clicking a link
  // does not wipe marks. It does not restart the walk. It opens that step so
  // he can add, edit, or re-tap." So this OPENS one step and does nothing
  // else: the skipped list is left exactly as it is (a restart is the only
  // thing that clears it), no mark is read, written or removed, and the
  // SEND-244 gate is untouched — the first tap on that step is still refused
  // until that product carries Standard or Architectural.
  // The server's walk_finished flag comes OFF while he is back inside the
  // walk, because a re-read after any save would otherwise flip him straight
  // back out of the step he just opened. Pressing FINISHED stamps it again.
  const walkJump = (key) => {
    if (navBlock) return;
    const i = WALK_STEPS.findIndex((s) => s.key === key);
    if (i < 0) return;
    setWalkDone(false);
    setWalkOn(true);
    goStep(i);
    setFinishedFlag(false);
  };
  // SEND-215 — the two-pane layout is on for exactly as long as the walk is
  // RUNNING. Finished or exited, the grouped editor is back.
  const walking = walkOn && !walkDone;
  const toggleLayer = (kind) => setHidden((cur) => (
    cur.includes(kind) ? cur.filter((k) => k !== kind) : [...cur, kind]));
  useEffect(() => { marksRef.current = marks; }, [marks]);

  // SEND-303 — ONE TAP, A QUARTER TURN CLOCKWISE, SAVED ON THE PAGE. The turn
  // is reconciled from the server like every other write-through on this
  // screen, so a refused turn never shows as turned. IT MOVES NO MARK: a
  // refusal puts the picture back and says nothing he measured changed.
  // SEND-304 — ONE TAP, THE WORDS OFF ON THIS PHOTO, SAVED ON THE PAGE. The
  // same write-through every other setting on this screen uses, so a refused
  // tap never shows as hidden. IT MOVES NO MARK.
  const toggleTags = async () => {
    const was = tagsHidden;
    const next = !was;
    await writeThrough({
      applyOptimistic: () => setTagsHidden(next),
      rollback: () => setTagsHidden(was),
      apiCall: () => api.put(`/estimates/${est.id}/photo-takeoff/tags-hidden`,
        { photo_key: photoKey, hidden: next }),
      reconcileFrom: (resp) => {
        const got = resp?.data?.tags_hidden;
        setTagsHidden(typeof got === "boolean" ? got : next);
      },
      refusalMessage: () => "That did not save — the words are back the way they were, and nothing you measured changed",
    });
  };

  const turnSheet = async () => {
    const was = turn;
    const next = (was + 90) % 360;
    await writeThrough({
      applyOptimistic: () => setTurn(next),
      rollback: () => setTurn(was),
      apiCall: () => api.put(`/estimates/${est.id}/photo-takeoff/turn`,
        { photo_key: photoKey, turn: next }),
      reconcileFrom: (resp) => {
        const got = resp?.data?.turn;
        setTurn([0, 90, 180, 270].includes(got) ? got : was);
        toast.success(`Turned to ${next}° — this page opens this way up from now on. Nothing you measured moved`);
      },
      refusalMessage: () => "That turn was not saved — the page is back the way it was, and no mark, size or scale changed",
    });
  };

  const tagElevation = async (wall) => {
    // WRITE-THROUGH (ruled 2026-08-11): no optimistic patch here at all —
    // the heading is RECONCILED FROM THE SERVER, so a refused tag never
    // shows as tagged. `applyOptimistic: null` is the opt-out the
    // contract allows, and a refusal says so in words.
    await writeThrough({
      applyOptimistic: null,
      rollback: () => {},
      apiCall: () => api.put(`/estimates/${est.id}/photo-takeoff/elevation`,
        { photo_key: photoKey, elevation: wall }),
      reconcileFrom: (resp) => {
        setElev(resp?.data?.elevation || null);
        toast.success(wall
          ? `Tagged ${wall.toUpperCase()} by hand — your call, and the sheet says it was you`
          : "Hand tag removed — this photo goes back to the read's call, or to OTHER PHOTOS");
      },
      refusalMessage: () => "That wall tag was not saved — nothing on this photo changed",
    });
  };

  // SEND-216 — which photo the last read was FOR. A re-read of the same photo
  // leaves the walk alone; a different photo is a different walk.
  // SEND-227 (Howard ruled 2026-08-14) — THE COLOUR-TIER TAP, PER PHOTO:
  // "Charter Oak → clap / Dutch lap → Standard / Architectural." A house can
  // wear Architectural clap on the front and Standard Dutch lap on a side, so
  // the tap is stored on the photo, never on the job. It is a TARGET, not a
  // price and not a paint name, and until it is made the paired lanes refuse
  // by name rather than guess which row he bought.
  const [taps, setTaps] = useState({});
  // SEND-294 — **EVERY LATER PHOTO OPENS WITH THE JOB PRODUCT ALREADY
  // SELECTED.** The photo's own override wins where it has one, the job
  // default carries the rest, and a photo he changed keeps that change. It
  // lands ONCE PER PHOTO, so his arming inside a photo is never overwritten —
  // and a product that names nothing arms nothing.
  const PREF_APPLIED = useRef(null);
  useEffect(() => {
    if (!prodPref || !photoKey) return;
    if (PREF_APPLIED.current === photoKey) return;
    PREF_APPLIED.current = photoKey;
    const own = (prodPref.by_photo || {})[photoKey] || {};
    const job = prodPref.job || {};
    const sid = own.siding || job.siding || null;
    const sof = own.soffit || job.soffit || null;
    if (sid && sid.series !== ARMED_TYPE.siding_series) {
      const ser = seriesOf(sid.series);
      if (ser) {
        ARMED_TYPE.siding_series = sid.series;
        // A PROFILE OR A WIDTH THE SERIES DOES NOT HAVE IS DROPPED, never
        // guessed — the SEND-221 rule, applied to what came back.
        ARMED_TYPE.siding_zone = (ser.types || []).includes(sid.profile)
          ? sid.profile : (OWN_PROFILE[sid.series] || null);
        ARMED_TYPE.siding_width = (ser.widths || []).includes(sid.width)
          ? sid.width : null;
      }
    }
    if (sof && sof.series !== ARMED_TYPE.soffit_series
        && SOFFIT_SERIES.some((x) => x.key === sof.series)) {
      ARMED_TYPE.soffit_series = sof.series;
      ARMED_TYPE.soffit_vent = sof.profile || null;
    }
    setArmed({ ...ARMED_TYPE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodPref, photoKey]);
  // SEND-293 — THE DERIVE ITSELF. It runs where the step READS the colour: the
  // family of the step he is on, the product he has armed, the colour on that
  // family's own field. No product, no colour, or an ambiguous colour derives
  // NOTHING. The write is the same per-photo tap door, flagged `derived` so a
  // protected estimate refuses it (423) and says so.
  useEffect(() => {
    if (!TAP_GATED_TOOLS.includes(tool) || estColors === null || busy) return;
    const fam = famOf(tool);
    const prod = tapProduct(fam, armed);
    if (!prod) return;
    const color = colorOfTapFamily(fam);
    if (!color) return;
    const want = fam === "soffit"
      ? soffitTierForColor(armed.soffit_series, color)
      : tierForColor(armed.siding_series, color);
    if (!want) return;
    const k = `${fam}|${prod}`;
    const once = `${photoKey}|${k}|${color}`;
    if (TIER_DERIVED.has(once)) return;
    TIER_DERIVED.add(once);
    if (taps[k] === want) return;
    (async () => {
      try {
        await api.put(`/estimates/${est.id}/photo-takeoff/color-tier`,
          { photo_key: photoKey, tier: want, clear: false, family: fam,
            product: prod, derived: true });
        setTaps((prev) => ({ ...prev, [k]: want }));
        toast.success(`${want === "standard" ? "Standard" : "Architectural"} on this photo for ${tapProductLabel(fam, armed)} — ${color} is in that collection`);
      } catch (e) {
        toast.error(e?.response?.data?.detail
          || `${color} names a color tier, but that tap was not saved`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, photoKey, estColors, taps, armed.siding_series, armed.siding_zone,
    armed.soffit_series]);
  const loadedKeyRef = useRef(null);
  const load = useCallback(async () => {    try {
      const { data } = await api.get(`/estimates/${est.id}/photo-takeoff`, { params: { photo_key: photoKey } });
      setMarks(data.marks || []);
      const per = (data.per_photo || {})[photoKey] || {};
      setScale(per.scale || null);
      setQty(per.quantities || null);
      setStage({ stage: per.stage || 1, ai_read: per.ai_read || null,
        stage_note: per.stage_note, proposals_refusal: per.proposals_refusal });
      setElev(per.elevation || null);
      // SEND-206 item 1 — A FINISHED WALK STAYS FINISHED. The flag is on the
      // photo's own record, so re-opening shows WALK FINISHED instead of
      // starting step 1 over the marks he already drew. Nothing is written to
      // the server about the walk beyond that flag: no step number, no order,
      // no history. (SEND-216 keeps the step in memory for this session only.)
      setWalkDone(!!per.walk_finished);
      // SEND-303 / SEND-304 — the page opens the way he left it: same way up,
      // words off if he left them off. Absent is upright and shown, never
      // guessed.
      setTurn([0, 90, 180, 270].includes(per.turn) ? per.turn : 0);
      setTagsHidden(per.tags_hidden === true);
      // SEND-227 — the colour-tier tap belongs to THIS photo. SEND-244 — and
      // to ONE PRODUCT on it, so the whole set comes back keyed by
      // family|product. A legacy photo-wide tap names no product and is not
      // in here: it no longer counts anywhere.
      setTaps(Object.fromEntries((per.color_taps || [])
        .filter((t) => t.tier)
        .map((t) => [`${t.family}|${t.product}`, t.tier])));
      // SEND-294 — the job default and every photo's own override, read whole.
      setProdPref(data.product_prefs || null);
      // SEND-216 (Howard ruled 2026-08-14) — P0. THIS RE-READ NO LONGER
      // TOUCHES THE WALK. It used to call `setStepIdx(0)` and `setSkipped([])`
      // every time, and every save re-reads the photo — so adding a mark on
      // step 3 threw him back to step 1. "Adding a mark does not. Confirming
      // a mark does not. Saving a mark does not. A photo re-read after save
      // does not."
      //
      // A DIFFERENT PHOTO IS A DIFFERENT WALK, and that is the one case that
      // still resets: its step comes out of the step memory (step 1 if it has
      // none), and skips are per photo, so they do not follow him across.
      if (loadedKeyRef.current !== stepKey) {
        loadedKeyRef.current = stepKey;
        setStepIdx(STEP_MEMORY.get(stepKey) || 0);
        setSkipped([]);
      }
      setOrderAll(data.opening_order || null);
      // SEND-289 — THE WALLS ON THIS JOB and the by-wall rollup, both read
      // straight off the same payload so the bar and the strip cannot drift
      // from each other or from the marks.
      setWalls((data.walls || {}).others || []);
      // SEND-302 — THE BY WALL STRIP CAME OFF THIS SCREEN, so this screen
      // no longer holds `by_wall` at all: SEND-181's rule is that state this
      // code sets and no screen can say is a vanishing door. The rollup
      // itself is untouched — the door still returns it and the estimate's own
      // by-wall reporting still reads it.
      setProducts(data.products || []);
      setProductsNote(data.products_note || null);
    } catch { toast.error("Could not read this photo's takeoff"); }
  }, [est.id, photoKey, stepKey]);
  useEffect(() => { load(); }, [load]);

  const sc = useMemo(() => inPerPx(scale), [scale]);
  // SEND-318 — the measured distance in FEET at this photo's scale. No scale,
  // no number — nothing is invented. px × in/px ÷ 12.
  const measureFeet = useMemo(() => {
    if (!sc?.ipp || measurePts.length < 2) return null;
    const a = measurePts[0], b = measurePts[1];
    const px = Math.hypot((b.x - a.x) * imgNat.w, (b.y - a.y) * imgNat.h);
    return (px * sc.ipp) / 12;
  }, [sc, measurePts, imgNat]);
  // SEND-321 — the distance written the way a plan prints it: 18'-0". Null when
  // there is no scale (nothing is invented); the rail readout says so.
  const measureLabel = useMemo(() => {
    if (measureFeet == null) return null;
    let ft = Math.floor(measureFeet);
    let inch = Math.round((measureFeet - ft) * 12);
    if (inch === 12) { ft += 1; inch = 0; }
    return `${ft}'-${inch}"`;
  }, [measureFeet]);
  // SEND-318 — leaving the step (tool) or switching the photo/page clears the
  // line. The Measure chip itself stays on the rail of every step.
  useEffect(() => { setMeasurePts([]); }, [tool, photoKey]);
  const sqftOf = (m) => {
    if (!sc) return null;
    const pts = m.points || [];
    if (pts.length < 3) return null;
    return Math.round(polyAreaPx(pts) * ((sc.ipp * sc.ipp) / 144) * 100) / 100;
  };

  // ── geometry plumbing (PdfOverlayEditor's convention: normalise against
  // the RENDERED rect exactly once, so zoom cancels and a vertex drag
  // tracks the pointer 1:1 at any zoom) ──────────────────────────────
  // SEND-255 (Howard ruled 2026-08-14) — THE RECT IS READ ONCE PER DRAG, NOT
  // ONCE PER MOUSEMOVE. `getBoundingClientRect()` on a photo rendered at 600%
  // forces a synchronous layout of that whole frame, and reading it inside
  // every pointermove is what put the layout thrash in the freeze. The frame
  // cannot move under a drag, so the rect taken at pointerdown IS the rect —
  // and the normalise-once convention, and 1:1 tracking, are unchanged.
  const dragRect = useRef(null);
  // SEND-303 — AND THE TAP IS MAPPED BACK THROUGH THE TURN. The rect a
  // rotated sheet reports is its upright bounding box, so a tap normalised
  // against it is in SCREEN space. These four cases put it back into the
  // PAGE'S OWN space before anything is stored, which is why a point drawn on
  // a turned page lands exactly where his finger was and why nothing already
  // stored had to move: 0° is the identity, and the other three are the
  // inverse of the quarter turn on screen.
  const unturn = (u, v) => (
    turn === 90 ? { x: v, y: 1 - u }
      : turn === 180 ? { x: 1 - u, y: 1 - v }
        : turn === 270 ? { x: 1 - v, y: u }
          : { x: u, y: v });
  const normFromEvent = (e) => {
    const r = dragRect.current || imgRef.current.getBoundingClientRect();
    const u = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const v = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    return unturn(u, v);
  };
  const toNat = (p) => ({ x: p.x * imgNat.w, y: p.y * imgNat.h });
  const toNorm = (p) => ({ x: imgNat.w ? p.x / imgNat.w : 0, y: imgNat.h ? p.y / imgNat.h : 0 });

  // SEND-255 (Howard ruled 2026-08-14) — OPENING NODE DRAG FROZE THE SCREEN.
  // P0. THE CAUSE, NAMED: every single `pointermove` wrote the marks array,
  // and that one write re-rendered the WHOLE editor — the live-quantities
  // panel, the plane box, the ft² cells, the layers panel, the marks panel
  // with every row and input, and every label on the canvas. Measured at
  // default zoom that was ~45 ms of render for ONE move; at 600% the same
  // render also re-lays-out a photo drawn six times its box, and
  // `getBoundingClientRect()` inside the move handler forced a synchronous
  // layout on top of it. Pointer moves arrive faster than that, the queue
  // never drains, and the screen is frozen.
  //
  // THE FIX, in three parts, and NOTHING about the geometry changes:
  //   1. ONE STATE WRITE PER ANIMATION FRAME. The latest pointer position is
  //      kept in a ref and committed on the next frame, so a burst of moves
  //      costs one render, not twenty. The LAST position always wins, so the
  //      node still tracks the cursor 1:1 — nothing is smoothed, averaged or
  //      dropped from the final point.
  //   2. THE RECT IS READ ONCE PER DRAG (see `dragRect` above).
  //   3. PLANE, ft² AND THE LIVE QUANTITIES ARE NOT RECOMPUTED ON A MOVE:
  //      the panels read a FROZEN snapshot of the marks for the length of the
  //      drag and the server figures are re-read on mouseup, exactly as he
  //      ruled. The canvas still moves, because that is the thing he is
  //      dragging.
  const dragLatest = useRef(null);
  const dragFrame = useRef(0);
  const dragFrozen = useRef(null);
  useEffect(() => {
    if (!dragVertex) return;
    dragRect.current = imgRef.current
      ? imgRef.current.getBoundingClientRect() : null;
    dragFrozen.current = marksRef.current;
    const commit = () => {
      dragFrame.current = 0;
      const nat = dragLatest.current;
      if (!nat) return;
      setMarks((cur) => cur.map((m) => {
        if (m.id !== dragVertex.markId) return m;
        // SEND-317 — a PLACED sheet moves RIGIDLY: the whole box follows the
        // finger and keeps its real size. A drawn mark moves the one vertex.
        if (m.placed) {
          const o = m.points[dragVertex.index];
          const dx = nat.x - o.x, dy = nat.y - o.y;
          return { ...m, points: m.points.map((q) => ({ x: q.x + dx, y: q.y + dy })) };
        }
        return { ...m, points: m.points.map((p, i) => (i === dragVertex.index ? nat : p)) };
      }));
    };
    const up = () => {
      if (dragFrame.current) {
        cancelAnimationFrame(dragFrame.current);
        commit();
      }
      const m = marksRef.current.find((x) => x.id === dragVertex.markId);
      const moved = dragLatest.current;
      dragLatest.current = null;
      dragRect.current = null;
      dragFrozen.current = null;
      setDragVertex(null);
      // THE WRITE — AND THE ONLY RECOMPUTE — HAPPENS HERE, ON MOUSEUP.
      if (m && moved) patchMark(m.id, { points: m.points });
    };
    const move = (e) => {
      if (!imgRef.current) return;
      if (e.pointerType === "mouse" && e.buttons === 0) { up(); return; }
      dragLatest.current = toNat(normFromEvent(e));
      if (!dragFrame.current) dragFrame.current = requestAnimationFrame(commit);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      if (dragFrame.current) {
        cancelAnimationFrame(dragFrame.current);
        dragFrame.current = 0;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragVertex]);
  // The marks the PANELS read: frozen for the length of a node drag, so no
  // plane, no ft², no live quantity and no row is recomputed on a mousemove.
  // Nothing is lost — the drag's own marks land on mouseup and the panels
  // pick them up with the server's own figures on the next read.
  const panelMarks = (dragVertex && dragFrozen.current) || marks;

  // ── server calls ───────────────────────────────────────────────────
  const addMark = async (points, shape, extra = {}) => {
    try {
      const body = {
        photo_key: photoKey, kind: tool, shape,
        points: points.map(toNat),
        category: tool === "non_siding_zone" ? category : null,
        // SEND-217 — the type he armed BEFORE the first click rides the
        // mark. A name only: the quantity math is untouched.
        ...(tool === "siding_zone" ? { siding_type: armed.siding_zone,
          siding_series: armed.siding_series,
          siding_width: armed.siding_width } : {}),
        // SEND-291 — NO MARK-LOCAL COLOUR RIDES A J OR A SOFFIT ANY MORE. The
        // colour is the parent field on the estimate, one per job, read by
        // every step that rides it. The server's own `j_color` /
        // `soffit_color` doors are untouched and every mark already carrying
        // one keeps it: nothing is migrated and nothing is rewritten.
        ...(tool === "j_channel" ? { j_type: armed.j_channel,
          j_size: armed.j_size } : {}),
        // SEND-282 — the STARTER product he armed rides the line he just
        // drew. Absent means NO CHIP, which the apply reads as a SIDING
        // starter: the step was renamed, not changed underneath him.
        ...(tool === "starter" && armed.starter
          ? { starter_type: armed.starter } : {}),
        // SEND-299 — the FINISH TRIM product he armed rides the line he just
        // drew. No chip means NO CHIP: the server reads that as the SIDING
        // finish trim this step has always written.
        ...(tool === "finish_trim" && armed.trim
          ? { trim_type: armed.trim } : {}),
        // SEND-286 — the BAND WIDTH he armed rides the band line he just
        // drew. It is REQUIRED: the screen refuses the first tap without it
        // and the server refuses the mark, so no band is stored with no width.
        ...(tool === "band_board"
          ? { band_width_in: armed.band_width,
            // SEND-288 — and WHICH COIL it batches with, armed to MATCH
            // FASCIA by default so a drawn band always names its coil.
            band_match: armed.band_match } : {}),
        ...(tool === "soffit" ? { soffit_series: armed.soffit_series,
          soffit_vent: armed.soffit_vent } : {}),
        // SEND-244 — THE TAP RIDES THE MARK, with the product it belonged to.
        // It is EVIDENCE of what he tapped before he drew, never a price: the
        // lane reads it at apply time so no run lands on a row nobody tapped
        // for. Fascia, starter, scale, openings and the masks have no tap and
        // carry none.
        ...(famOf(tool) ? { vinyl_tier: tapOf(famOf(tool)),
          vinyl_product: tapProduct(famOf(tool), armed) } : {}),
        // SEND-242 — the product he armed rides the box he just drew. It is
        // the SAME field the rail's row chips write and it is a NAME: no ft²,
        // no deduction, no count and no money move with it.
        ...(tool === "opening" ? { opening_type: armed.opening } : {}),
        // SEND-261 — the WINDOW STYLE he armed rides the box he just drew, and
        // ONLY a window's box: it is a name, not a size, a count or a price.
        ...(tool === "opening" && armed.opening === "window"
          && armed.opening_style ? { style: armed.opening_style } : {}),
        // SEND-311 — THE LP PICKS RIDE THE MARK (lp_smart jobs). The product
        // chip names the row the lane lands on, and the LP color rides beside
        // it, a label only. Absent on a vinyl job — `lpWalk` is false there.
        ...(lpWalk && tool === "siding_zone" && armed.lp_product
          ? { lp_product: armed.lp_product } : {}),
        // SEND-316 — the B&B batten on-center pick rides a SHEET mark (4'×8' /
        // 4'×10'). Vertical Panel never carries it (fixed 16"); no pick, no key.
        ...(lpWalk && tool === "siding_zone"
          && (armed.lp_product === "panel_4x8" || armed.lp_product === "panel_4x10")
          && armed.bb_oc
          ? { bb_oc: armed.bb_oc } : {}),
        ...(lpWalk && (tool === "band_board" || tool === "fascia")
          && armed.lp_trim_series
          ? { band_series: armed.lp_trim_series } : {}),
        // SEND-323 follow-up — the LP fascia SIZE rides the mark; the lane
        // lands it on the 440/540 trim row for that series+size.
        ...(lpWalk && tool === "fascia" && armed.band_width != null
          ? { band_width_in: armed.band_width } : {}),
        ...(lpWalk && tool === "outside_corner" && armed.lp_osc_size
          ? { corner_size: armed.lp_osc_size } : {}),
        ...(lpWalk && armed.lp_color
          && ["siding_zone", "band_board", "outside_corner",
              "soffit", "fascia", "inside_corner"].includes(tool)
          ? { lp_color: armed.lp_color } : {}),
        ...extra,
      };
      const { data } = await api.post(`/estimates/${est.id}/photo-takeoff/marks`, body);
      await load();
      // SEND-147 — the tap moved the box, so say so and say what it did not move.
      if (data?.rebase?.moved) toast.success(`${tool === "eave" ? "Eave" : "Start"} line set — ${data.rebase.moved} provisional zone(s) moved to it`);
      (data?.rebase?.notes || []).forEach((n) => toast.info(n));
      if (!sc && !LINE_TOOLS.includes(tool)) toast.warning("No scale on this photo yet — the mark saves, but it carries no quantity until you set the scale");
      if (!sc && LINEAR_TOOLS.includes(tool)) toast.warning("No scale on this photo yet — the line saves in PIXELS and needs a length on this photo; no feet are invented");
      if (data?.corner_shot_refusal) toast.error(data.corner_shot_refusal);
    } catch (e) { toast.error(e?.response?.data?.detail || "The mark was refused"); }
  };
  const patchMark = async (id, body) => {
    try {
      const { data } = await api.patch(`/estimates/${est.id}/photo-takeoff/marks/${id}`, body);
      if (data?.mark?.refused_reason === "geometry changed after confirmation — re-confirm the new figure") {
        toast.warning("Geometry changed — that mark went back to PROVISIONAL; confirm the new figure");
      }
      await load();
      if (data?.rebase?.moved) toast.success(`Start line moved — ${data.rebase.moved} provisional zone(s) followed it`);
      (data?.rebase?.notes || []).forEach((n) => toast.info(n));
    } catch (e) { toast.error(e?.response?.data?.detail || "The change was refused"); }
  };
  // SEND-319 (+ 319 follow-up) — ROTATE A PLACED SHEET 90°. The box keeps its
  // REAL panel size: a 4'×8' sheet becomes 8'×4', a 4'×10' becomes 10'×4', and
  // a second tap turns it back to standing. It is NEVER a square. It stays ONE
  // panel (counted by box) and the battens follow, because the backend reads
  // the box's own width and height.
  //
  // THE POINTS ARE STORED IN NATURAL PIXELS (addMark does `points.map(toNat)`,
  // nMark renders via `toNorm`), and the photo scale is ISOTROPIC — a stored
  // box is `ft×12/ipp` pixels on BOTH axes. So the turn is a plain swap of the
  // box's pixel width and height about its own centre: real size preserved, no
  // dependence on the image's aspect, and patchMark is handed the same pixel
  // space every other write uses. (The earlier version mis-read the pixels as
  // normalised and double-scaled by imgNat, collapsing the box toward a square
  // on screen — 319 follow-up fixes exactly that.)
  const rotateSheet = (m) => {
    if (!m?.placed) return;
    const xs = m.points.map((p) => p.x), ys = m.points.map((p) => p.y);
    const x0 = Math.min(...xs), y0 = Math.min(...ys);
    const pxW = Math.max(...xs) - x0, pxH = Math.max(...ys) - y0;
    const cx = x0 + pxW / 2, cy = y0 + pxH / 2;      // the box's own centre
    const nx = cx - pxH / 2, ny = cy - pxW / 2;      // swapped dims, re-centred
    patchMark(m.id, { points: [{ x: nx, y: ny }, { x: nx + pxH, y: ny },
      { x: nx + pxH, y: ny + pxW }, { x: nx, y: ny + pxW }] });
  };
  // SEND-261 (Howard ruled 2026-08-14) — WINDOW STYLE ON THE OPENINGS RAIL.
  // ONE TAP, BOTH JOBS, no extra click: it RETAGS the selected window box and
  // ARMS the boxes he draws next. Tap the same one again to clear it; skip it
  // and the box stays WINDOW with no style. THE TEN NAMES ARE SEND-254'S OWN
  // LIST — no eleventh type — and a style is a NAME ON THE MARK: it moves no
  // size, no COUNT, no ft², no deduction and no money. Garage door and entry
  // door are never offered it, and SLIDING GLASS DOOR THE PRODUCT stays as
  // shipped — "Sliding glass" the STYLE only ever rides a WINDOW.
  const armOpeningStyle = async (s) => {
    const next = ARMED_TYPE.opening_style === s ? null : s;
    ARMED_TYPE.opening_style = next;
    setArmed({ ...ARMED_TYPE });
    const sel = marks.find((m) => m.id === selectedId);
    if (sel && sel.kind === "opening" && sel.opening_type === "window") {
      await patchMark(sel.id, { style: next || "" });
    }
  };
  const delMark = async (id) => {
    try {
      const { data } = await api.delete(`/estimates/${est.id}/photo-takeoff/marks/${id}`);
      if (selectedId === id) setSelectedId(null);
      await load();
      if (data?.rebase?.moved) toast.info(`Start line deleted — ${data.rebase.moved} provisional zone(s) went back to the read's own answer`);
    } catch { toast.error("Could not delete that mark"); }
  };
  const commitScale = async (p1, p2, inches) => {
    try {
      const { data } = await api.put(`/estimates/${est.id}/photo-takeoff/scale`, {
        photo_key: photoKey, anchor: { p1: toNat(p1), p2: toNat(p2), inches },
      });
      setScaleAsk(null); setSpanFt(""); setSpanIn("");
      await load();
      if (data.refusal) toast.warning(data.refusal);
    } catch (e) { toast.error(e?.response?.data?.detail || "The scale was refused"); }
  };
  const commitTape = async () => {
    const inches = ftin(tapeFt, tapeIn);
    if (!inches) { toast.error("Type the taped figure for that same span"); return; }
    try {
      await api.put(`/estimates/${est.id}/photo-takeoff/scale`, { photo_key: photoKey, tape_inches: inches });
      setTapeFt(""); setTapeIn("");
      await load();
      toast.success("Tape set — THE TAPE GOVERNS this photo's scale");
    } catch (e) { toast.error(e?.response?.data?.detail || "The tape was refused"); }
  };
  const clearScale = async () => {
    await api.put(`/estimates/${est.id}/photo-takeoff/scale`, { photo_key: photoKey, clear: true });
    await load();
  };
  const importAnnotations = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/estimates/${est.id}/photo-takeoff/import-annotations`, null, { params: { photo_key: photoKey } });
      await load();
      toast.success(data.imported ? `${data.imported} mark(s) pulled in — all PROVISIONAL` : "Nothing new to pull in");
      if (data.scale_note) toast.info(data.scale_note);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };
  const pullProposals = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/estimates/${est.id}/photo-takeoff/propose`, null, { params: { photo_key: photoKey } });
      await load();
      toast.success(data.proposed
        ? `${data.proposed} AI proposal(s) on this photo — all PROVISIONAL until you rule on them`
        : "Nothing new — this read's proposals are already here");
      if (data.openings_without_a_box_note) toast.warning(data.openings_without_a_box_note);
      Object.values(data.kinds_absent || {}).forEach((n) => toast.info(n));
    } catch (e) { toast.error(e?.response?.data?.detail || "The read had nothing to propose here"); }
    setBusy(false);
  };
  const pullZones = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/estimates/${est.id}/photo-takeoff/propose-zones`, null, { params: { photo_key: photoKey } });
      await load();
      if (data.refusal) toast.warning(data.refusal);
      else if (data.moved) toast.success(
        `${data.face?.toUpperCase()} — ${data.moved} zone(s) MOVED to the wall_base line you tapped${data.proposed ? `, ${data.proposed} newly placed` : ""}`);
      else toast.success(data.proposed
        ? `${data.face?.toUpperCase()} — ${data.proposed} starting zone(s) placed, all PROVISIONAL: move them onto the wall you see, then confirm`
        : `${data.face?.toUpperCase()} — this read's starting zones are already here; nothing was overwritten`);
      (data.notes || []).forEach((n) => toast.info(n));
      if (data.openings?.note) toast.warning(data.openings.note);
    } catch (e) { toast.error(e?.response?.data?.detail || "There were no findings to hand off here"); }
    setBusy(false);
  };
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/estimates/${est.id}/photo-takeoff/apply`);
      const b = data.photo_takeoff;
      toast.success(`Quantities written — siding ${b.photo_siding_sqft ?? "—"} ft² · non-siding ${b.photo_non_siding_sqft ?? "—"} ft² · openings ${b.photo_opening_count ?? "—"}. No money written.`);
      // SEND-225 (Howard's field result, ruled 2026-08-14) — EVERY ROW SAYS
      // WHAT IT DID. "No money written" over four silent zeros was the
      // defect: a landed row names itself with its QTY and unit, and a
      // REFUSED ROW IS A SENTENCE, NOT A BLANK. One toast per row, in the
      // writer's own words — nothing is summarised away and nothing invented.
      // SEND-226 (Howard's field result) — A KEEP IS NOT A REFUSE. Yellow
      // when his typed QTY stood and the derived figure sits beside it,
      // GREEN only when the box itself changed, RED only for a real refuse
      // (no row in his catalog, no size, a tier collision). Red on a
      // successful 36 LF is why the walk felt like a failure.
      (b.photo_siding_lines || []).forEach((r) => {
        const name = r.line || r.label || r.kind;
        if (r.qty_src === "human" && r.kept_note)
          toast.warning(`Kept your QTY — ${name}: derived ${r.raw_qty} ${r.unit || ""} sits beside it`.trim());
        else if (r.qty_written != null && r.qty_src === "photo")
          toast.success(`Landed — ${name}: ${r.qty_written} ${r.unit || ""}${r.created ? " (row added from your catalog)" : ""}`.trim());
        else if (r.refusal) toast.error(`Refused — ${name}: ${r.refusal}`);
      });
      // THE PAGE BEHIND THIS EDITOR IS NOW STALE: APPLY wrote `lines` on the
      // server, and a stale page's own autosave would PUT `derived_qty: null`
      // straight back over it. Re-read the estimate so the list shows what
      // landed and the erase edge cannot happen.
      if (onWrote) onWrote();
    } catch (e) {
      toast.error(e?.response?.status === 423
        ? "Protected estimate — this derived write is refused (423)"
        : (e?.response?.data?.detail || "Apply failed"));
    }
    setBusy(false);
  };

  // ── drawing ────────────────────────────────────────────────────────
  const onPointerDown = (e) => {
    if (!imgRef.current || pinch.current) return;
    if (e.pointerType === "touch" && e.isPrimary === false) return;
    const p = normFromEvent(e);
    // SEND-318 — MEASURE short-circuits every draw path: while measuring, a tap
    // only moves the ephemeral line. Two taps draw it; a THIRD tap clears it.
    // It saves no mark, fills no row, changes no quantity — it returns here, so
    // neither the vinyl walk nor the LP walk is touched.
    if (measuring) {
      setMeasurePts((cur) => (cur.length >= 2 ? [] : [...cur, p]));
      return;
    }
    if (tool === "scale") {
      if (!scaleDraft?.p1) { setScaleDraft({ p1: p, p2: p }); return; }
      const d = Math.hypot((p.x - scaleDraft.p1.x) * imgNat.w, (p.y - scaleDraft.p1.y) * imgNat.h);
      if (d < 8) { toast.error("Second tap too close — tap the other end of the known span"); return; }
      setScaleAsk({ p1: scaleDraft.p1, p2: p });
      setScaleDraft(null);
      return;
    }
    if (dragVertex) return;
    // SEND-217 — NO TYPE PICKED, NO DRAWING. The refusal lands on the FIRST
    // click, before any draft exists, and it names the chips to pick from.
    if (tool === "siding_zone") {
      if (lpWalk && !armed.lp_product) {
        toast.error("Pick the LP product first — Lap, Nickel Gap, Shake, a panel or Vertical Panel. It names the row and nothing is drawn until you do");
        return;
      }
      if (!lpWalk) {
        const miss = sidingPickMissing(armed);
        if (miss) { toast.error(miss); return; }
      }
    }
    if (tool === "soffit") {
      if (lpWalk && !armed.soffit_vent) {
        toast.error("Pick vented or solid first — it names the LP soffit row and nothing is drawn until you do");
        return;
      }
      if (!lpWalk) {
        const miss = soffitPickMissing(armed);
        if (miss) { toast.error(miss); return; }
      }
    }
    if (lpWalk && tool === "band_board" && !armed.lp_trim_series) {
      toast.error('Pick the LP trim series first — 440 (4/4") or 540 (5/4"). It names the LP SmartSide Trim row and nothing is defaulted');
      return;
    }
    // SEND-323 follow-up — the LP FASCIA step needs BOTH the series and the
    // size before the first tap, the same as Trim Board. Nothing is defaulted,
    // and the fascia lands on that 440/540 trim row — never a vinyl row.
    if (lpWalk && tool === "fascia") {
      if (!armed.lp_trim_series) {
        toast.error('Pick the LP trim series first — 440 (4/4") or 540 (5/4"). It names the LP SmartSide Trim row the fascia lands on; nothing is defaulted');
        return;
      }
      if (!armed.band_width) {
        toast.error('Pick the fascia size first — 4", 6", 8", 10" or 12". It names the LP trim row for this size; nothing is defaulted');
        return;
      }
    }
    // SEND-223 — the J needs BOTH picks, and the refusal names the missing one.
    if (tool === "j_channel") {
      const miss = jPickMissing(armed);
      if (miss) { toast.error(miss); return; }
    }
    // SEND-313 follow-up — the vinyl "Pick a siding type first" gate is a
    // VINYL gate. On an LP job the siding step is gated by lp_product + chip
    // (above), and siding_zone is never set, so this must not fire there.
    if (TYPE_FIRST[tool] && !armed[tool] && !(lpWalk && tool === "siding_zone")) { toast.error(TYPE_FIRST[tool]); return; }
    // SEND-244 (Howard ruled 2026-08-14) — STANDARD / ARCHITECTURAL IS
    // REQUIRED BEFORE THE FIRST TAP on every step that has those two chips.
    // "Do not let the contractor draw first and pick later." The tap must
    // belong to THE ARMED PRODUCT on THIS PHOTO: clap's tap does not unlock
    // the soffit, and a Standard row already on the estimate is not a tap.
    // SEND-263 (Howard ruled 2026-08-14) — THAT GATE IS FOR THE PRODUCT STEPS
    // ONLY. From the field: "Howard cannot draw J, corners, or finish trim.
    // Toast: Pick the siding product first. WRONG GATE. That toast belongs on
    // a siding color pick, not on drawing J." So drawing J, outside corner,
    // inside corner and finish trim requires NO siding product, NO Standard or
    // Arch, and NO accessories color. On those rails the tap ONLY FILTERS the
    // color list (SEND-262) — skip the color and the line still draws, and
    // nothing is defaulted in its place. The gate is UNCHANGED where the tap
    // names the product's own catalog row: the siding zones and the soffit.
    // SEND-293 — the same two steps own the DERIVE, so the list lives at
    // module scope now and both readers share the one declaration.
    // SEND-313 follow-up 2 — the TAP-PRODUCT / Standard-Arch gate is a VINYL
    // gate. On an LP job the siding step is gated by lp_product + chip (above)
    // and the soffit by its vent pick; the vinyl series/profile tier never
    // shows on LP, so this gate must not fire there.
    const tapFam = (!lpWalk && TAP_GATED_TOOLS.includes(tool)) ? famOf(tool) : null;
    if (tapFam) {
      if (!tapProduct(tapFam, armed)) {
        toast.error(TAP_PRODUCT_FIRST[tapFam]); return;
      }
      if (!tapOf(tapFam)) { toast.error(TAP_FIRST[tapFam]); return; }
    }
    // SEND-286 — THE BAND WIDTH IS THE GATE ON ITS OWN STEP: no chip, no
    // first tap. The width is the yield bucket, so nothing is defaulted to 8"
    // and no band is drawn without one.
    if (tool === "band_board" && !armed.band_width) {
      toast.error("Pick the trim board size first — 4\", 6\", 8\", 10\" or 12\". It is required on every trim board mark: the size names the yield bucket, and nothing is defaulted to 8\"");
      return;
    }
    // SEND-288 — and WHICH COIL it batches with. This one is ARMED TO MATCH
    // FASCIA by his ruling, so it only stops a tap if he cleared it.
    if (tool === "band_board" && !armed.band_match) {
      toast.error("Pick MATCH FASCIA or MATCH SIDING first — every trim board mark names the coil it batches with: fascia goes on the soffit .019 Coil, siding on the accessories .019 Coil, and nothing is assumed");
      return;
    }
    const pts = draft?.points || [];
    // SEND-147 — THE WALL BASE IS A TWO-TAP LINE: left end, then right end.
    // Same gesture as the scale, and it is stored as an ANCHOR — no LF.
    // SEND-208 — a J BOX is the two-tap box the openings already use, and a
    // J POLYGON falls through to the polygon gesture below (tap each corner,
    // tap the first again to close). A J LINE is the two-tap line it always
    // was. NOTHING IS AUTO-BUILT FROM AN OPENING: J is only what he draws.
    if (shapeTool && shapeNow === "rect") {
      if (!pts.length) { setDraft({ points: [p], cx: p.x, cy: p.y }); return; }
      const a = pts[0];
      setDraft(null);
      addMark([a, { x: p.x, y: a.y }, p, { x: a.x, y: p.y }], "rect");
      return;
    }
    if (shapeTool && shapeNow === "poly") {
      if (pts.length >= 3) {
        const d = Math.hypot(pts[0].x - p.x, pts[0].y - p.y);
        if (d < 0.02) { setDraft(null); addMark(pts, "poly"); return; }
      }
      setDraft({ points: [...pts, p], cx: p.x, cy: p.y });
      return;
    }
    // SEND-317 — PLACE A SHEET. One tap stamps a box the real size of the
    // armed 4'×8' / 4'×10' sheet at this photo's scale, centered on the tap.
    // The box IS the zone (it fills battens too) and counts as ONE panel.
    if (tool === "siding_zone" && zoneShape === "sheet"
        && (armed.lp_product === "panel_4x8"
            || armed.lp_product === "panel_4x10")) {
      if (!sc.ipp) {
        toast.error("No scale on this photo — set the tape before placing a sheet");
        return;
      }
      const ftW = 4;
      const ftH = armed.lp_product === "panel_4x10" ? 10 : 8;
      const wN = (ftW * 12 / sc.ipp) / imgNat.w;   // sheet width, normalized
      const hN = (ftH * 12 / sc.ipp) / imgNat.h;   // sheet height, normalized
      const x0 = p.x - wN / 2, y0 = p.y - hN / 2;  // centered on the tap
      setDraft(null);
      addMark([{ x: x0, y: y0 }, { x: x0 + wN, y: y0 },
               { x: x0 + wN, y: y0 + hN }, { x: x0, y: y0 + hN }],
              "rect", { placed: true });
      return;
    }
    // SEND-306 — THE SIDING ZONE'S BOX. Two taps, opposite corners, stored as
    // the same four corners a tapped zone would be, so its square feet come
    // off the box by the very same area reader. CORNERS is untouched: it is
    // the default and it falls through to the polygon gesture at the end.
    if (tool === "siding_zone" && zoneShape === "rect") {
      if (!pts.length) { setDraft({ points: [p], cx: p.x, cy: p.y }); return; }
      const a = pts[0];
      setDraft(null);
      addMark([a, { x: p.x, y: a.y }, p, { x: a.x, y: p.y }], "rect");
      return;
    }
    if (tool === "wall_base" || tool === "eave" || LINEAR_TOOLS.includes(tool)) {
      if (!pts.length) { setDraft({ points: [p], cx: p.x, cy: p.y }); return; }
      const d = Math.hypot((p.x - pts[0].x) * imgNat.w, (p.y - pts[0].y) * imgNat.h);
      if (d < 8) { toast.error(`Second tap too close — tap the OTHER end of the ${tool === "eave" ? "eave" : tool === "wall_base" ? "start" : tool} line`); return; }
      setDraft(null);
      addMark([pts[0], p], "line");
      return;
    }
    if (tool === "opening") {
      // an opening is a TWO-TAP BOX — tap one corner, tap the opposite one
      if (!pts.length) { setDraft({ points: [p], cx: p.x, cy: p.y }); return; }
      const a = pts[0];
      setDraft(null);
      addMark([a, { x: p.x, y: a.y }, p, { x: a.x, y: p.y }], "rect");
      return;
    }
    // SEND-139 — GABLE: three taps, LEFT EAVE → PEAK → RIGHT EAVE.
    // DORMER: four taps round the vertical face. The annotator's gesture,
    // unchanged; the count is fixed, so nothing is padded to fit.
    if (tool === "gable" || tool === "dormer") {
      const need = tool === "gable" ? 3 : 4;
      const next = [...pts, p];
      if (next.length < need) { setDraft({ points: next, cx: p.x, cy: p.y }); return; }
      setDraft(null);
      addMark(next, "poly");
      return;
    }
    if (pts.length >= 3) {
      const d = Math.hypot(pts[0].x - p.x, pts[0].y - p.y);
      if (d < 0.02) { setDraft(null); addMark(pts, "poly"); return; }
    }
    setDraft({ points: [...pts, p], cx: p.x, cy: p.y });
  };
  const onPointerMove = (e) => {
    if (!imgRef.current || dragVertex) return;
    const p = normFromEvent(e);
    if (tool === "scale" && scaleDraft?.p1) { setScaleDraft({ ...scaleDraft, p2: p }); return; }
    if (draft) setDraft({ ...draft, cx: p.x, cy: p.y });
  };
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") { setDraft(null); setScaleDraft(null); setScaleAsk(null); }
      if (e.key === "Enter" && draft?.points?.length >= 3
          && tool !== "opening" && tool !== "gable" && tool !== "dormer") {
        const pts = draft.points; setDraft(null); addMark(pts, "poly");
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, tool, category, sc]);

  // ── zoom: wheel (cursor-anchored) + PINCH on a phone ───────────────
  const bumpZoom = (f) => setZoom((z) => Math.max(0.5, Math.min(6, z * f)));
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const ox = e.clientX - rect.left;
      const oy = e.clientY - rect.top;
      wheelAnchor.current = {
        fx: el.scrollWidth ? (el.scrollLeft + ox) / el.scrollWidth : 0.5,
        fy: el.scrollHeight ? (el.scrollTop + oy) / el.scrollHeight : 0.5,
        ox, oy,
      };
      bumpZoom(e.deltaY < 0 ? 1.1 : 1 / 1.1);
    };
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onTouchStart = (e) => {
      if (e.touches.length !== 2) return;
      pinch.current = { d0: dist(e.touches), z0: zoom };
      setDraft(null); setScaleDraft(null);
    };
    const onTouchMove = (e) => {
      if (!pinch.current || e.touches.length !== 2) return;
      e.preventDefault();
      const ratio = dist(e.touches) / (pinch.current.d0 || 1);
      setZoom(Math.max(0.5, Math.min(6, pinch.current.z0 * ratio)));
    };
    const onTouchEnd = (e) => { if (e.touches.length < 2) setTimeout(() => { pinch.current = null; }, 120); };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [zoom]);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const a = wheelAnchor.current;
    if (!el || !a) return;
    el.scrollLeft = a.fx * el.scrollWidth - a.ox;
    el.scrollTop = a.fy * el.scrollHeight - a.oy;
    wheelAnchor.current = null;
  }, [zoom]);

  // SEND-175 item 2 — FULL SCREEN MEANS THE WHOLE PHOTO IS IN THE MEASURE
  // WINDOW, not a bigger photo he still has to scroll. At 100% the frame is
  // sized so the photo's own aspect fits the canvas column; zoom then
  // multiplies it exactly as before. THIS TOUCHES DISPLAY ONLY — every
  // point is stored in the photo's NATURAL pixels and every gesture
  // normalises against the RENDERED rect once (`normFromEvent` →
  // `toNat`), so the ft², the scale and the plane basis cannot move when
  // the frame does, and a vertex drag still tracks 1:1.
  const [colBox, setColBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const read = () => setColBox({ w: el.clientWidth, h: el.clientHeight });
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [full]);  // the column is re-measured when the shell changes
  // SEND-302 (Howard ruled 2026-08-14) — **A BLUEPRINT PAGE FITS THE
  // ELEVATION WHEN THE WALK OPENS. THE WHOLE ELEVATION IS ON SCREEN. HE ZOOMS
  // IN TO DRAW. DO NOT OPEN IT CROPPED.** A plan sheet is far taller than it
  // is wide, so at 100% of the column it opened past the bottom of the window
  // and he had to scroll to find the elevation. The fit now applies IN BOTH
  // SHELLS, not only full screen: the frame is sized to the image's OWN
  // ASPECT so the whole of it lands in the measure window, and zoom still
  // multiplies it exactly as before. DISPLAY ONLY — every point is stored in
  // natural pixels and every gesture normalises against the RENDERED rect, so
  // no ft², no scale and no plane basis can move with the frame.
  const fitPct = (() => {
    if (!imgNat.w || !imgNat.h || !colBox.w || !colBox.h) return 100;
    const availW = colBox.w - 24;   // the p-3 gutter, both sides
    const availH = colBox.h - 24;
    if (availW <= 0 || availH <= 0) return 100;
    // SEND-303 — A TURNED PAGE IS FITTED THE WAY IT IS SHOWN. On a quarter
    // turn the sheet's width and height swap on screen, so the fit swaps with
    // it; otherwise a sideways elevation he just turned would open past the
    // edge of the window, which is the very thing SEND-302 fixed.
    if (turn === 90 || turn === 270) {
      return Math.min(100, Math.max(20, 100 * Math.min(
        imgNat.w / imgNat.h, availH / availW)));
    }
    const wForH = availH * (imgNat.w / imgNat.h);
    return Math.min(100, Math.max(20, (wForH / availW) * 100));
  })();

  // SEND-303 — THE SHEET'S BOX, IN PIXELS, SO THE TURNED PAGE STILL HAS A
  // PLACE TO SIT. A rotated element keeps its upright layout box, so without
  // this the turned sheet would hang outside the scroller and he could not
  // reach the bottom of it. The OUTER box is what the turn actually occupies
  // (width and height swapped on a quarter turn) and the INNER box — the one
  // that carries the image and every mark — is the sheet itself, rotated
  // about its own centre inside it. DISPLAY ONLY: no stored point is touched.
  const sheetReady = !!(imgNat.w && imgNat.h && colBox.w - 24 > 0);
  const sheetBox = sheetReady ? (() => {
    const availW = colBox.w - 24;
    const w = (availW * zoom * fitPct) / 100;
    const h = w * (imgNat.h / imgNat.w);
    const swap = turn === 90 || turn === 270;
    return { w, h, outW: swap ? h : w, outH: swap ? w : h };
  })() : null;

  // SEND-139 — the annotator's own gable/dormer math, on this photo's
  // scale. gableDims returns base, rise, pitch and grossAreaFt = ½ ×
  // base × rise; dormerDims averages the opposing edges. Nothing here is
  // a second implementation of either.
  const gDims = (m) => (m.kind === "gable" ? gableDims(m.points, sc?.ipp || null)
    : m.kind === "dormer" ? dormerDims(m.points, sc?.ipp || null) : null);

  // PITCH → PEAK, ported verbatim: rise = base/2 × pitch/12. The peak
  // MOVES, which is a geometry change — a confirmed gable returns to
  // provisional, exactly as any other adjustment does.
  const applyGablePitch = (m, pitch) => {
    const [L, P, R] = m.points;
    const basePx = Math.hypot(R.x - L.x, R.y - L.y);
    if (basePx <= 0) { toast.error("this gable has no width — re-tap the eave points"); return; }
    const risePx = (basePx / 2) * (pitch / 12);
    const mid = { x: (L.x + R.x) / 2, y: (L.y + R.y) / 2 };
    const peakX = m.symmetric ? mid.x : P.x;
    patchMark(m.id, { points: [L, { x: peakX, y: mid.y - risePx }, R], pitch_set: pitch });
  };
  const toggleSymmetric = (m) => {
    const sym = !m.symmetric;
    const pts = m.points.map((q) => ({ ...q }));
    if (sym) pts[1] = { x: (pts[0].x + pts[2].x) / 2, y: pts[1].y };
    patchMark(m.id, { symmetric: sym, points: pts });
  };

  // SEND-141 (Howard ruled 2026-08-27) — A REFUSED ROW SHOWS NO NUMBER.
  // The figure a refused mark used to print was the FLAT POLYGON'S PIXEL
  // AREA — 0 — and next to REFUSED it read as a measured zero. It is not
  // a takeoff, so it is not promoted anywhere: the cell is an EM DASH.
  // Never 0, never 0.0, never 0 ft². The receipt stays, the reason stays,
  // and a measured gable still prints ½ × width × rise.
  // The FACE refusal blanks the cell; a CHEEK refusal never does — that
  // face was drawn and measured (SEND-140).
  const figureRefused = (id) => {
    const row = [...(qty?.gable_rows || []), ...(qty?.dormer_rows || [])]
      .find((r) => r.id === id);
    return !!(row && row.refusal);
  };
  // The same rule for any ft² figure on a panel: a number only when there
  // IS one. 0.0 ft² beside a refusal is the same measured-zero lie.
  const ft2 = (v) => (v > 0 ? `${v.toFixed(1)} ft²` : "—");

  // The ONE place that decides what a mark's quantity cell may say.
  const qtyCell = (m, a) => {
    // SEND-147 — A WALL BASE IS AN ANCHOR, NOT A QUANTITY: no ft², and above
    // all NO LF. It prints what it is.
    if (m.kind === "wall_base" || m.kind === "eave") return "anchor · no LF";
    // SEND-150/152 — a trim LINE carries feet, and only where this photo has
    // a scale: no scale → the line's own words, never a number and never a 0.
    if (LINEAR_TOOLS.includes(m.kind)) {
      if (m.status === "refused") return "—";
      // SEND-208 — A J BOX OR POLYGON HAS NO TWO ENDS, so it carries no line
      // record: its LF is the CLOSED PERIMETER, and the SERVER is the only
      // thing that measures it. Nothing is computed here — before it is
      // confirmed it says so, and after it is confirmed the server's own row
      // is printed, refusal words included. Never a 0, never a guess.
      if (m.kind === "j_channel" && (m.points || []).length >= 3) {
        if (m.status !== "confirmed") return "provisional · confirm for its LF";
        const row = (qty?.j_channel_rows || []).find((r) => r.id === m.id);
        if (!row) return "—";
        return row.lf != null ? `${row.lf} LF` : (row.refusal || "—");
      }
      return m.line?.length_ft != null
        ? `${m.line.length_ft} LF`
        : "needs a length on this photo";
    }
    if (m.shape === "point") return "count only — no drawn extent";
    if (a == null) return "no scale";
    if (m.status === "refused" || figureRefused(m.id) || !(a > 0)) return "—";
    return `${a} ft²`;
  };

  // SEND-140 — the receipt comes from the SERVER's own refusal, keyed by
  // mark id. Nothing about the reason is re-decided here.
  const receiptFor = (id) => ([...(qty?.gable_receipts || []),
    ...(qty?.dormer_receipts || [])].find((r) => r.id === id) || {}).receipt || null;

  const nMark = (m) => (m.points || []).map(toNorm);
  // SEND-204 — WHAT IS PAINTED. The layer switch is a VISIBILITY choice:
  // the rail still lists every mark, every quantity still counts, and
  // nothing is deleted — a hidden layer is simply not drawn on the photo.
  // SEND-220 (Howard ruled 2026-08-14) — WALK FILTER: THIS STEP'S MARKS ONLY.
  // "On the last photos the canvas fills up. On Outside Corners he wants to
  // see outside corners only." A — HIDE THE OTHERS COMPLETELY: no fade, no
  // ghost outline, no show-all. Everything that paints a mark is drawn from
  // `shown`, so one filter takes the shape, the label, the vertices, the
  // confirm/delete chip and the type chips with it.
  // THE RULE IS WRITTEN OFF THE STEP'S OWN KIND (`step.layer`), so every step
  // filters to its own marks and nothing is listed twice: a step with NO kind
  // — PHOTO-PICK and SCALE — paints NO marks at all, and the scale tape is
  // not a mark, so the tape stays.
  // IT IS A FILTER ON THE PAINT AND NOTHING ELSE: hidden marks stay stored,
  // the server's confirmed figures still count them, nothing is deleted and
  // nothing is un-confirmed. FINISHED, EXIT and the free editor paint every
  // mark again — where the LAYER SWITCH is his to work. No new store.
  // SEND-298c — a step may carry a SECOND tool and lane (the gutter step
  // carries the downspout), so the step shows BOTH lanes and nothing he drew
  // on this step is hidden from it. Every other step still shows exactly one.
  const stepLanes = walking
    ? [WALK_STEPS[stepIdx].layer, WALK_STEPS[stepIdx].layer2].filter(Boolean)
    : [];
  const shown = walking
    // SEND-272 — the step and the eye both read the LANE (`layerKeyOf`), so
    // siding J and soffit J are two rows and two steps off the same kind.
    ? marks.filter((m) => stepLanes.includes(layerKeyOf(m)))
    : marks.filter((m) => !hidden.includes(layerKeyOf(m)));
  // SEND-238 (Howard ruled 2026-08-14) — CONFIRM ALL, THIS STEP. "It confirms
  // every COMPLETE mark of that step's kind on THIS photo. It does not confirm
  // other steps. It does not confirm other photos. It does not write money."
  // The list it works from is THIS STEP'S KIND on the photo already loaded —
  // nothing else is reachable from here — and INCOMPLETE MARKS ARE LEFT
  // ALONE: a line with one tap and a box with one corner are not marks yet.
  // ON OPENINGS IT INVENTS NO TYPE: it sends `status` and nothing else, so a
  // box with no type confirms as an opening and STAYS an opening.
  const MARK_POINTS_NEEDED = {
    opening: 4, gable: 3, dormer: 4, wall_base: 2, eave: 2, starter: 2,
    fascia: 2, soffit: 2, outside_corner: 2, inside_corner: 2, coil: 2,
    finish_trim: 2,
    // SEND-298a — the gutter run is a two-tap line like fascia, so CONFIRM
    // ALL sweeps a complete gutter the same way. EACH confirmed line is one
    // run, which is exactly why the sweep must reach them.
    gutter: 2,
    // SEND-298c — the DOWNSPOUT line is two taps as well, so CONFIRM ALL ON
    // THIS STEP sweeps a complete downspout the same way. IT MUST: every
    // CONFIRMED line is one stick, so an unswept line is a stick he loses.
    downspout: 2,
    // SEND-287 (Howard ruled 2026-06) — **CONFIRM ALL ON BAND BOARD WAS
    // BROKEN, AND THIS LINE IS THE WHOLE MISS.** `band_board` was never
    // listed here, so a band fell through to the POLYGON rule below (`n >= 3`)
    // and a two-tap band — his complete 10" band at 26.08 LF — read as
    // INCOMPLETE. `stepPending` came back empty, the button sat disabled and
    // the helper said "nothing complete and unconfirmed of this kind".
    // A TWO-TAP LINE WITH A WIDTH CHIP IS COMPLETE, exactly as fascia and
    // soffit are at 2, and Confirm All sweeps it the same way.
    band_board: 2,
  };
  const markComplete = (m) => {
    const n = (m.points || []).length;
    if (m.kind === "j_channel") {
      // SEND-241 — AN UNTYPED J IS NOT A MARK YET, so CONFIRM ALL leaves it
      // alone. The SIZE does not gate this: a named J with no size is a mark
      // that measures LF and simply fills no catalog row yet.
      if (!m.j_type) return false;
      return m.shape === "line" ? n === 2 : m.shape === "box" ? n === 4 : n >= 3;
    }
    // SEND-325 (Howard ruled 2026-06) — **CONFIRM ALL ON THE LP OUTSIDE
    // CORNER STEP.** On the LP walk there is no vinyl tap: an outside corner
    // carries its 4"/6" SIZE (corner_size), not a vinyl_tier, so the vinyl tap
    // gate below read every LP corner as incomplete, stepPending came back
    // empty and the button did nothing. A two-tap LP outside corner WITH its
    // size is a finished mark, and Confirm All sweeps it the same as every
    // other step. The vinyl walk's gate below is untouched.
    if (lpWalk && m.kind === "outside_corner") {
      return !!m.corner_size && n === MARK_POINTS_NEEDED.outside_corner;
    }
    // SEND-325 FOLLOW-UP (Howard ruled 2026-06) — **THE SAME ON THE OTHER LP
    // STEPS.** An LP mark carries an LP PICK, never a vinyl series+profile tap
    // or a coil match: the LP siding box carries its PRODUCT, the LP trim its
    // 440/540 SERIES + SIZE, the LP soffit its VENT, the LP inside corner its
    // fixed 440 4" row (no extra pick). The vinyl tap gate and the band_match
    // gate below read every one of these as incomplete, so Confirm All sat
    // silent. On the LP walk each is finished when its own pick is made and
    // its shape is complete, swept the same as every other step. The vinyl
    // gates below are left byte-for-byte for the vinyl walk. (LP fascia is not
    // listed here on purpose: it is not a tap kind and not a band_board, so it
    // already falls through to the two-tap rule and Confirm All never missed
    // it.)
    if (lpWalk && m.kind === "siding_zone") return !!m.lp_product && n >= 3;
    if (lpWalk && m.kind === "band_board")
      return !!m.band_series && m.band_width_in != null && n === 2;
    if (lpWalk && m.kind === "soffit") return !!m.soffit_vent && n === 2;
    if (lpWalk && m.kind === "inside_corner") return n === 2;
    // SEND-244 — "Confirm All skips a mark that has no vinyl tap." A mark on
    // one of the tapped steps is not finished until it carries the tap it was
    // drawn under; he can still confirm it by hand on the photo, and its lane
    // will say by name that no tier was named.
    if (TAP_KINDS_FE[m.kind] && !m.vinyl_tier) return false;
    // SEND-242 — "An untyped opening is incomplete — Confirm All leaves it
    // alone." The four corners are still the box; the product is the name it
    // needs before the sweep will touch it. NOTHING IS AUTO-NAMED: he can
    // still confirm that box by hand on the photo, and it stays an opening.
    if (m.kind === "opening" && !m.opening_type) return false;
    // SEND-287 — NO CHIP STAYS A REFUSAL, NOT A CONFIRM. The width is what
    // names the yield bucket, so a band without one is not a finished mark
    // and Confirm All leaves it alone and does not count it. The screen
    // already blocks the first tap without a chip, so this only catches a
    // band that arrived some other way — nothing is auto-widened either way.
    if (m.kind === "band_board" && !m.band_width_in) return false;
    // SEND-288 — and a band that names NO COIL is not a finished mark either:
    // no match chip, no landing, so Confirm All leaves it alone and does not
    // count it. The walk arms MATCH FASCIA, so this catches a cleared chip.
    if (m.kind === "band_board" && !m.band_match) return false;
    const need = MARK_POINTS_NEEDED[m.kind];
    return need ? n === need : n >= 3;   // a zone is a polygon: three at least
  };
  const stepKind = WALK_STEPS[stepIdx]?.layer || null;  // SEND-252 — THE SELECTED BOXED OPENING, and only a BOXED one: a box with
  // one corner is not a shape yet and has no width to type. Nothing is
  // guessed here and no mark is created — this only names which mark the two
  // typed boxes write to.
  const selOpening = marks.find(
    (m) => m.id === selectedId && m.kind === "opening"
      && (m.points || []).length >= 3) || null;
  // SEND-298b FOLLOW-UP — EVERY gutter run on this photo, in the order he drew
  // them. The rail lists them all with their own boxes, so the count never
  // depends on finding a tap target first (his defect was exactly that: the
  // overlay could not be tapped, so the boxes never appeared). The run number
  // here is the same run the apply counts: one confirmed line, one run.
  const gutterRuns = marks.filter(
    (m) => m.kind === "gutter" && (m.points || []).length >= 2);
  // SEND-298c — the DOWNSPOUT LINES on this photo. The rail shows them as a
  // receipt, never a box: each CONFIRMED line is 1 STICK and the server is
  // the one that counts them.
  const downspoutLines = marks.filter(
    (m) => m.kind === "downspout" && (m.points || []).length >= 2);
  // SEND-298c — the sweep names EVERY lane it reaches. The gutter step holds
  // two (its gutter run and its downspout line), so the button says both
  // rather than naming one kind and quietly confirming another.
  const stepLaneWords = stepLanes
    .map((k) => kindLabel({ kind: k })).join(" / ");
  const stepPending = stepKind
    ? marks.filter((m) => stepLanes.includes(layerKeyOf(m))
      && m.status !== "confirmed" && markComplete(m))
    : [];
  const confirmAllOnStep = async () => {
    if (!stepPending.length) return;
    setBusy(true);
    let ok = 0;
    const refused = [];
    try {
      for (const m of stepPending) {
        try {
          await api.patch(`/estimates/${est.id}/photo-takeoff/marks/${m.id}`,
            { status: "confirmed" });
          ok += 1;
        } catch (e) {
          refused.push(e?.response?.data?.detail || "that mark was refused");
        }
      }
      await load();
      if (ok) toast.success(`Confirmed ${ok} ${kindLabel({ kind: stepKind })} mark(s) on THIS photo — no other step, no other photo, no price`);
      refused.forEach((r) => toast.error(r));
    } finally { setBusy(false); }
  };

  const TOOLS = [
    { key: "siding_zone", label: "Siding zone", color: SIDING },
    { key: "non_siding_zone", label: "Non-siding", color: CATEGORIES.find((c) => c.key === category)?.color },
    { key: "opening", label: "Opening", color: OPENING },
    { key: "gable", label: "Gable", color: GABLE },
    { key: "dormer", label: "Dormer", color: DORMER },
    // SEND-147 — the two-tap START LINE. An anchor, not a trim run: it
    // carries no LF and it is never priced.
    { key: "wall_base", label: "Wall base", color: WALL_BASE },
    // SEND-149 — the two-tap EAVE / frieze line. Sets the body TOP. Also an
    // anchor: no LF, never priced, and it is not a soffit or a fascia.
    { key: "eave", label: "Eave", color: EAVE },
    // SEND-150 — the two TRIM LINES he draws. These carry LF: measured as
    // drawn at this photo's own scale, no AI finder, no price, no waste.
    { key: "starter", label: "Starter", color: STARTER },
    { key: "fascia", label: "Fascia", color: FASCIA },
    // SEND-298a — the GUTTER run, its own lane and its own colour so it never
    // reads as the fascia it hangs under. Its colour rides the job
    // SOFFIT / FASCIA parent; it owns no tap and no colour of its own.
    { key: "gutter", label: "Gutter", color: GUTTER },
    // SEND-298c — the DOWNSPOUT line, drawn on the gutter step: two taps, top
    // to bottom. Each confirmed line is 1 STICK; its feet are its label.
    { key: "downspout", label: "Downspout", color: DOWNSPOUT },
    // SEND-152 — the four remaining trims, same two-tap LINE, no AI finder.
    { key: "soffit", label: "Soffit", color: SOFFIT },
    { key: "j_channel", label: "J-channel", color: J_CHANNEL },
    { key: "outside_corner", label: "Out corner", color: OUTSIDE_CORNER },
    { key: "inside_corner", label: "In corner", color: INSIDE_CORNER },
    // SEND-230 — COIL and FINISH TRIM, the same two-tap line. Coil takes NO
    // colour tap: its colour comes from Job Info.
    { key: "coil", label: "Coil", color: COIL },
    { key: "finish_trim", label: "Finish trim", color: FINISH_TRIM },
    // SEND-286 — the BAND BOARD line. Its width chip is required; it carries
    // no colour tap and it orders NO wood-board SKU — coil wrap only.
    { key: "band_board", label: "Trim Board", color: BAND_BOARD },
    { key: "scale", label: "Scale", color: "#10B981" },
  ];

  // SEND-175 item 2 (Howard ruled 2026-08-14) — PHASE 1 FULL SCREEN. THE
  // PHOTO GROWS TO THE MEASURE WINDOW, not browser F11: the shell drops its
  // page padding, its 95vh cap and its max-width, and the long prose lines
  // collapse to one line each so the height they were eating goes to the
  // photo. NOTHING IS UNMOUNTED — full screen is expressed ONLY as class
  // swaps, so NEXT/PREV, the wall tags, the mid-draft refusal, the picker
  // status, the way back, the 13-tool bar, the live quantities panel with
  // its plane basis and the zoom control are all still on screen. A pin
  // bans `full &&` and `!full &&` from this file for exactly that reason:
  // a conditional render is how a control becomes a casualty.
  // SEND-215 (Howard ruled 2026-08-14) — ONE SOURCE FOR A CONTROL, TWO
  // PLACES IT CAN SIT. The walk's right pane needs THE STEP'S OWN tools, and
  // the free editor needs all of them; copying the chips would be two
  // controls that drift. These render the same buttons, same testids, same
  // handlers — only the list is filtered. They decide nothing and store
  // nothing.
  // SEND-248 (Howard ruled 2026-08-14) — THE WALL CHIPS JUMP TO THAT PHOTO.
  // THEY DO NOT TAG. "FRONT LEFT BACK RIGHT take you to the photo already
  // tagged as that wall. They do not retag the photo you are on. If no photo
  // is tagged that wall, stay on this photo and say so — no left photo. Do not
  // tag this photo. Do not invent one. TAGGING STAYS IN WALK STEP 1 ONLY."
  //
  // SO ONE CHIP HAS TWO BEHAVIOURS DECIDED BY WHERE IT IS RENDERED, and the
  // component is still ONE source: the walk's step 1 (the only tagger) and the
  // free editor's elevation bar (the jump) are mutually exclusive renders, so
  // `walking` is the whole of the rule. OTHER AND CLEAR ARE LEFT EXACTLY AS
  // THEY WERE, by his word — they still tag on both, and that is reported.
  //
  // THE TARGET IS THE SERVER'S OWN CALL — each photo's `face`, the same
  // heading the editor prints. NOTHING IS INVENTED: no photo is tagged, no
  // face is guessed from a file name, and a wall with no photo REFUSES BY
  // NAME and leaves you where you are.
  const photoForWall = (w) => photos.find(
    (p) => (p.face || "").toUpperCase() === w.toUpperCase()) || null;
  const [wallMiss, setWallMiss] = useState(null);
  // SEND-289 — an Other wall is jumped BY ITS NAME, which is the heading the
  // photo list carries for it, so this function is untouched: it never knew
  // about wall ids and it still does not.
  const jumpWall = (w) => {
    if (navBlock) { toast.error(navBlock); return; }
    const hit = photoForWall(w);
    if (!hit) {
      setWallMiss(`no ${w} photo`);
      toast.error(`no ${w} photo — you are still on this photo, and nothing `
        + `was tagged. Tag a photo ${w.toUpperCase()} on walk step 1 first`);
      return;
    }
    setWallMiss(null);
    if (hit.photo_key === photoKey) {
      toast.success(`this IS the ${w} photo — you are already on it`);
      return;
    }
    if (onSwitch) onSwitch(hit.photo_key);
  };
  // SEND-289 (Howard ruled 2026-06) — **+ OTHER, AND MORE THAN ONE.** Each
  // press creates the next wall — Other 1, Other 2, Other 3 — with NO CAP and
  // no Garage or Porch chip invented for him. Renaming is his: blank and a
  // name another living wall carries are both refused BY THE SERVER, and the
  // refusal is shown in words. Deleting a wall that still carries marks is
  // refused by name until those marks move or go.
  const addWall = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/estimates/${est.id}/photo-takeoff/walls`);
      setWalls(data.others || []);
      toast.success(`${data.wall.name} added — rename it to what you call it on site, then tag its photos`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That wall was not added");
    } finally { setBusy(false); }
  };
  const renameWall = async (wall) => {
    const next = window.prompt(`Rename ${wall.name} — what do you call this wall on site?`, wall.name);
    if (next === null) return;
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/estimates/${est.id}/photo-takeoff/walls/${wall.wall_id}`,
        { name: next });
      setWalls(data.others || []);
      await load();
      toast.success(`This wall is ${data.name} now — the name only: no photo moved, no mark moved, no quantity changed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That name was not saved");
    } finally { setBusy(false); }
  };
  const deleteWall = async (wall) => {
    setBusy(true);
    try {
      const { data } = await api.delete(
        `/estimates/${est.id}/photo-takeoff/walls/${wall.wall_id}`);
      setWalls(data.others || []);
      await load();
      toast.success(data.note);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That wall was not deleted");
    } finally { setBusy(false); }
  };

  // SEND-290 (Howard ruled 2026-06) — **JOB COLORS ON THE WALK.** He walks
  // photos and cannot keep one siding colour straight across siding, J, trim
  // and corners. So a STICKY COLOR ROW sits under the wall chips, the same
  // row on every photo: NOT per photo and NOT per wall. Each parent writes
  // the ONE estimate field it already owned (SEND-259/260/262/264), so one
  // colour per parent for the whole job is true by construction — change
  // SIDING on photo 2 and every photo and every wall reads it, with no
  // per-wall colour split possible anywhere.
  //
  // THE LISTS ARE NOT DECLARED HERE. Every name comes out of
  // /app/frontend/src/lib/colorOptions.js — the estimate page's own dropdown
  // source — narrowed per parent, with that palette's own Standard /
  // Architectural headers. NO FREE TEXT, no second colour book, no new name.
  const JOB_COLOR_PARENTS = [
    { key: "siding", label: "Siding", field: "siding_color",
      groups: (lines) => vinylSidingColorGroupsForEstimate(lines),
      rides: "siding, siding-match J-channel, finish trim, inside corners and the accessories .019 Coil" },
    { key: "soffit_fascia", label: "Soffit / fascia", field: "soffit_fascia_color",
      groups: () => SOFFIT_COLOR_GROUPS,
      rides: "soffit, fascia, soffit J-channel, fascia-match bands and the soffit .019 Coil — one colour for the pair" },
    { key: "outside_corner", label: "Outside corners", field: "accessories_color",
      groups: (lines) => accessoryColorGroupsForEstimate(lines),
      rides: "outside corners, and nothing else" },
    // These two show ONLY when the job carries that series — a line with a
    // quantity, or a product pick on a photo. Never a guess, never a chip
    // for a series he is not selling.
    { key: "board_batten", label: "B&B", field: "board_batten_color",
      groups: () => BOARD_BATTEN_COLOR_GROUPS, series: "vertical_bb",
      rowWord: "board and batten", rides: "the board & batten rows and B&B J" },
    { key: "shake", label: "Shake", field: "shake_color",
      groups: () => SHAKE_COLOR_GROUPS, series: "shakes",
      rowWord: "pelican bay", rides: "the shake rows and shake J" },
  ];
  const parentOnJob = (p) => !p.series
    || (estLines || []).some((l) => String(l.name || "").toLowerCase()
      .includes(p.rowWord) && Number(l.qty || 0) > 0)
    || (marks || []).some((m) => m.siding_series === p.series);
  const JobColorRow = () => (
    <div className="px-3 py-1.5 border-b border-[var(--border)] flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] sticky top-0 bg-[var(--surface)] z-10"
      data-testid="photo-takeoff-color-row">
      <span className="font-bold uppercase tracking-wider text-[var(--ink-2)]"
        data-testid="photo-takeoff-color-row-label">Color</span>
      {JOB_COLOR_PARENTS.filter(parentOnJob).map((p) => (
        <label key={p.key} className="flex items-center gap-1"
          title={`One colour for the whole job — it sets ${p.rides}. Changing it here changes it on every photo and every wall.`}>
          <span className="uppercase tracking-wider text-[var(--muted)]">{p.label}</span>
          <select disabled={busy || !estColors}
            value={(estColors || {})[p.field] || ""}
            onChange={(e) => pickProductColor(p.field, e.target.value)}
            data-testid={`photo-takeoff-color-${p.key}`}
            className="border border-[var(--border)] bg-transparent px-1 py-0.5 text-[10px] disabled:opacity-40">
            <option value="">— not picked —</option>
            {p.groups(estLines).map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.colors.map((c) => <option key={c} value={c}>{c}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
      ))}
      {/* SEND-302 — THE PARAGRAPH UNDER THE COLOR PICKERS IS OFF THIS
          SCREEN. "The color pickers stay." They do, and so does everything
          they write; only the prose under them is gone, because it was in the
          way of the sheet. */}
    </div>
  );

  // SEND-304 — **HIDE TAGS: ONE COMPONENT, BOTH RAILS.** It sits at the top
  // of the walk's step rail and at the top of the grouped rail, so it is
  // there while he draws and still there after he exits — ONE control, not a
  // second screen. It says which way it is pointing, because a toggle whose
  // state he has to guess is worse than no toggle.
  // SEND-318 — A MEASURE TOOL ON EVERY STEP. The chip sits on the rail of every
  // walk step (and the grouped rail). Two taps on the photo draw a line and
  // read the feet at this photo's own scale; it saves no mark, fills no row and
  // changes no quantity. No scale → it says so and invents no distance. A third
  // tap, or leaving the step, clears the line. A blueprint page is a photo
  // here, so it works the same on a page.
  const MeasureChip = () => (
    <div className="w-full" data-testid="photo-takeoff-measure-row">
      <button type="button" disabled={busy}
        onClick={() => { setMeasuring((v) => !v); setMeasurePts([]); }}
        title={measuring
          ? "Measuring: two taps on the photo draw a line and read the distance in feet at this photo's scale. It saves nothing and changes no quantity. A third tap, or leaving the step, clears the line"
          : "Measure a distance on this photo: two taps draw a line and read the feet at this photo's scale. It saves no mark and fills no row"}
        className={`w-full px-2 py-1 border text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40 ${measuring
          ? "bg-[var(--ink)] text-white border-[var(--ink)]"
          : "border-[var(--border)] hover:bg-[var(--surface-muted)]"}`}
        data-testid="photo-takeoff-measure-chip">
        {measuring ? "Measuring…" : "Measure"}
      </button>
      {measuring && measurePts.length === 2 ? (
        <div className="text-[10px] mt-1 font-bold text-[var(--ink-2)]"
          data-testid="photo-takeoff-measure-readout">
          {sc.ipp
            ? `${measureFeet.toFixed(1)} ft`
            : "No scale on this photo — set the tape; no distance is invented"}
        </div>
      ) : null}
    </div>
  );

  const TagsToggle = () => (
    <div className="w-full" data-testid="photo-takeoff-tags-toggle-row">
      <button type="button" onClick={toggleTags} disabled={busy}
        title={tagsHidden
          ? "Show the name and the figure on every mark on THIS photo again. Nothing you measured changed while they were off"
          : "Hide the name and the figure on every mark on THIS photo so they stop blocking your next tap. The coloured line, the box and the polygon stay, a tap on the line still picks the mark, and nothing you measured changes. This photo only"}
        className={`w-full px-2 py-1 border text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40 ${tagsHidden
          ? "bg-[var(--ink)] text-white border-[var(--ink)]"
          : "border-[var(--border)] hover:bg-[var(--surface-muted)]"}`}
        data-testid="photo-takeoff-hide-tags">
        {tagsHidden ? "Show tags" : "Hide tags"}
      </button>
    </div>
  );

  const ElevationChips = () => (
    <>
      {/* SEND-249 — OTHER COMES OFF THE JUMP BAR: it still TAGGED on a bar
          that now jumps, and "tagging stays in walk step 1 only". It is
          hidden, not deleted — walk step 1 still renders all five chips, and
          the one flag brings it back on the bar. */}
      {["front", "left", "back", "right", "other"].filter(
        (w) => w !== "other" || walking || TAKEOFF_AI_CHROME_SHOWN).map((w) => (
        <button key={w} type="button"
          onClick={() => ((walking || w === "other")
            ? tagElevation(w) : jumpWall(w))}
          data-mode={(walking || w === "other") ? "tag" : "jump"}
          title={(walking || w === "other")
            ? `Tag this photo ${w.toUpperCase()} — your call beats the read's, and nothing is inferred from a file name`
            : `Go to the photo already tagged ${w.toUpperCase()} — this chip does not retag the photo you are on, and if no photo carries that wall it says so and you stay here`}
          data-testid={`photo-takeoff-elevation-${w}`}
          className={`px-2 py-0.5 border uppercase tracking-wider font-bold transition-colors ${
            elev?.heading === w.toUpperCase() && elev?.by_hand
              ? "bg-[var(--ai)] text-white border-[var(--ai)]"
              : "border-[var(--border)] hover:bg-[var(--surface-muted)]"}`}>
          {w}
        </button>
      ))}
      {/* SEND-289 — EVERY OTHER WALL HE ADDED, BY ITS OWN NAME, beside the
          four. In the walk it TAGS this photo at that wall (which takes the
          photo's marks with it, because a mark rides its photo); on the free
          bar it JUMPS to that wall's photo like the cardinals do. Rename and
          delete sit on the wall you are on — nothing is named for him and a
          wall with marks will not delete. */}
      {walls.map((w) => (
        <button key={w.wall_id} type="button" disabled={busy}
          onClick={() => (walking
            ? tagElevation(w.wall_id) : jumpWall(w.name))}
          data-mode={walking ? "tag" : "jump"}
          title={walking
            ? `Tag this photo ${w.name.toUpperCase()} — a full wall of its own: its own marks, its own confirm, its own scale, and its quantities roll up under this name`
            : `Go to a photo already tagged ${w.name.toUpperCase()}`}
          data-testid={`photo-takeoff-elevation-${w.wall_id}`}
          className={`px-2 py-0.5 border uppercase tracking-wider font-bold transition-colors disabled:opacity-40 ${
            elev?.wall_id === w.wall_id
              ? "bg-[var(--ai)] text-white border-[var(--ai)]"
              : "border-[var(--border)] hover:bg-[var(--surface-muted)]"}`}>
          {w.name}
        </button>
      ))}
      <button type="button" disabled={busy} onClick={addWall}
        title="Add another wall — Other 1, Other 2, Other 3 … There is no cap, each one is a full wall, and you name it yourself"
        data-testid="photo-takeoff-add-other-wall"
        className="px-2 py-0.5 border border-[var(--ai)] text-[var(--ai)] uppercase tracking-wider font-bold hover:bg-[var(--surface-muted)] disabled:opacity-40">
        + other
      </button>
      {elev?.wall_id ? (
        <>
          <button type="button" disabled={busy}
            onClick={() => renameWall(
              walls.find((w) => w.wall_id === elev.wall_id) || {
                wall_id: elev.wall_id, name: elev.wall_name })}
            data-testid="photo-takeoff-rename-wall"
            className="px-2 py-0.5 border border-[var(--border)] uppercase tracking-wider hover:bg-[var(--surface-muted)] disabled:opacity-40">
            rename
          </button>
          <button type="button" disabled={busy}
            onClick={() => deleteWall(
              walls.find((w) => w.wall_id === elev.wall_id) || {
                wall_id: elev.wall_id, name: elev.wall_name })}
            data-testid="photo-takeoff-delete-wall"
            className="px-2 py-0.5 border border-[var(--border)] uppercase tracking-wider hover:bg-[var(--surface-muted)] disabled:opacity-40">
            delete wall
          </button>
        </>
      ) : null}
      {/* SEND-249 — AND SO DOES CLEAR: it removes THIS photo's hand tag, which
          is a tagging action, and the bar no longer tags. Walk step 1 keeps
          it, untouched, and `tagElevation(null)` is not rewired. */}
      {(walking || TAKEOFF_AI_CHROME_SHOWN) && elev?.by_hand ? (
        <button type="button" onClick={() => tagElevation(null)}
          data-testid="photo-takeoff-elevation-clear"
          className="px-2 py-0.5 border border-[var(--border)] uppercase tracking-wider hover:bg-[var(--surface-muted)]">
          clear
        </button>
      ) : null}
    </>
  );
  // SEND-256 (Howard ruled 2026-08-14) — FREE-BAR WALK-STEP CHIPS OPEN THAT
  // STEP. "On WALK OFF — FREE BAR, clicking a chip that is also a walk step
  // starts the walk on that step, same as JUMP TO. Chips that are only tools
  // stay tools." So the chip asks the WALK for its own step — one table, the
  // walk's own `tool` on each step, nothing hardcoded twice — and it opens it
  // through the SAME `walkJump` the jump links use: no restart, no mark
  // wiped, the SEND-244 tap gate untouched. INSIDE the walk the very same
  // component still ARMS the tool, because that is the row's job there.
  // WALL BASE, EAVE and COIL are only tools and stay tools.
  const stepForTool = (t) => WALK_STEPS.find((s) => s.tool === t) || null;
  // SEND-302 (Howard ruled 2026-08-14) — **SHRINK THE CHIPS. THE SHEET GETS
  // THE ROOM.** "On the photo walk, blueprint page or photo, the tool chips
  // are too big and the notes are in the way of the sheet. Shrink the chips.
  // Same tools, same order, smaller."
  // SO THIS IS SIZE AND NOTHING ELSE: every chip keeps its key, its testid,
  // its handler, its order and its job. px-2 py-1 / 10px became
  // px-1.5 py-0.5 / 9px, and the rows' gap came in with them.
  const ToolChips = ({ only, sectioned }) => {
    // SEND-259 — THE SIDING RAIL IS THE WALK'S SIDING STEP, and only that.
    // The free editor's flat bar (`sectioned` unset) is untouched by this
    // send: same chips, same testids, same handlers.
    const sidingRail = !!sectioned && tool === "siding_zone";
    // SEND-261 — THE OPENINGS RAIL, and only that rail: the style row he asked
    // for is on the walk's openings step beside its product chips. The free
    // editor's flat bar is deliberately unchanged (he ruled "style row only"),
    // and a single opening's STYLE there is still SEND-254's own control.
    const openingStyleRail = !!sectioned && tool === "opening"
      && armed.opening === "window";
    // SEND-257 (Howard ruled 2026-08-14) — HIDE GABLE AND DORMER ON THIS
    // SCREEN. "Hide the GABLE and DORMER chips on the photo takeoff bar and
    // any jump list. DO NOT DELETE THE MARK TYPES. Existing gable or dormer
    // marks stay on the photo and still count."
    // So exactly two chips come off the bar, behind one flag of the same hide
    // family. NOTHING ELSE MOVED: `gable` and `dormer` are still mark kinds
    // end to end — the marks already drawn still paint, still carry their
    // pitch and depth, still confirm and still count in the quantities — and
    // the jump list never named them (SEND-245). One flag brings the chips
    // back.
    const shownTools = (only ? TOOLS.filter((t) => only.includes(t.key)) : TOOLS)
      .filter((t) => GABLE_DORMER_CHIPS_SHOWN
        || !["gable", "dormer"].includes(t.key));
    // SEND-236 (Howard ruled 2026-08-14) — STANDARD / ARCHITECTURAL OFF
    // FASCIA AND STARTER. "Those two products do not have a Standard /
    // Architectural type. The chips on those walk steps do nothing useful and
    // should not be there." So the vinyl tap is hidden when the ONLY tool on
    // this row is fascia or starter. It STAYS on siding, J, soffit, finish
    // trim, and it stays on the free editor's full toolbar. This hides a
    // control that does nothing for those rows — IT CHANGES NO QUANTITY:
    // fascia is still whole LF and starter is still ceil(LF ÷ its own 10 ft
    // stick).
    // SEND-237 — `opening` joined the list: an opening is a window / garage
    // door / entry door / sliding glass door, not a vinyl tier.
    // SEND-238 — `scale` joined it: the tape is not a product.
    // SEND-239 (Howard ruled 2026-08-14) — TAP OFF NON-SIDING. "Non-siding
    // is a mask — stone, brick, not sided. It is not a vinyl tier." So
    // `non_siding_zone` joined the list and the walk's Step 5 shows the
    // NON-SIDING tool with its own mask chips — BRICK · STONE · GARAGE DOOR ·
    // STUCCO · OTHER — and no tier tap. A mask's ft² is measured at this
    // photo's scale either way: NOTHING MOVED.
    const tierUseful = shownTools.some((t) => !TIER_FREE_TOOLS.includes(t.key));
    // SEND-240 (Howard ruled 2026-08-14) — WALK CHIP LAYOUT. EVERY STEP.
    // "The right rail is no longer one dump of chips. Every walk step uses
    // labeled sections, then CONFIRM ALL ON THIS STEP at the bottom of the
    // rail." SIDING IS THE MODEL: Type (the tool chip plus the tap) · Product
    // (his six) · Exposure (4" / 5") · Profile (clap / Dutch lap) · Kind (that
    // step's own chips) — and the Confirm button lives in the rail below,
    // rendered by the walk, not by this component. `sectioned` is the WALK'S
    // VIEW ONLY: the free editor's bar passes nothing and stays one flat row
    // of the very same buttons, same testids, same handlers. NO NEW TOOL WAS
    // INVENTED AND NO CHIP WRITES ANYTHING DIFFERENT — this is layout.
    // WHAT IS HIDDEN, AND ONLY IN THE WALK: "VERTICAL B&B or SHAKES selected
    // → hide Exposure and Profile. A lap line selected → Exposure and Profile
    // show." Those two products ARE their own profile (he ruled the product
    // chip arms it: shakes has exactly one, and B&B is the product itself), so
    // the rows that would be left empty are not drawn. NO LAP ROW IS EVER
    // AUTO-PICKED, and the labels VERTICAL B&B and SHAKES stay his words.
    const ser = seriesOf(armed.siding_series);
    const lapLine = !!ser && !OWN_PROFILE[ser.key];
    const showExposure = tool === "siding_zone" && lapLine
      && (ser?.widths || []).length > 0;
    const showProfile = tool === "siding_zone" && (!sectioned || lapLine);
    const Sec = ({ label, when = true, children }) => {
      if (!sectioned) return <>{children}</>;
      if (!when) return null;
      // SEND-259 (Howard ruled 2026-08-14) — NO SIDING ZONE CHIP ON THAT ROW.
      // On the walk's siding rail the step already IS the siding zone, so the
      // chip that arms it says nothing and the TYPE row that carries it is not
      // drawn at all — which is what takes the chip off this row. `shownTools`
      // is deliberately LEFT WHOLE: it is what tells `tierUseful` this step
      // still has the vinyl tap, and emptying it silently killed the STANDARD
      // | ARCH chips the first time this was built (browser run,
      // test_reports/iteration_76.json).
      if (sidingRail && label === "Type") return null;
      // SEND-271 (Howard ruled 2026-06) — HIS SOFFIT ORDER: PRODUCT → KIND →
      // STANDARD | ARCHITECTURAL → COLOR. "Not TYPE-first." So TYPE goes the
      // same way it went on the siding rail: the section is dropped and the
      // tap moves down to its own row, below KIND and immediately above the
      // list it filters. `tierUseful` is read off `shownTools`, which is
      // deliberately left whole (iteration_76's lesson).
      if (soffitRail && label === "Type") return null;
      // SEND-272 — and off BOTH J steps, for his order there:
      // PRODUCT → SIZE → STANDARD | ARCH → COLOR, with SHAPE under them as
      // the draw tool it is.
      if (jWalkRail && label === "Type") return null;
      // SEND-322 — and off the LP outside corner step: only the LP size chips
      // (4" / 6") and the LP color list show there, not the vinyl OUT CORNER /
      // STANDARD / ARCHITECTURAL chips.
      if (lpOscRail && label === "Type") return null;
      if (lpIcRail && label === "Type") return null;
      return (
        <div className="w-full"
          data-testid={`photo-takeoff-walk-section-${label.toLowerCase()}`}>
          <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-0.5">{label}</div>
          <div className="flex flex-wrap gap-1 items-center">{children}</div>
        </div>
      );
    };
    // SEND-260 — THE PRODUCT'S OWN FIELD. The armed product names which
    // estimate field this row writes — SIDING COLOR, BOARD & BATTEN COLOR or
    // SHAKE COLOR — and the row shows THAT field's current value. Until a
    // product is armed there is no field, so there is nothing to show and
    // nothing to write.
    // SEND-292 FOLLOW-UP (Howard's screenshot, photo 2) — **THE SIDING ZONE IS
    // THE SIDING PARENT STEP.** With NO product armed there is no series to
    // name a field, and reading `""` showed no colour at all — the very thing
    // SEND-292 forbade. So the field falls back to SIDING COLOR for the
    // DISPLAY. Nothing can write the wrong field behind that: with no product
    // the list is gated, so there is no pick to make.
    const colorField = COLOR_FIELD_FOR_SERIES[armed.siding_series]
      || "siding_color";
    const colorFieldLabel = COLOR_LABEL_FOR_FIELD[colorField] || "Color";
    const colorFieldValue = estColors === null
      ? null
      : (estColors[colorField] || "");
    // SEND-262 (Howard ruled 2026-08-14) — ACCESSORIES COLOR ON J, CORNERS AND
    // FINISH TRIM. Four rails, ONE FIELD: whichever of them he is on writes
    // ACCESSORIES COLOR, which is why the color he picked on the J shows
    // SELECTED on both corners and on the finish trim.
    const accRail = !!sectioned && ACCESSORY_TOOLS.includes(tool);
    // SEND-322 (Howard ruled 2026-06) — THE OUTSIDE CORNER STEP IS LP ON AN LP
    // ESTIMATE. On an lp_smart job the outside corner step shows ONLY the LP
    // corner chips (4" / 6", its own "LP size" section) and the LP ExpertFinish
    // color list. The vinyl TYPE section — the OUT CORNER tool chip and the
    // STANDARD | ARCHITECTURAL tier tap — is dropped here the same way the
    // siding / soffit / J rails drop theirs (by the Sec label guard below),
    // leaving `shownTools` whole (iteration_76's lesson). `lpWalk` is false on a
    // vinyl job, so a vinyl estimate still shows the vinyl chips.
    const lpOscRail = !!sectioned && lpWalk && tool === "outside_corner";
    // SEND-323 — the LP inside corner walk step: its vinyl TYPE section (the
    // In corner tool chip and the STANDARD | ARCHITECTURAL tier) is dropped the
    // same way, leaving the LP 440 4" trim landing and the LP color list.
    const lpIcRail = !!sectioned && lpWalk && tool === "inside_corner";
    const accValue = estColors === null
      ? null
      : (estColors[ACCESSORY_FIELD] || "");
    // SEND-291 (Howard ruled 2026-06) — **EVERY RIDER READS ITS PARENT.**
    // "SIDING color sets siding, siding-match J, finish trim and inside
    // corners. SOFFIT / FASCIA sets soffit, fascia and MATCH FASCIA bands.
    // OUTSIDE CORNERS sets only outside corners. One color per parent for the
    // whole job." So a step that rides a parent shows THAT FIELD'S CURRENT
    // VALUE — it never starts empty when the job already has one — and its
    // pick writes the parent, which is why every other rider and every photo
    // shows it at once. NO PER-WALL COLOR IS ADDED and none is possible: the
    // value lives on the estimate, not on a wall, a photo or a mark.
    const jParentField = J_PARENT_FIELD[armed.j_channel] || "";
    const jParentValue = estColors === null
      ? null
      : (estColors[jParentField] || "");
    const soffitParentValue = estColors === null
      ? null
      : (estColors[SOFFIT_J_FIELD] || "");
    // The four steps that carried NO colour control at all until now. The
    // band board rides the coil its own MATCH chip names: MATCH FASCIA reads
    // SOFFIT / FASCIA, MATCH SIDING reads SIDING. No match, no parent.
    const riderField = !sectioned ? "" : (
      tool === "gutter" ? "gutter_color"
      : ["fascia", "downspout"].includes(tool) ? SOFFIT_J_FIELD
        : tool === "band_board"
          ? (armed.band_match === "siding" ? "siding_color"
            : armed.band_match === "fascia" ? SOFFIT_J_FIELD : "")
          : ["finish_trim", "inside_corner"].includes(tool) ? "siding_color"
            : "");
    const riderValue = estColors === null
      ? null
      : (estColors[riderField] || "");
    const riderGroups = riderField === "gutter_color"
      ? GUTTER_WALK_COLOR_GROUPS
      : riderField === SOFFIT_J_FIELD
        ? SOFFIT_COLOR_GROUPS
        : vinylSidingColorGroupsForEstimate(estLines);
    // SEND-264 — the J rail's ONE colour row: SOFFIT J, writing the estimate's
    // SOFFIT & FASCIA COLOR. Siding J, B&B J and shake J carry no colour row —
    // they take the colour of the product they follow, picked once on that
    // product's own rail.
    // SEND-270 — THE WALK'S SOFFIT STEP, and only that step. Its color row
    // reads the SOFFIT palette narrowed by the PRODUCT picked right above it,
    // and the pick rides the mark instead of writing a field on the estimate.
    // The free editor's flat bar is untouched (`sectioned` unset).
    const soffitRail = !!sectioned && tool === "soffit";
    // SEND-272 — the WALK's two J steps. `jLockedProduct` is the step's own
    // word (soffit J on step 10); `jStepProducts` is the chip list that step
    // offers. The free editor's flat bar keeps all four chips and its old
    // order — `sectioned` is unset there.
    const jWalkRail = !!sectioned && tool === "j_channel";
    // SEND-311 — THE LP COLOR ENTRY for the row the current step lands on,
    // keyed by the LP pick it carries, straight off the server map. `lpWalk`
    // is false on a vinyl job so every `lp*` prop below is undefined and the
    // vinyl pickers render exactly as before.
    const lpTrimSize = armed.band_width != null ? `${armed.band_width}` : "";
    const mkLp = (entry, hasProduct, extra = {}) => (lpWalk ? {
      hasProduct: !!hasProduct,
      colors: entry?.colors || [],
      row: entry?.row || null,
      refusal: entry?.refusal || null,
      value: armed.lp_color || "",
      onPick: pickLpColor,
      ...extra,
    } : undefined);
    const lpSiding = mkLp(lpColors?.siding?.[armed.lp_product],
      armed.lp_product);
    const lpTrim = mkLp(
      lpColors?.trim?.[`${armed.lp_trim_series}|${lpTrimSize}`],
      armed.lp_trim_series && armed.band_width);
    const lpOsc = mkLp(lpColors?.osc?.[armed.lp_osc_size], armed.lp_osc_size);
    const lpSoffit = mkLp(lpColors?.soffit?.[armed.soffit_vent],
      armed.soffit_vent);
    // SEND-323 — the Fascia step's LP picker shows the FULL ExpertFinish palette
    // (no LP fascia row — hasProduct true so it is never dead), and the Inside
    // Corner step reads the 440 4" trim's ExpertFinish colors. Both ride the
    // mark like every other LP color. `lpWalk` false → both undefined on vinyl.
    const lpFascia = mkLp(lpColors?.fascia, true,
      { what: "Fascia color", testid: "photo-takeoff-lp-fascia-color" });
    const lpInsideCorner = mkLp(lpColors?.inside_corner, true,
      { what: "Inside corner color",
        testid: "photo-takeoff-lp-inside-corner-color" });
    const jStep = WALK_STEPS[stepIdx] || {};
    const jLockedProduct = jWalkRail ? (jStep.jLock || null) : null;
    const jStepProducts = jWalkRail
      ? (jStep.jProducts || (jStep.jLock ? [jStep.jLock] : SIDING_J_PRODUCTS))
      : J_TYPES.map((t) => t.key);
    const jProductLabel = (J_TYPES.find(
      (t) => t.key === armed.j_channel) || {}).label;
    // SEND-272 — THE TAP ON A J STEP BELONGS TO THE PRODUCT THE J TRIMS
    // (SEND-244's per-product tap, SEND-264's product → colour map): siding,
    // B&B and shake J follow the SIDING product — its series AND its profile —
    // and soffit J follows the SOFFIT series. Those are picked on their own
    // steps, so if he lands here without them the chips are DEAD and the row
    // SAYS WHICH PICK IS MISSING instead of letting him click into a toast.
    const jFam = jWalkRail ? famOf(tool) : null;
    const jTapLive = !!(armed.j_channel && jFam && tapProduct(jFam, armed));
    const jTapWhy = !armed.j_channel
      ? ("Pick the J product first — the tap names which of that product's "
         + "catalog rows this photo's confirmed marks land on.")
      : (TAP_PRODUCT_FIRST[jFam] || "");
    // SEND-271 — the tap is LIVE only where the catalog gives this product two
    // rows to choose between; otherwise the chips are dead and say why.
    const soffitSeriesLabel = (SOFFIT_SERIES.find(
      (s) => s.key === armed.soffit_series) || {}).label;
    const soffitTapLive = !!armed.soffit_series
      && soffitSeriesTiered(armed.soffit_series);
    const soffitTapWhy = !armed.soffit_series
      ? ("Pick the soffit product first — the tap names which of that "
         + "product's catalog rows this photo's confirmed marks land on")
      : (`${soffitSeriesLabel || "This product"} is ONE row in the Vinyl `
         + "Soffit with Siding catalog with no Standard / Architectural "
         + "pair — no tap is needed and none was invented for it");
    // SEND-259 — ONE declaration of the tap chips for both places the rail
    // can put them. Their job is unchanged (SEND-227 / SEND-244): the tap
    // names which of the two catalog rows this product's confirmed marks land
    // on, and it is still required before the first tap. On the siding rail it
    // ALSO filters the color list below it — and it picks no color.
    // SEND-271 — ONE DECLARATION STILL, and now it can also be handed out
    // DEAD: a disabled chip fires nothing, so the soffit step can SHOW the tap
    // and say why it cannot be used on a product the catalog gives one
    // untiered row. No second chip row was written to do it.
    const tierChips = (dead = false) => (
      <>{tierUseful && TIER_CHIPS.map((c) => (
        <button key={c.key} type="button" disabled={busy || dead}
          onClick={() => tapTier(c.key)}
          title={dead ? soffitTapWhy : `${c.label} color tier for ${tapProductLabel(famOf(tool))} on THIS photo — it names which of the two catalog rows this product's confirmed marks land on, it is required before the first tap, and it is not a price and not a paint name`}
          className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${tapOf(famOf(tool)) === c.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
          data-dead={dead ? "true" : undefined}
          data-testid={`photo-takeoff-tier-${c.key}`}>{c.label}</button>
      ))}</>
    );
    const tierRow = tierChips();
    return (
      <>
        <Sec label="Type">
        {shownTools.map((t) => (
          <button key={t.key} type="button"
            onClick={() => {
              const st = !walking && !sectioned ? stepForTool(t.key) : null;
              if (st) { walkJump(st.key); return; }
              setTool(t.key); setDraft(null); setScaleDraft(null);
            }}
            data-mode={!walking && !sectioned && stepForTool(t.key)
              ? "open-step" : "arm"}
            title={!walking && !sectioned && stepForTool(t.key)
              ? `Open the ${stepForTool(t.key).title} step — same as JUMP TO: nothing you drew is wiped and the walk does not restart`
              : `Draw with the ${t.label} tool`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${tool === t.key ? "text-white" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            style={tool === t.key ? { background: t.color, borderColor: t.color } : {}}
            data-testid={`photo-takeoff-tool-${t.key}`}>{t.label}</button>
        ))}
        {/* SEND-227 — the colour tier, after series and type. It sits in the
            ONE chip row, so the walk's siding step and the free editor's
            toolbar both carry it, and it serves every paired row on this
            photo — clap, that soffit, the 3/4" J — without asking again.
            SEND-240 — it is the second half of the TYPE section, on the steps
            where the tap still belongs: siding, J, soffit, finish trim.
            SEND-259 — on the SIDING RAIL it moves out of TYPE and sits under
            PROFILE as his STANDARD | ARCH row, immediately above the color
            list it filters. ONE declaration either way (`tierRow` below) —
            the chips are never written twice. */}
        {!sidingRail && !soffitRail && !jWalkRail && tierRow}
        </Sec>
        {/* SEND-217 — THE TYPE CHIPS ARE THIS TOOL'S OWN TOOLS. They sit in
            the right pane on the walk's siding and J steps and in the free
            editor's bar when that tool is active — ONE source, because this
            is the same component both places render. A NAME ONLY: no length,
            no price, no waste.
            SEND-240 — PRODUCT: his six siding names, and the soffit series
            that names the soffit line already on the estimate. */}
        <Sec label="Product"
          when={tool === "siding_zone" || tool === "soffit"
            || tool === "j_channel" || tool === "opening"
            || tool === "starter" || tool === "band_board"}>
        {/* SEND-312 follow-up (Howard ruled 2026-06) — THE VINYL BRAND CHIPS
            STAY OFF THE LP SIDING STEP. On an lp_smart job the LP product
            chips below are the only siding product selector; the vinyl series
            (and so, downstream, the vinyl EXPOSURE and PROFILE which only
            appear once a vinyl series is armed) never show. The vinyl walk is
            unchanged — `!lpWalk` is true there. */}
        {!lpWalk && tool === "siding_zone" && SIDING_SERIES.map((s) => (
          <button key={s.key} type="button" onClick={() => armSeries(s.key)}
            title={`Draw this zone as ${s.label} — the series and the type together name one catalog line; no colour is picked for you`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${armed.siding_series === s.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-siding-series-${s.key}`}>{s.label}</button>
        ))}
        {!lpWalk && tool === "soffit" && SOFFIT_SERIES.map((s) => (
          <button key={s.key} type="button" onClick={() => armSoffit("soffit_series", s.key)}
            title={`Draw this soffit as ${s.label} — the series names the soffit line already on the estimate`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${armed.soffit_series === s.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-soffit-series-${s.key}`}>{s.label}</button>
        ))}
        {/* SEND-241 — THE J'S PRODUCT IS ITS OWN SECTION, and it is the gate:
            no product, no first tap. The colour on the photo comes from it. */}
        {tool === "j_channel" && J_TYPES.filter(
          (t) => jStepProducts.includes(t.key)).map((t) => (
          <button key={t.key} type="button"
            disabled={busy || !!jLockedProduct}
            onClick={() => armType("j_channel", t.key)}
            title={jLockedProduct
              ? "This step is Soffit J — siding, B&B and shake J live on Step 9"
              : `Draw this J as ${t.label} — a name only, it changes no LF and no money`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-70 ${armed.j_channel === t.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-j-type-${t.key}`}>{t.label}</button>
        ))}
        {/* SEND-272 — STEP 10 SAYS WHY ITS ONE CHIP CANNOT BE CHANGED. */}
        {jLockedProduct ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-j-locked-why">
            This step is Soffit J — siding, B&amp;B and shake J live on Step 9.
          </span>
        ) : null}
        {/* SEND-282 — THE STARTER STEP'S TWO PRODUCTS, on the SAME step:
            siding starter and shake starter. A NAME only — it changes no LF
            and no money, it names WHICH ROW the run lands on. No chip is a
            SIDING starter, which is what this step has always written, so
            nothing already drawn is re-asked and no qty moves. */}
        {tool === "starter" && STARTER_PRODUCTS.map((s) => (
          <button key={s.key} type="button" disabled={busy}
            onClick={() => armType("starter", s.key)}
            title={s.key === "shake"
              ? "Draw this run as SHAKE starter — it fills the Vinyl shake-starter row and its own Ascend copy, never the Ascend Starter"
              : "Draw this run as SIDING starter — the Vinyl starter row and the Ascend Starter, exactly as this step always wrote"}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.starter === s.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-starter-type-${s.key}`}>{s.label}</button>
        ))}
        {/* SEND-299 — THE FINISH TRIM STEP'S THREE PRODUCTS, on the SAME
            step: siding, B&B and shake finish trim. A NAME only — it changes
            no LF and no money, it names WHICH ROW the run lands on and WHICH
            JOB COLOUR rides with it. No chip is a SIDING finish trim, which
            is what this step has always written, so nothing already drawn is
            re-asked and no qty moves. */}
        {tool === "finish_trim" && TRIM_PRODUCTS.map((t) => (
          <button key={t.key} type="button" disabled={busy}
            onClick={() => armType("trim", t.key)}
            title={t.key === "siding"
              ? "Draw this run as SIDING finish trim — the AMI 105200 row this step has always written, carrying the job SIDING color"
              : t.key === "bb"
                ? "Draw this run as B&B finish trim — it lands on the B&B finish-trim row in your catalog and carries the job B&B color. If that row is not in your catalog the run is REFUSED BY NAME and nothing is written onto the siding row"
                : "Draw this run as SHAKE finish trim — it lands on the shake finish-trim row in your catalog and carries the job SHAKE color. If that row is not in your catalog the run is REFUSED BY NAME and nothing is written onto the siding row"}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.trim === t.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-trim-type-${t.key}`}>{t.label}</button>
        ))}
        {tool === "finish_trim" && !armed.trim ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-trim-no-chip-why">
            No chip picked — this run is a SIDING finish trim, which is what
            this step has always written. Tap B&amp;B or SHAKE to draw theirs.
          </span>
        ) : null}
        {tool === "starter" && !armed.starter ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-starter-no-chip-why">
            No chip picked — this run is a SIDING starter, which is what this
            step has always written. Tap SHAKE STARTER to draw the shake run.
          </span>
        ) : null}
        {/* SEND-286 — THE TRIM BOARD'S SIZE CHIP, REQUIRED ON EVERY MARK.
            SEND-305 — five sizes now: 4", 6", 8" and 10" are slit from the
            24" × 50' coil and SHARE the 100 LF bucket; 12" takes its full
            width (50 LF / roll) in its own bucket, with no remainder crossing
            either way. It is not a name — it is the yield bucket — so no chip
            means NO FIRST TAP and nothing is defaulted to 8". No wood-board
            SKU is ordered either way. */}
        {tool === "band_board" && BAND_WIDTHS.map((b) => (
          <button key={b.key} type="button" disabled={busy}
            onClick={() => armType("band_width", b.key)}
            title={b.key === 12
              ? "Draw this trim board as 12\" — the full 24\" roll width, 50 LF per roll, and its own bucket: no remainder crosses into the slit bucket"
              : `Draw this trim board as ${b.label} — slit from the 24" roll, 100 LF per roll, and it shares the 4/6/8/10 bucket`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.band_width === b.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-band-width-${b.key}`}>{b.label}</button>
        ))}
        {tool === "band_board" && !armed.band_width ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-band-no-width-why">
            Pick the trim board size — it is REQUIRED on every trim board
            mark. The size names the yield bucket (4&quot;, 6&quot;, 8&quot;
            and 10&quot; are slit and share one 100 LF bucket; 12&quot; is the
            full width, 50 LF per roll), so nothing is
            defaulted to 8&quot; and no trim board draws without it. COIL WRAP
            ONLY — no wood-board SKU is ordered.
          </span>
        ) : null}
        {/* SEND-288 — WHICH COIL THIS BAND BATCHES WITH, per mark. Howard:
            "80% of the time the band batches with fascia, not siding", so
            MATCH FASCIA is armed by default. It is not a step-wide tab: one
            house can carry both, and each mark keeps the chip it was drawn
            under. */}
        {tool === "band_board" && BAND_MATCHES.map((b) => (
          <button key={b.key} type="button" disabled={busy}
            onClick={() => armType("band_match", b.key)}
            title={b.key === "fascia"
              ? "This band batches with the FASCIA — its feet land on the Soffit .019 Coil, the same line as fascia"
              : "This band batches with the SIDING — its feet land on the Siding Accessories .019 Coil, added to the openings coil on that same line"}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.band_match === b.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-band-match-${b.key}`}>{b.label}</button>
        ))}
        {tool === "band_board" ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-band-match-why">
            {armed.band_match === "siding"
              ? "MATCH SIDING — this band's feet land on the Siding Accessories .019 Coil, ADDED to the openings coil on that same line. One line only."
              : armed.band_match === "fascia"
                ? "MATCH FASCIA — this band's feet land on the Soffit .019 Coil, the SAME line as fascia. Most bands batch with the fascia, which is why this chip starts picked."
                : "No coil picked — a band with no match writes NOTHING to either coil. Tap MATCH FASCIA or MATCH SIDING."}
          </span>
        ) : null}
        {/* SEND-242 — THE OPENING'S PRODUCT, THE SAME WAY: his four, armed
            before the first tap, and the box he draws is coloured by it. A
            NAME ONLY — it builds no window line, no door line and no count,
            it deducts nothing, and it is not the vinyl tap. */}
        {tool === "opening" && OPENING_TYPE_CHIPS.map((o) => (
          <button key={o.key} type="button" onClick={() => armType("opening", o.key)}
            title={`Draw this box as a ${o.label} — a name only. It changes no size, no ft², no deduction, no count and no money`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${armed.opening === o.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-opening-type-${o.key}`}>{o.label}</button>
        ))}
        </Sec>
        {/* SEND-311 (Howard ruled 2026-06) — THE LP PRODUCT CHIPS. On an
            lp_smart job the siding zone, trim board and outside corner each
            carry their LP pick, which names the row the lane lands on AND the
            row's own ExpertFinish color list below. Soffit uses its existing
            Vented/Solid chips. Nothing is pre-picked; the vinyl walk never
            renders these (gated on `lpWalk`). */}
        {/* SEND-313 (Howard ruled 2026-06) — THE LP SIDING CHIPS GROUP BY
            PRODUCT. Four products; tap one, then the chip under it. The chip's
            key is the `lp_product` that names the row (SEND-311/312). Single-
            chip products still show their one chip. The vinyl brand chips are
            off an LP job (SEND-312 follow-up); `lpWalk` is false on the vinyl
            walk so none of this renders there. */}
        <Sec label="LP product" when={lpWalk && tool === "siding_zone"}>
        {lpWalk && tool === "siding_zone" && LP_SIDING_PRODUCTS.map((g) => (
          <button key={g.key} type="button" disabled={busy}
            onClick={() => armLpProduct(g.key)}
            title={`${g.label} — pick it, then the chip under it; the chip names the LP Smart Siding row its ft² fills and that row's own color list`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.lp_product_group === g.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-lp-product-${g.key}`}>{g.label}</button>
        ))}
        {lpWalk && tool === "siding_zone" && armed.lp_product_group ? (
          <span className="w-full flex flex-wrap gap-1 items-center"
            data-testid="photo-takeoff-lp-siding-chip-row">
            {(LP_SIDING_PRODUCTS.find((g) => g.key === armed.lp_product_group)
              ?.chips || []).map((c) => (
              <button key={c.key} type="button" disabled={busy}
                onClick={() => armLp("lp_product", c.key)}
                title={`Draw this zone as ${c.label} — it names the LP Smart Siding row its ft² fills and that row's own ExpertFinish color list; no color is picked for you`}
                className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.lp_product === c.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
                data-testid={`photo-takeoff-arm-lp-siding-${c.key}`}>{c.label}</button>
            ))}
          </span>
        ) : null}
        {/* SEND-316 — BATTENS ON A B&B ZONE. A B&B zone fills its panel row AND
            a batten line on 190 Series Trim. Vertical Panel's battens are
            automatic (16" o.c., the panel width); a 4'×8' / 4'×10' SHEET needs
            a 12"/16" on-center pick, nothing defaulted. */}
        {lpWalk && tool === "siding_zone"
          && (armed.lp_product === "panel_4x8"
              || armed.lp_product === "panel_4x10") ? (
          <span className="w-full flex flex-wrap gap-1 items-center"
            data-testid="photo-takeoff-bb-oc-row">
            <span className="text-[9px] font-bold uppercase text-[var(--ink-2)]">
              Batten o.c.
            </span>
            {[12, 16].map((oc) => (
              <button key={oc} type="button" disabled={busy}
                onClick={() => armBbOc(oc)}
                title={`Battens ${oc}" on center — each batten line is the zone height; pieces are ceil(height ÷ 16') × (ceil(width ÷ ${oc}") + 1). Nothing is defaulted.`}
                className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.bb_oc === oc ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
                data-testid={`photo-takeoff-arm-bb-oc-${oc}`}>{oc}&quot; o.c.</button>
            ))}
            {!armed.bb_oc ? (
              <span className="text-[9px] text-[var(--muted)] w-full"
                data-testid="photo-takeoff-bb-oc-why">
                Pick 12&quot; or 16&quot; on center for the battens — nothing is
                defaulted, and no batten count is guessed.
              </span>
            ) : null}
          </span>
        ) : null}
        {lpWalk && tool === "siding_zone"
          && armed.lp_product === "vertical_panel" ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-bb-vertical-note">
            Vertical Panel battens are automatic — one seam every 16&quot;, the
            panel width. They fill 190 Series Trim beside the panel row.
          </span>
        ) : null}
        </Sec>
        <Sec label="LP series" when={lpWalk && tool === "band_board"}>
        {lpWalk && tool === "band_board" && LP_TRIM_SERIES_LIST.map((s) => (
          <button key={s.key} type="button" disabled={busy}
            onClick={() => armLp("lp_trim_series", s.key)}
            title={`Draw this trim board as the ${s.label} series — it names the LP SmartSide Trim row, picked before the size; that row's own color list shows below`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.lp_trim_series === s.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-lp-trim-series-${s.key}`}>{s.label}</button>
        ))}
        {lpWalk && tool === "band_board" && !armed.lp_trim_series ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-lp-trim-series-why">
            Pick the LP trim series — 440 (4/4&quot;) or 540 (5/4&quot;) —
            then the size. Nothing is defaulted.
          </span>
        ) : null}
        </Sec>
        <Sec label="LP size" when={lpWalk && tool === "outside_corner"}>
        {lpWalk && tool === "outside_corner" && LP_OSC_SIZE_LIST.map((z) => (
          <button key={z.key} type="button" disabled={busy}
            onClick={() => armLp("lp_osc_size", z.key)}
            title={`Draw this outside corner as ${z.label} — it names the 540 Series OSC row the run lands on and that row's own color list; nothing is defaulted`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.lp_osc_size === z.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-lp-osc-${z.key}`}>{z.label}</button>
        ))}
        {lpWalk && tool === "outside_corner" && !armed.lp_osc_size ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-lp-osc-why">
            Pick the outside corner size — 4&quot; or 6&quot;. Nothing is
            defaulted for you.
          </span>
        ) : null}
        </Sec>
        {/* SEND-323 follow-up — THE LP FASCIA STEP picks the trim SERIES and
            SIZE, exactly like the Trim Board. The drawn fascia lands on that
            440/540 LP trim row; nothing is defaulted and no vinyl row is
            named. The color row below stays the full LP ExpertFinish list. */}
        <Sec label="LP series" when={lpWalk && tool === "fascia"}>
        {lpWalk && tool === "fascia" && LP_TRIM_SERIES_LIST.map((s) => (
          <button key={s.key} type="button" disabled={busy}
            onClick={() => armLp("lp_trim_series", s.key)}
            title={`Draw this fascia as the ${s.label} series — it names the LP SmartSide Trim row the run lands on, picked before the size; nothing is defaulted`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.lp_trim_series === s.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-lp-fascia-series-${s.key}`}>{s.label}</button>
        ))}
        {lpWalk && tool === "fascia" && !armed.lp_trim_series ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-lp-fascia-series-why">
            Pick the LP trim series — 440 (4/4&quot;) or 540 (5/4&quot;) — then
            the size. Nothing is defaulted.
          </span>
        ) : null}
        </Sec>
        <Sec label="LP size" when={lpWalk && tool === "fascia"}>
        {lpWalk && tool === "fascia" && BAND_WIDTHS.map((b) => (
          <button key={b.key} type="button" disabled={busy}
            onClick={() => armType("band_width", b.key)}
            title={`Draw this fascia as ${b.label} — it names the LP trim row for this series and size; nothing is defaulted`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.band_width === b.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-lp-fascia-size-${b.key}`}>{b.label}</button>
        ))}
        {lpWalk && tool === "fascia" && !armed.band_width ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-lp-fascia-size-why">
            Pick the fascia size — 4&quot;, 6&quot;, 8&quot;, 10&quot; or
            12&quot;. Nothing is defaulted.
          </span>
        ) : null}
        </Sec>
        {/* SEND-261 (Howard ruled 2026-08-14) — WINDOW STYLE ON THE OPENINGS
            RAIL, on that same rail and with NO EXTRA CLICK. It shows only when
            the product is WINDOW: GARAGE DOOR AND ENTRY DOOR GET NO STYLE ROW,
            and the product SLIDING GLASS DOOR is untouched — "Sliding glass"
            the style is under WINDOW only. The ten names are SEND-254's own
            list, imported, never re-typed. */}
        <Sec label="Style" when={openingStyleRail}>
        {openingStyleRail && OPENING_STYLES.map((s) => (
          <button key={s} type="button" disabled={busy}
            onClick={() => armOpeningStyle(s)}
            title={armed.opening_style === s
              ? "Tap again to take this style back off — the box stays WINDOW, and nothing about money moves either way"
              : `Tag windows ${s} — it retags the window box you have selected and rides the ones you draw next. A name only: no size, no COUNT, no ft² and no money move with it`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${armed.opening_style === s ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-opening-style-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s}</button>
        ))}
        {openingStyleRail ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-arm-opening-style-note">
            Optional. Skip it and the box stays WINDOW with no style. Tapping one retags the selected window box and arms the next ones — it is a name, not a size, a count or a price.
          </span>
        ) : null}
        </Sec>
        {/* SEND-262 — the accessories rails' color row. The STANDARD | ARCH
            tap is already above it in TYPE (SEND-240, unchanged) and it only
            filters this list. J SIZE, COUNT and STYLE are untouched.
            SEND-264 — this row is the OUTSIDE CORNER'S ALONE. */}
        {accRail ? (
          <div className="w-full" data-testid="photo-takeoff-accessories-color-row">
            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-0.5">{COLOR_LABEL_FOR_FIELD[ACCESSORY_FIELD]}</div>
            <div className="flex flex-wrap gap-1 items-center">
              <AccessoryColorRow tier={tapOf(famOf(tool))} value={accValue}
                lines={estLines} busy={busy}
                lp={tool === "outside_corner" ? lpOsc : undefined}
                onPick={(c) => pickProductColor(ACCESSORY_FIELD, c)} />
            </div>
          </div>
        ) : null}
        {/* SEND-264 built this rail's colour row to write the ESTIMATE'S
            SOFFIT & FASCIA COLOR. SEND-272 (Howard ruled 2026-06) rules the J
            colour RIDES THE J MARK and writes NO ESTIMATE FIELD, so the J
            steps carry ONE colour control — the one below, in his order — and
            not two for one decision. The estimate's own field is still picked
            where it always was, on the estimate page's Material Colors. */}
        <Sec label="Size" when={tool === "j_channel"}>
        {tool === "j_channel" && J_SIZES.map((s) => (
          <button key={s.key} type="button" onClick={() => armJSize(s.key)}
            title={`${s.label} J — the size and the use together name one catalog line; no colour is picked for you`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${armed.j_size === s.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-j-size-${s.key === "3/4" ? "34" : "12"}`}>{s.label}</button>
        ))}
        </Sec>
        {/* SEND-272 — THE TAP, IN HIS ORDER: after PRODUCT and SIZE, and
            immediately above the COLOR it filters. It is dead with the reason
            until the step's product is armed — on Step 10 the step arms it
            itself. */}
        {jWalkRail ? (
          <div className="w-full" data-testid="photo-takeoff-j-tier-row">
            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-0.5">Standard | Arch</div>
            <div className="flex flex-wrap gap-1 items-center">
              {jTapLive ? tierRow : (
                <>
                  {tierChips(true)}
                  <span className="text-[9px] text-[var(--muted)] w-full"
                    data-testid="photo-takeoff-j-tier-why">
                    {jTapWhy}
                  </span>
                </>
              )}
            </div>
          </div>
        ) : null}
        {/* SEND-272 — COLOR ON BOTH J STEPS, from the product's OWN catalog:
            siding J reads the siding palette of the brands on this estimate's
            lines, B&B J the board & batten palette, shake J the shake palette
            and soffit J the soffit & fascia palette. It RIDES THE MARK as a
            name — no quantity, no row, no money, no estimate field — and it
            is optional: skipping it leaves the mark with no color. */}
        {jWalkRail ? (
          <div className="w-full" data-testid="photo-takeoff-j-step-color-row">
            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-0.5">
              {J_COLOR_LABEL[armed.j_channel] || "J color"}
            </div>
            <div className="flex flex-wrap gap-1 items-center">
              <JStepColorRow product={armed.j_channel}
                productLabel={jProductLabel}
                tier={tapOf(famOf(tool))} value={jParentValue}
                lines={estLines} busy={busy}
                onPick={(c) => pickProductColor(jParentField, c)} />
            </div>
          </div>
        ) : null}
        {/* SEND-208 — HE PICKS THE J SHAPE. A drawing choice only: no
            product, no price, and nothing auto-built from an opening.
            SEND-305 — and the TRIM BOARD picks from the same three, on its own
            step, with its own state so neither tool moves the other. */}
        <Sec label="Shape" when={shapeTool}>
        {tool === "j_channel" && J_SHAPES.map((j) => (
          <button key={j.key} type="button" onClick={() => { setJShape(j.key); setDraft(null); }}
            title={j.hint}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${jShape === j.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-j-shape-${j.key}`}>{j.label}</button>
        ))}
        {tool === "band_board" && J_SHAPES.map((j) => (
          <button key={j.key} type="button" onClick={() => { setBandShape(j.key); setDraft(null); }}
            title={j.hint}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${bandShape === j.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-band-shape-${j.key}`}>{j.label}</button>
        ))}
        </Sec>
        {/* SEND-306 — THE SIDING ZONE PICKS ITS SHAPE TOO: CORNERS (today's
            gesture, and the one that starts picked) or BOX (two taps,
            opposite corners). The rectangle IS the zone — its square feet
            come off the box by the same area reader — and NOTHING IS CUT OUT
            of the photo or out of any other mark. */}
        <Sec label="Shape" when={tool === "siding_zone"}>
        {tool === "siding_zone" && ZONE_SHAPES.map((z) => (
          <button key={z.key} type="button" onClick={() => { setZoneShape(z.key); setDraft(null); }}
            title={z.hint}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${zoneShape === z.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-zone-shape-${z.key}`}>{z.label}</button>
        ))}
        {/* SEND-317 — PLACE SHEET: only for the 4'×8' / 4'×10' B&B chips. Each
            tap stamps a box the real size of that sheet at this photo's scale,
            centered on the tap; the box IS the zone and counts as ONE panel.
            Vertical Panel never gets it. No scale → the option is off. */}
        {lpWalk && tool === "siding_zone"
          && (armed.lp_product === "panel_4x8"
              || armed.lp_product === "panel_4x10") ? (
          <button type="button" disabled={!sc.ipp}
            onClick={() => { setZoneShape("sheet"); setDraft(null); }}
            title={sc.ipp
              ? "tap to stamp one sheet-size box, centered on the tap — the box is the zone and counts as one panel; drag it later to move it"
              : "set the scale first — a placed sheet needs the photo's scale to size the box"}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${zoneShape === "sheet" ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid="photo-takeoff-zone-shape-sheet">Place sheet</button>
        ) : null}
        </Sec>
        <Sec label="Exposure" when={showExposure}>
        {tool === "siding_zone" && (seriesOf(armed.siding_series)?.widths || []).map((w) => (
          <button key={w} type="button" onClick={() => armWidth(w)}
            title={`${w}" exposure — ${seriesOf(armed.siding_series)?.label} sells this in two exposures and they are different lines`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${armed.siding_width === w ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-siding-width-${w}`}>{w}"</button>
        ))}
        </Sec>
        <Sec label="Profile" when={showProfile}>
        {tool === "siding_zone" && SIDING_TYPES.filter((t) => (seriesOf(armed.siding_series)?.types || []).includes(t.key)).map((t) => (
          <button key={t.key} type="button" onClick={() => armType("siding_zone", t.key)}
            title={`Draw this zone as ${t.label} — a name only, it changes no ft² and no money`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${armed.siding_zone === t.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-siding-type-${t.key}`}>{t.label}</button>
        ))}
        </Sec>
        {/* SEND-259 (Howard ruled 2026-08-14) — HIS ORDER ON THE SIDING RAIL:
            PRODUCT · EXPOSURE · PROFILE · STANDARD | ARCH · then the color
            dropdown for that pick. SIDING ONLY — no other step gets a color
            list in this send, and the shake, board-and-batten, composite and
            accessory palettes were not built here. */}
        {sidingRail && !lpWalk ? (
          <div className="w-full" data-testid="photo-takeoff-siding-tier-row">
            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-0.5">Standard | Arch</div>
            <div className="flex flex-wrap gap-1 items-center">{tierRow}</div>
          </div>
        ) : null}
        {sidingRail ? (
          <div className="w-full" data-testid="photo-takeoff-siding-color-row">
            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-0.5">{colorFieldLabel}</div>
            <div className="flex flex-wrap gap-1 items-center">
              <ProductColorRow seriesKey={armed.siding_series}
                seriesLabel={seriesOf(armed.siding_series)?.label}
                fieldLabel={colorFieldLabel}
                tier={tapOf(famOf(tool))} value={colorFieldValue}
                busy={busy} lp={lpSiding}
                onPick={(c) => pickProductColor(colorField, c)} />
            </div>
          </div>
        ) : null}
        {/* SEND-240 — KIND: that step's OWN chips and nothing else. The
            non-siding masks and the soffit's vent. An opening's four types
            are NOT here: they ride the selected box on the photo (SEND-235)
            and the rail says OPENING. SEND-241 — THE J'S OWN PICKS MOVED to
            their three labeled sections above (Product · Size · Shape), and
            NO VENTED / SOLID WAS EVER ADDED TO J. */}
        <Sec label="Kind" when={["non_siding_zone", "soffit"].includes(tool)}>
        {tool === "non_siding_zone" && CATEGORIES.map((c) => (
          <button key={c.key} type="button" onClick={() => setCategory(c.key)}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border ${category === c.key ? "text-white" : ""}`}
            style={category === c.key ? { background: c.color, borderColor: c.color } : { color: c.color, borderColor: c.color }}
            data-testid={`photo-takeoff-category-${c.key}`}>{c.name}</button>
        ))}
        {tool === "soffit" && SOFFIT_VENTS.map((v) => (
          <button key={v.key} type="button"
            disabled={busy || (!lpWalk && soffitRail && !armed.soffit_series)}
            onClick={() => armSoffit("soffit_vent", v.key)}
            title={lpWalk
              ? `${v.label} — it names the LP soffit row (${v.key === "vented" ? "16 x 16 Vented" : "16 x 16 Closed"}) its ft² fills and that row's own ExpertFinish color list; the 24" rows stay off the walk and nothing is defaulted`
              : `${v.label} — it rides the mark as a name; the vinyl catalog has no vented/solid row yet and none was invented`}
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase border disabled:opacity-40 ${armed.soffit_vent === v.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
            data-testid={`photo-takeoff-arm-soffit-vent-${v.key}`}>{v.label}</button>
        ))}
        {/* SEND-271 — "If no product yet, later rows stay disabled and say
            why." KIND is the first of those later rows. */}
        {!lpWalk && soffitRail && !armed.soffit_series ? (
          <span className="text-[9px] text-[var(--muted)] w-full"
            data-testid="photo-takeoff-soffit-kind-why">
            Pick the soffit product first — vented or solid belongs to a
            product, and nothing is armed until you do.
          </span>
        ) : null}
        </Sec>
        {/* SEND-271 (Howard ruled 2026-06) — THE TAP, IN HIS ORDER: after
            PRODUCT and KIND, immediately above the COLOR it filters. It is
            LIVE only where the catalog has two rows to choose between:
            Charter Oak. Greenbriar's single row needs no tap and 2T has no
            rows at all, so the chips are DISABLED and the reason stands
            beside them — nothing is invented to make them clickable. */}
        {soffitRail && !lpWalk ? (
          <div className="w-full" data-testid="photo-takeoff-soffit-tier-row">
            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-0.5">Standard | Arch</div>
            <div className="flex flex-wrap gap-1 items-center">
              {soffitTapLive ? tierRow : (
                <>
                  {tierChips(true)}
                  <span className="text-[9px] text-[var(--muted)] w-full"
                    data-testid="photo-takeoff-soffit-tier-why">
                    {soffitTapWhy}
                  </span>
                </>
              )}
            </div>
          </div>
        ) : null}
        {/* SEND-270 (Howard ruled 2026-06) — COLOR ON THE SOFFIT STEP. The
            step asked TYPE / PRODUCT / KIND and never COLOR. The list is the
            SOFFIT palette the estimate page's own SOFFIT & FASCIA COLOR
            dropdown reads — never the siding or vinyl colors — narrowed to
            the PRODUCT picked above and then by the STANDARD | ARCH tap. A
            product the catalog gives no collection (2T today) shows the
            picker DISABLED with the reason. NO DEFAULT.
            SEND-271 — and it is LAST in his order: PRODUCT → KIND → tap →
            COLOR. Greenbriar now carries the five names he listed, on its one
            catalog row, with no tap needed. */}
        {soffitRail ? (
          <div className="w-full" data-testid="photo-takeoff-soffit-step-color-row">
            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-0.5">Soffit color</div>
            <div className="flex flex-wrap gap-1 items-center">
              <SoffitStepColorRow
                seriesKey={armed.soffit_series}
                seriesLabel={soffitSeriesLabel}
                tier={tapOf(famOf(tool))}
                value={soffitParentValue}
                busy={busy} lp={lpSoffit}
                onPick={(c) => pickProductColor(SOFFIT_J_FIELD, c)} />
            </div>
          </div>
        ) : null}
        {/* SEND-291 — THE RIDERS THAT HAD NO COLOUR CONTROL: fascia, the band
            board, finish trim and inside corners. Each shows the parent it
            rides, already set, and a pick writes that parent for the whole
            job. No per-wall colour, no second colour book, no default. */}
        {riderField ? (
          <div className="w-full" data-testid={`photo-takeoff-rider-color-row-${tool}`}>
            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-0.5">
              {COLOR_LABEL_FOR_FIELD[riderField]}
            </div>
            <div className="flex flex-wrap gap-1 items-center">
              <RiderColorRow field={riderField} groups={riderGroups}
                value={riderValue} busy={busy}
                lp={tool === "band_board" ? lpTrim
                  : tool === "fascia" ? lpFascia
                    : tool === "inside_corner" ? lpInsideCorner
                      : undefined}
                onPick={(c) => pickProductColor(riderField, c)}
                testid={`photo-takeoff-rider-color-${tool}`} />
            </div>
          </div>
        ) : null}
        {tool === "j_channel" && jPickMissing(armed) ? (
          <span className="text-[10px] font-bold text-[var(--warning-text)] px-1 self-center"
            data-testid="photo-takeoff-j-pick-first">
            {jPickMissing(armed)}
          </span>
        ) : null}
        {tool === "soffit" && soffitPickMissing(armed) ? (
          <span className="text-[10px] font-bold text-[var(--warning-text)] px-1 self-center"
            data-testid="photo-takeoff-soffit-pick-first">
            {soffitPickMissing(armed)}
          </span>
        ) : null}
        {!lpWalk && tool === "siding_zone" && sidingPickMissing(armed) ? (
          <span className="text-[10px] font-bold text-[var(--warning-text)] px-1 self-center"
            data-testid="photo-takeoff-siding-pick-first">
            {sidingPickMissing(armed)}
          </span>
        ) : null}
        {/* SEND-313 — THE ORANGE NOTE NAMES THE LP PRODUCTS on an LP job, not
            the vinyl series. It leads him through the two taps: product, then
            the chip under it. */}
        {lpWalk && tool === "siding_zone" && !armed.lp_product ? (
          <span className="text-[10px] font-bold text-[var(--warning-text)] px-1 self-center"
            data-testid="photo-takeoff-lp-siding-pick-first">
            {armed.lp_product_group
              ? "Pick the chip under that product — then trace the zone. Nothing is drawn until you do"
              : "Pick the LP product first — Lap, Shake, B&B or Nickel Gap — then the chip under it. Nothing is drawn until you do"}
          </span>
        ) : null}
        {TYPE_FIRST[tool] && !armed[tool] && !(lpWalk && tool === "siding_zone") ? (
          <span className="text-[10px] font-bold text-[var(--warning-text)] px-1 self-center"
            data-testid={`photo-takeoff-type-first-${tool}`}>
            {TYPE_FIRST[tool]}
          </span>
        ) : null}
        <span className="text-[9px] text-[var(--muted)] px-1 self-center">
          {shapeTool
            ? (J_SHAPES.find((j) => j.key === shapeNow) || {}).hint
            : tool === "scale" ? "tap both ends of a known span"
            : tool === "opening" ? "tap two opposite corners"
              : TAP_ORDER[tool]
                ? TAP_ORDER[tool][(draft?.points?.length || 0)] || TAP_ORDER[tool][0]
                : "tap each corner · tap the first again to close"}
          {tool === "wall_base" ? " — an ANCHOR for the AI body bottom, never an LF run"
            : tool === "eave" ? " — an ANCHOR for the AI body top, never an LF run (not a soffit, not a fascia)"
              : LINEAR_TOOLS.includes(tool) ? " — measured AS DRAWN at this photo's own scale · no AI finder, no price, no waste" : ""}
        </span>
      </>
    );
  };

  const shellCls = full
    ? "w-full h-screen max-w-none"
    : "w-full h-[95vh] max-w-7xl";
  const proseCls = full ? "min-w-0 truncate" : "leading-snug";

  return (
    <div className={`fixed inset-0 z-[70] bg-black/70 flex items-center justify-center ${full ? "p-0" : "p-2"}`}
      data-testid="photo-takeoff-modal" data-fullscreen={full ? "1" : "0"}>
      <div className={`bg-[var(--surface)] ${shellCls} flex flex-col border border-[var(--border)]`}>
        <div className="bg-[var(--ai)] text-white px-4 py-2.5 flex items-center justify-between">
          <div className="min-w-0">
            <div className="font-heading text-base">Photo Takeoff — phase 1</div>
            <div className="text-[11px] opacity-90 truncate">{photoKey} · siding · non-siding · openings · gables · dormers — confirmed marks carry quantity, never money</div>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setFull((v) => !v)}
              title={full
                ? "back to the windowed size — nothing on screen changes but the size"
                : "grow the photo to the whole measure window — every control stays"}
              className="text-white/90 hover:text-white flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider"
              data-testid="photo-takeoff-fullscreen-toggle">
              {full ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              {full ? "windowed" : "full screen"}
            </button>
            <button type="button" onClick={onClose} className="text-white/90 hover:text-white" data-testid="photo-takeoff-close"><X size={18} /></button>
          </div>
        </div>

        {/* SEND-166 item 1 (Howard ruled 2026-08-14) — NEXT / PREVIOUS INSIDE
            THE EDITOR. Eight photos should not mean eight trips back to the
            estimate page. THE ORDER IS THE PICKER'S OWN — 1 through 8 — and
            the picker's status travels with it. Nothing is dropped on the way:
            while a box is half-drawn, a scale is half-set, a vertex is under
            the finger or a save is in flight, the buttons REFUSE and say
            which. And the photo swap REMOUNTS the editor (`key={photoKey}`
            upstream), so photo B cannot inherit photo A's scale, plane, trim
            or marks — the reset is structural, not asserted. */}
        {photos.length > 1 && (
          <div className="px-3 py-1.5 bg-[var(--surface-muted)] border-b border-[var(--border)] flex flex-wrap items-center gap-2 text-[10px]"
            data-testid="photo-takeoff-nav-bar">
            <button type="button" onClick={() => goPhoto(-1)} disabled={!!navBlock || photoIdx <= 0}
              data-testid="photo-takeoff-prev-photo"
              className="px-2 py-0.5 border border-[var(--border)] font-bold uppercase tracking-wider disabled:opacity-40 hover:bg-[var(--surface)] flex items-center gap-1">
              <ChevronLeft size={11} /> prev
            </button>
            <span className="font-bold uppercase tracking-wider" data-testid="photo-takeoff-photo-position">
              photo {photoIdx + 1} of {photos.length}
            </span>
            <span className="text-[var(--ink-2)] font-bold uppercase tracking-wider" data-testid="photo-takeoff-photo-status">
              {here?.marks > 0 ? `${here.marks} mark${here.marks === 1 ? "" : "s"}` : "no marks"}
              {here?.has_scale ? " · scaled" : ""}
            </span>
            <button type="button" onClick={() => goPhoto(1)} disabled={!!navBlock || photoIdx >= photos.length - 1}
              data-testid="photo-takeoff-next-photo"
              className="px-2 py-0.5 border border-[var(--border)] font-bold uppercase tracking-wider disabled:opacity-40 hover:bg-[var(--surface)] flex items-center gap-1">
              next <ChevronRight size={11} />
            </button>
            <span className={navBlock ? "font-bold text-[var(--warning-text)]" : "text-[var(--muted)]"}
              data-testid="photo-takeoff-nav-note">
              {navBlock || "closing lands on the photo picker, not the estimate page"}
            </span>
          </div>
        )}

        {/* SEND-165 item 1 — ELEVATION BY HAND. He calls the wall when no
            read has named it, HIS CALL BEATS THE READ'S, and nothing is
            ever inferred from a file name.
            SEND-215 — while the walk runs this bar is not shown: tagging the
            wall IS step 1, so the very same chips (`ElevationChips`, ONE
            source, never a copy) are handed to the right pane on that step. */}
        {!walking && (
        <div className="px-3 py-1.5 border-b border-[var(--border)] flex flex-wrap items-center gap-1.5 text-[10px]"
          data-testid="photo-takeoff-elevation-bar">
          <span className="font-bold uppercase tracking-wider text-[var(--ink-2)]"
            data-testid="photo-takeoff-wall-bar-label">Go to wall</span>
          <ElevationChips />
          {/* SEND-248 — THE REFUSAL STAYS ON SCREEN. A toast he missed is not
              an answer: the bar prints "no left photo" until he moves. It
              names the wall, it tags nothing, and it invents no photo. */}
          {wallMiss ? (
            <span className="font-bold text-[var(--warning-text)]"
              data-testid="photo-takeoff-wall-jump-refusal">
              {wallMiss} — you are still on this photo and nothing was tagged
            </span>
          ) : null}
          {/* SEND-250 (Howard ruled 2026-08-14) — THE HELPER SENTENCE AND THE
              TAGGED BY HAND LINE COME OFF THIS BAR: "FRONT already shows which
              wall you are on." HIDE, NOT DELETE — both are behind one flag,
              their testids and their wording are untouched on disk, and the
              chips jump exactly as SEND-248 built them. */}
          {WALL_BAR_HELP_SHOWN ? (
          <span className="text-[var(--muted)]" data-testid="photo-takeoff-wall-jump-note">
            FRONT · LEFT · BACK · RIGHT take you to the photo already tagged that wall. They do not retag this photo — tagging is walk step 1.
          </span>
          ) : null}
          {WALL_BAR_HELP_SHOWN ? (
          <span className="text-[var(--muted)]" data-testid="photo-takeoff-elevation-state">
            {elev?.by_hand
              ? `TAGGED BY HAND — ${elev.heading}${elev.by ? ` (${elev.by})` : ""}`
              : elev?.heading
                ? `the read calls this ${elev.heading} — tag it yourself to overrule that`
                : "no read has named this wall — untagged, it stays under OTHER PHOTOS on the sheet"}
          </span>
          ) : null}
          {/* SEND-204 — OTHER IS A WALL THE FOUR VIEWS MISS (bump-out,
              garage, hidden face, addition), and that photo STANDS ALONE:
              its own scale, its own marks, its own material list. Nothing
              is copied from another photo — the scale on this screen has
              always been local to this photo, and it stays that way.
              SEND-249 — the sentence goes with the chip it explains: hidden
              behind the same flag, not deleted. */}
          {TAKEOFF_AI_CHROME_SHOWN ? (
          <span className="text-[var(--muted)]" data-testid="photo-takeoff-elevation-other-note">
            OTHER = a wall the four views miss. That photo stands alone: its own scale, its own marks, its own list.
          </span>
          ) : null}
        </div>
        )}

        {/* SEND-290 — THE COLOR ROW, JUST UNDER THE WALL CHIPS. Sticky, and
            the SAME row on every photo: it is the job's colour, not this
            photo's. It renders on the walk too (under step 1's wall row), so
            he can fix a colour wherever he notices it drifting. */}
        {!walking ? <JobColorRow /> : null}

        {/* SEND-302 — **THE BY WALL LINE IS OFF THIS SCREEN.** It was a
            read-only summary that wrote nothing, and it was in the way of the
            sheet. The rollup it printed is untouched: the walk still reads
            `by_wall` from the door, the quantities still roll up by the wall
            he tagged, and the estimate's own by-wall reporting is where it
            always was. NOTHING WAS DELETED BUT THE STRIP. */}
        {/* SEND-206 item 2 — A DRAWN STEP IS NOT A MISSED STEP. He may tap
            skip and draw it anyway (or have drawn it before the walk). The
            finished banner may only name a step with NO EVIDENCE on this
            photo: no mark of that kind, no scale, no wall tag.
            SEND-215 — the RUNNING row moved into the right pane; this strip
            now carries the FINISHED and WALK-OFF rows only, so it is not
            rendered while the walk is running. */}
        {!walking && (
        <WalkBar steps={WALK_STEPS}
          hasEvidence={(step) => (
            step.layer ? marks.some((m) => layerKeyOf(m) === step.layer)
              : step.key === "scale" ? !!scale
                : step.key === "photo" ? !!elev?.heading
                  : false)}
          walkDone={walkDone} skipped={skipped}
          onResume={() => { setWalkDone(false); setWalkOn(true); }}
          onRestart={walkRestart} />
        )}

        {!walking && (
          /* SEND-249 — HIDDEN, NOT DELETED: the yellow Phase 1 paragraph sits
             behind the one flag, and the walk gate above it is untouched. */
          TAKEOFF_AI_CHROME_SHOWN ? (
        <div className={`px-3 py-1.5 bg-[#FEF3C7] border-b border-[#F59E0B] text-[10px] font-bold text-[var(--warning-text)] flex items-center gap-1.5 ${proseCls}`} data-testid="photo-takeoff-known-limit">
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          {/* SEND-206 item 3 — WALL BASE and EAVE came out of this sentence.
              Their tools stay on the bar; they are NOT required work and
              listing them here read as though a takeoff owed them. */}
          Phase 1: areas, openings, gables, dormers, and the six TRIM LINES you draw: STARTER, FASCIA, SOFFIT, J-CHANNEL and both CORNERS, measured as drawn at this photo's own scale (no AI finder, no price, no waste). Nothing is auto-built from an opening: a boxed opening's perimeter is reported on its own. Openings are reported — nothing is deducted from siding.
        </div>
          ) : null
        )}

        {/* ONE EDITOR, TWO STAGES. The stage is decided by THIS photo's own
            read — a read on another photo unlocks nothing here.
            SEND-151 — in the MEASURE WITHOUT AI door there is no stage to
            argue about and no AI button: this door made no AI call.
            SEND-215 — "During the walk, the long banners on the photo are not
            shown. The only instruction is the one short line in the right
            panel." These banners and their buttons come straight back on
            FINISHED or EXIT; nothing was deleted. */}
        {walking ? null : noAi ? (
          /* SEND-302d — HIDDEN, NOT DELETED: with the badge (302b) and the
             OPENINGS pull both off the screen this strip had nothing left in
             it, so the empty grey bar goes behind its own flag. The door
             itself (`noAi`) is untouched, and so is everything inside. */
          NO_AI_STRIP_SHOWN ? (
          <div className="px-3 py-2 border-b bg-[#F4F4F5] border-[var(--border)] flex flex-col md:flex-row md:items-center gap-1.5 md:gap-3"
            data-testid="photo-takeoff-no-ai-banner">
            {/* SEND-302b — HIDDEN, NOT DELETED. */}
            {NO_AI_BADGE_SHOWN ? (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 text-white bg-[var(--ink)]">
              No AI — you draw
            </span>
            ) : null}
            {/* SEND-302 — **THE LONG NOTE UNDER NO AI IS OFF THIS SCREEN.**
                "The NO AI badge stays." It does, and so does the OPENINGS
                pull beside it; only the paragraph is gone. */}
            <span className="flex-1" />
            {/* SEND-152 — OPENINGS ARE NOT STRIPPED. This pulls the OPENING
                boxes of a read THIS PHOTO ALREADY HAS: it re-runs no measure,
                proposes nothing else, and adds only boxes that are not here
                yet — his hand starter / fascia / zone are never touched. */}
            {/* SEND-302d — HIDDEN, NOT DELETED. */}
            {PULL_AI_OPENINGS_SHOWN ? (
            <button
              type="button" onClick={pullProposals} disabled={busy || stage?.stage !== 2}
              title={stage?.stage === 2
                ? "Pull this photo's AI OPENING boxes from the read it already has — no new measure, nothing of yours is overwritten"
                : (stage?.proposals_refusal || "no finished AI read carries this photo, so there are no opening boxes to pull. Nothing is invented")}
              className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed border-[var(--success)] text-[var(--success)]"
              data-testid="photo-takeoff-openings-btn"
            >
              <Sparkles className="w-3 h-3" /> pull AI openings
            </button>
            ) : null}
          </div>
          ) : null
        ) : (
          /* SEND-249 — HIDDEN, NOT DELETED: STAGE 1 — BEFORE AI, AI PROPOSALS
             and STARTING ZONES sit behind the one flag. `pullProposals` and
             `pullZones` are untouched, and so is the stage read itself. */
          TAKEOFF_AI_CHROME_SHOWN ? (
        <div
          className={`px-3 py-2 border-b flex flex-col md:flex-row md:items-center gap-1.5 md:gap-3 ${stage?.stage === 2 ? "bg-[#ECFDF5] border-[#10B981]" : "bg-[#EFF6FF] border-[#3B82F6]"}`}
          data-testid="photo-takeoff-stage-banner"
        >
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 text-white ${stage?.stage === 2 ? "bg-[var(--success)]" : "bg-[var(--ai)]"}`}>
            {stage?.stage === 2 ? "Stage 2 — after AI" : "Stage 1 — before AI"}
          </span>
          <span className={`text-[10px] text-[var(--ink-2)] flex-1 ${proseCls}`} data-testid="photo-takeoff-stage-note">
            {stage?.stage_note || "Reading this photo's state…"}
          </span>
          <button
            type="button" onClick={pullProposals} disabled={busy || stage?.stage !== 2}
            title={stage?.proposals_refusal || "Pull this photo's AI proposals"}
            className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed border-[var(--success)] text-[var(--success)]"
            data-testid="photo-takeoff-propose-btn"
          >
            <Sparkles className="w-3 h-3" /> AI proposals
          </button>
          <button
            type="button" onClick={pullZones} disabled={busy || stage?.stage !== 2}
            title={stage?.proposals_refusal || "Pull this face's starting zones from the finished read"}
            className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed border-[var(--ai)] text-[var(--ai)]"
            data-testid="photo-takeoff-zones-btn"
          >
            <Sparkles className="w-3 h-3" /> starting zones
          </button>
        </div>
          ) : null
        )}
        {!walking && !noAi && stage?.proposals_refusal && (
          /* SEND-249 — HIDDEN, NOT DELETED: the "AI proposals are off" line. */
          TAKEOFF_AI_CHROME_SHOWN ? (
          <div className="px-3 py-1 bg-white border-b border-[var(--border)] text-[10px] text-[var(--warning-text)]" data-testid="photo-takeoff-proposals-refusal">
            AI proposals are off on this photo: {stage.proposals_refusal}
          </div>
          ) : null
        )}

        <div className="flex-1 flex min-h-0 flex-col md:flex-row">
          {/* canvas */}
          <div className="flex-1 relative min-w-0 min-h-0">
            <div ref={scrollRef} className="absolute inset-0 overflow-auto bg-[#27272A] touch-pan-x touch-pan-y">
              <div className="p-3">
                {/* SEND-303 — THE TURN'S TWO BOXES. The OUTER one is the space
                    the turned sheet occupies (width and height swapped on a
                    quarter turn) so the scroller can still reach all of it;
                    the INNER one is the sheet — the image and every mark, node
                    and label — rotated about its own centre. They ride it
                    together, and not one stored point is rewritten. */}
                <div className="relative" data-testid="photo-takeoff-sheet-box"
                  style={sheetBox
                    ? { width: `${sheetBox.outW}px`, height: `${sheetBox.outH}px`, lineHeight: 0 }
                    : { width: `${zoom * fitPct}%`, lineHeight: 0 }}>
                <div className="absolute" data-testid="photo-takeoff-sheet"
                  style={sheetBox
                    ? { width: `${sheetBox.w}px`, height: `${sheetBox.h}px`,
                      left: `${(sheetBox.outW - sheetBox.w) / 2}px`,
                      top: `${(sheetBox.outH - sheetBox.h) / 2}px`,
                      transform: `rotate(${turn}deg)`, transformOrigin: "center",
                      lineHeight: 0 }
                    : { position: "relative", width: "100%", lineHeight: 0 }}>
                  <img
                    ref={imgRef} src={photoUrl} alt={photoKey} draggable={false}
                    className="select-none" style={{ display: "block", width: "100%", height: sheetBox ? "100%" : "auto", cursor: "crosshair", touchAction: "none" }}
                    onLoad={(e) => setImgNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                    onPointerDown={onPointerDown} onPointerMove={onPointerMove}
                    data-testid="photo-takeoff-canvas-img"
                  />
                  <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: "100%", height: "100%" }} viewBox="0 0 100 100" preserveAspectRatio="none">
                    {shown.map((m) => {
                      const pts = nMark(m);
                      const col = markColor(m);
                      const sel = m.id === selectedId;
                      if (pts.length === 2) {
                        // SEND-147 — the WALL BASE start line. Two ends, one
                        // stored y, no LF anywhere near it.
                        return (
                          <line key={m.id} x1={pts[0].x * 100} y1={pts[0].y * 100}
                            x2={pts[1].x * 100} y2={pts[1].y * 100}
                            stroke={col} strokeWidth={sel ? "0.5" : "0.35"}
                            strokeDasharray={m.status === "confirmed" ? "0" : m.status === "refused" ? "0.8 0.8" : "2 1.2"} />
                        );
                      }
                      if (pts.length < 3) {
                        return pts.length === 1 ? (
                          <circle key={m.id} cx={pts[0].x * 100} cy={pts[0].y * 100} r={sel ? 1.4 : 1}
                            fill={m.status === "confirmed" ? col : "none"} stroke={col} strokeWidth="0.25" />
                        ) : null;
                      }
                      return (
                        <polygon
                          key={m.id}
                          points={pts.map((p) => `${p.x * 100},${p.y * 100}`).join(" ")}
                          fill={`${col}${m.status === "confirmed" ? "44" : m.status === "refused" ? "08" : "18"}`}
                          stroke={col} strokeWidth={sel ? "0.35" : "0.2"}
                          strokeDasharray={m.status === "confirmed" ? "0" : m.status === "refused" ? "0.8 0.8" : "2 1.2"}
                        />
                      );
                    })}
                    {draft?.points?.length > 0 && (
                      <polyline
                        points={[...draft.points, { x: draft.cx, y: draft.cy }].map((p) => `${p.x * 100},${p.y * 100}`).join(" ")}
                        fill="none" stroke={tool === "opening" ? OPENING : tool === "siding_zone" ? SIDING : CATEGORIES.find((c) => c.key === category)?.color}
                        strokeWidth="0.2" strokeDasharray="1 0.5"
                      />
                    )}
                    {scaleDraft?.p1 && (
                      <line x1={scaleDraft.p1.x * 100} y1={scaleDraft.p1.y * 100} x2={scaleDraft.p2.x * 100} y2={scaleDraft.p2.y * 100}
                        stroke="#10B981" strokeWidth="0.5" strokeDasharray="2 1" />
                    )}
                    {/* SEND-321 — the MEASURE draws like a PLAN DIMENSION: a
                        thin solid line between the two taps with short end
                        ticks, and the feet written on the line (18'-0", as an
                        upright HTML label below). No orange ovals, no dashed
                        bars. A white halo lets the hairline read without
                        covering the drawing. It renders nothing on disk and
                        clears on the third tap or when the step changes. The
                        ticks are drawn perpendicular ON SCREEN — the overlay is
                        stretched (preserveAspectRatio none), so the aspect
                        A = imgNat.w/imgNat.h corrects the tick direction. */}
                    {measuring && measurePts.length === 2 && (() => {
                      const a = measurePts[0], b = measurePts[1];
                      const A = imgNat.h > 0 ? imgNat.w / imgNat.h : 1;
                      const dx = b.x - a.x, dy = b.y - a.y;
                      const N = Math.hypot(dy, dx * A) || 1;
                      const T = 2.4;                       // short tick, viewBox units
                      const tvx = (-dy / N) * T / A, tvy = ((dx * A) / N) * T;
                      const seg = (x1, y1, x2, y2, stroke, w) => (
                        <line x1={x1} y1={y1} x2={x2} y2={y2}
                          stroke={stroke} strokeWidth={w} strokeLinecap="round" />
                      );
                      const line = (stroke, w) => seg(a.x * 100, a.y * 100, b.x * 100, b.y * 100, stroke, w);
                      const tick = (e, stroke, w) => seg(
                        e.x * 100 - tvx / 2, e.y * 100 - tvy / 2,
                        e.x * 100 + tvx / 2, e.y * 100 + tvy / 2, stroke, w);
                      return (
                        <g data-measure="dimension">
                          {line("#ffffff", 0.45)}
                          {tick(a, "#ffffff", 0.45)}
                          {tick(b, "#ffffff", 0.45)}
                          {line("#111827", 0.15)}
                          {tick(a, "#111827", 0.15)}
                          {tick(b, "#111827", 0.15)}
                        </g>
                      );
                    })()}
                    {/* SEND-295 (Howard ruled 2026-08-14) — **THE SCALE LINE
                        RENDERS ONLY WHERE THE SCALE IS WORKED.** On photo 3,
                        step 12 OPENINGS, the green tape still ran across the
                        wall while he boxed windows. It is NOT A MARK, so the
                        walk's own mark filter never touched it and it painted
                        on every step. Now it paints on the SCALE TOOL — the
                        walk's Scale step and the free editor's Scale tool —
                        and nowhere else. THE SCALE STILL GOVERNS EVERY
                        QUANTITY: only the ink is hidden. Nothing is cleared,
                        nothing is re-measured and no figure moves. */}
                    {tool === "scale" && scale?.anchor && imgNat.w > 0 && (
                      <line
                        x1={(scale.anchor.p1.x / imgNat.w) * 100} y1={(scale.anchor.p1.y / imgNat.h) * 100}
                        x2={(scale.anchor.p2.x / imgNat.w) * 100} y2={(scale.anchor.p2.y / imgNat.h) * 100}
                        stroke="#10B981" strokeWidth="0.5" />
                    )}
                    <CandidateEdges marks={shown} nMark={nMark} />
                  </svg>
                  {/* SEND-321 — the feet, written ON the line, the way a plan
                      prints 18'-0". An UPRIGHT HTML label at the line's midpoint
                      (the overlay SVG is stretched, so text there would skew).
                      It shows only with a scale — no scale, no number, and the
                      rail readout says so. It is ephemeral like the line. */}
                  {measuring && measurePts.length === 2 && measureLabel && (
                    <div
                      className="absolute px-1.5 py-[1px] text-[10px] font-bold leading-none text-[#111827] bg-white rounded-[2px] shadow-sm ring-1 ring-black/15 pointer-events-none whitespace-nowrap"
                      style={{
                        left: `${((measurePts[0].x + measurePts[1].x) / 2) * 100}%`,
                        top: `${((measurePts[0].y + measurePts[1].y) / 2) * 100}%`,
                        transform: "translate(-50%, -50%)",
                      }}
                      data-testid="photo-takeoff-measure-dim-label">
                      {measureLabel}
                    </div>
                  )}
                  {/* SEND-298b FOLLOW-UP (Howard ruled 2026-08-14) — **THE
                      GUTTER LINE ITSELF IS TAPPABLE.** His defect: "tapping
                      it does nothing." THE CAUSE: the mark overlay above is
                      `pointer-events-none`, so no stroke on it has ever been
                      clickable — only the little label chip was. A
                      TRANSPARENT HIT STROKE now lies over every gutter line
                      on the GUTTER step and selects that run, which is what
                      opens its DROPS and ELBOWS in the rail. It is live only
                      while no draw is in progress, so starting a new line
                      still goes to the photo, and it SELECTS, never
                      de-selects: his tap must open the boxes, not close them.
                      NOTHING IS MEASURED HERE — this layer has no geometry of
                      its own and writes nothing. */}
                  {walking && WALK_STEPS[stepIdx]?.key === "gutter"
                    && !(draft?.points?.length) && (
                    <svg className="absolute top-0 left-0" viewBox="0 0 100 100" preserveAspectRatio="none"
                      style={{ width: "100%", height: "100%", pointerEvents: "none" }}>
                      {shown.filter((m) => ["gutter", "downspout"].includes(m.kind)
                        && (m.points || []).length === 2).map((m) => {
                        const pts = nMark(m);
                        return (
                          <line key={`hit-${m.id}`}
                            x1={pts[0].x * 100} y1={pts[0].y * 100}
                            x2={pts[1].x * 100} y2={pts[1].y * 100}
                            stroke="transparent" strokeWidth="2.4"
                            style={{ pointerEvents: "stroke", cursor: "pointer" }}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); setSelectedId(m.id); }}
                            data-testid={`photo-takeoff-gutter-hit-${m.id}`} />
                        );
                      })}
                    </svg>
                  )}
                  {/* SEND-304 — **THE LINE IS TAPPABLE WHEN THE WORDS ARE
                      OFF.** With the chips hidden the only thing that ever
                      selected a mark would be gone, so a transparent hit
                      stroke lies over every mark: the line itself and the
                      OUTLINE of every box and polygon (never the fill, which
                      would swallow taps meant for the photo). It is live only
                      while no draw is in progress, so starting a new line
                      still goes to the photo. NOTHING IS MEASURED HERE: this
                      layer has no geometry of its own and writes nothing. */}
                  {tagsHidden && !(draft?.points?.length) && (
                    <svg className="absolute top-0 left-0" viewBox="0 0 100 100" preserveAspectRatio="none"
                      style={{ width: "100%", height: "100%", pointerEvents: "none" }}
                      data-testid="photo-takeoff-tagless-hits">
                      {shown.filter((m) => (m.points || []).length >= 2).map((m) => {
                        const pts = nMark(m);
                        const d = pts.map((p) => `${p.x * 100},${p.y * 100}`).join(" ");
                        const Shape = pts.length >= 3 ? "polygon" : "polyline";
                        return (
                          <Shape key={`taghit-${m.id}`} points={d}
                            fill="none" stroke="transparent" strokeWidth="2.4"
                            style={{ pointerEvents: "stroke", cursor: "pointer" }}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); setSelectedId(m.id); }}
                            data-testid={`photo-takeoff-tagless-hit-${m.id}`} />
                        );
                      })}
                    </svg>
                  )}
                  {/* vertex handles on the selected mark — drag tracks 1:1 at any zoom */}
                  {shown.filter((m) => m.id === selectedId).map((m) => nMark(m).map((p, i) => (
                    <div
                      key={`${m.id}-${i}`}
                      onPointerDown={(e) => { e.stopPropagation(); setDragVertex({ markId: m.id, index: i }); }}
                      className="absolute w-3.5 h-3.5 -ml-[7px] -mt-[7px] rounded-full bg-white border-2 cursor-move"
                      style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, borderColor: markColor(m), touchAction: "none" }}
                      data-testid={`photo-takeoff-vertex-${m.id}-${i}`}
                    />
                  )))}
                  {/* SEND-209 — ONE LINE PER MARK: its kind and its figure,
                      nothing else. The SELECTED mark is the loud one — full
                      layer colour, bigger, ringed, on top — and every other
                      mark goes quiet so the house can be read underneath.
                      NO NEW COLOUR IS INVENTED: every label wears its own
                      layer's colour, the one already on the shape. */}
                  {/* SEND-304 — the words come off with ONE flag. The shapes,
                      the vertices and the hit layer above are untouched. */}
                  {!tagsHidden && shown.filter((m) => (m.points || []).length === 2).map((m) => {
                    const sp = labelSpot(nMark(m));
                    const sel = m.id === selectedId;
                    return (
                      <div key={`lbl2-${m.id}`}
                        onClick={(e) => { e.stopPropagation(); setSelectedId(m.id === selectedId ? null : m.id); }}
                        onPointerDown={(e) => e.stopPropagation()}
                        title="Tap to select this mark — its confirm lands on the photo"
                        className={`absolute -translate-x-1/2 whitespace-nowrap text-white font-bold cursor-pointer ${sp.above ? "-translate-y-full" : ""} ${m.id === selectedId ? "ring-2 ring-white" : ""}`}
                        style={{ left: `${sp.cx * 100}%`, top: `${sp.y * 100}%`, background: markColor(m), fontSize: sel ? "10px" : "8px", padding: sel ? "2px 4px" : "1px 3px", lineHeight: 1.15, opacity: sel ? 1 : 0.55, zIndex: sel ? 15 : 5, marginTop: sp.above ? (sel && m.status !== "confirmed" ? "-2.1rem" : "-0.15rem") : "0.15rem" }}
                        data-testid={`photo-takeoff-line-label-${m.id}`}>
                        {kindLabel(m)} {LINEAR_TOOLS.includes(m.kind)
                          ? qtyCell(m, null)
                          : `y ${Math.round(m.wall_base?.y ?? m.eave?.y ?? 0)}px`}
                      </div>
                    );
                  })}
                  {!tagsHidden && shown.filter((m) => (m.points || []).length >= 3).map((m) => {
                    const sp = labelSpot(nMark(m));
                    const sel = m.id === selectedId;
                    const a = sqftOf(m);
                    return (
                      <div key={`lbl-${m.id}`}
                        onClick={(e) => { e.stopPropagation(); setSelectedId(m.id === selectedId ? null : m.id); }}
                        onPointerDown={(e) => e.stopPropagation()}
                        title="Tap to select this mark — its confirm lands on the photo"
                        className={`absolute -translate-x-1/2 whitespace-nowrap text-white font-bold cursor-pointer ${sp.above ? "-translate-y-full" : ""} ${m.id === selectedId ? "ring-2 ring-white" : ""}`}
                        style={{ left: `${sp.cx * 100}%`, top: `${sp.y * 100}%`, background: markColor(m), fontSize: sel ? "10px" : "8px", padding: sel ? "2px 4px" : "1px 3px", lineHeight: 1.15, opacity: sel ? 1 : 0.55, zIndex: sel ? 15 : 5, marginTop: sp.above ? (sel && m.status !== "confirmed" ? "-2.1rem" : "-0.15rem") : "0.15rem" }}
                        data-testid={`photo-takeoff-label-${m.id}`}>
                        {kindLabel(m)} {qtyCell(m, a)}
                      </div>
                    );
                  })}
                  {/* SEND-208 put the J's TYPE chip on the selected mark on
                      the photo, and SEND-223 the size beside it.
                      SEND-241 (Howard ruled 2026-08-14) TOOK BOTH OFF THE
                      PHOTO: "Do not put type chips on the photo. They stay in
                      the rail." Re-naming a J he already drew now happens on
                      that mark's own row in the rail's marks list — same
                      `patchMark`, same two fields, and the mark's colour
                      follows its product the moment it changes. NOTHING ELSE
                      MOVED: the J still measures LF as drawn and writes no
                      money, and the opening's four types stay on the selected
                      box on the photo, where SEND-235 put them. */}
                  {/* SEND-235 put the four TYPE CHIPS on the selected opening
                      on the photo.
                      SEND-242 (Howard ruled 2026-08-14) TOOK THEM OFF IT:
                      "Type chips come OFF the selected box. They live on that
                      opening's row in the rail. Changing Product there
                      recolors the mark." DELETE THIS OPENING stays on the box
                      for now, and so does CONFIRM. A TYPE IS STILL A NAME: it
                      changes no size, no ft², no LF, no deduction, no count
                      and no money, it builds no window line and no door line,
                      and it seeds nothing into J. Nothing is pre-picked and
                      nothing is read off the box's size — an opening with no
                      type STAYS AN OPENING. */}
                  {/* SEND-207 (Howard ruled 2026-08-14) — SELECTING A MARK IS
                      NOW A GESTURE ON THE PHOTO. Field check found the chip
                      only appearing after ADJUST, because ADJUST was the only
                      labelled control that set the selection: the canvas had
                      NO HIT TEST (a tap on a drawn mark feeds the armed tool's
                      draft) and every mark label was `pointer-events-none`.
                      The labels now select, so SELECT -> CHIP with no other
                      click, and ADJUST is back to being only about vertices.
                      A tap INSIDE a shape still belongs to the tool — an
                      opening is drawn inside the siding polygon, and stealing
                      that tap would break the drawing he does most. */}
                  {/* SEND-206 item 4 (Howard ruled 2026-08-14) — CONFIRM WAS
                      TOO EASY TO MISS: 16 marks sat provisional because the
                      only confirm was a 9px button in the rail. THE SELECTED
                      MARK NOW CARRIES ITS OWN CONFIRM, ON THE PHOTO, WHERE HE
                      IS LOOKING. One mark at a time, by his hand: there is NO
                      confirm-all and nothing auto-confirms. The rail's button
                      stays exactly where it was. */}
                  {/* SEND-218 (Howard ruled 2026-08-14) — DELETE THE SELECTED
                      MARK ON THE WALK SCREEN. "A bad mark has no delete on
                      this screen. The rail is hidden while the walk runs and
                      that hid delete with it. Do not bring the rail back."
                      SO THE DELETE SITS NEXT TO CONFIRM, ON THE MARK. It is
                      THE EXISTING delete — `delMark`, the same call the
                      rail's bin makes, the same store, no prompt and no new
                      state — so there is ONE delete path and NO second one.
                      There is no CLEAR ALL and no delete-all here.
                      The chip group is now gated on SELECTION ALONE, because
                      a bad mark can already be confirmed and it must still be
                      removable; CONFIRM itself is still gated on the status,
                      so a confirmed mark shows the delete and no confirm.
                      The step does not move: 216 still holds, `delMark`
                      re-reads the photo and names nothing about the walk. */}
                  {shown.filter((m) => m.id === selectedId).map((m) => {
                    const p = nMark(m)[0];
                    return (
                      /* SEND-207 — and it STAYS INSIDE THE FRAME: a mark
                         drawn near the top of the photo would have its chip
                         clipped off the canvas, which is the same defect as
                         not having one. Near the top it sits BELOW the mark
                         instead of above it. */
                      <div key={`ok-${m.id}`}
                        className={`absolute z-20 flex items-center gap-1.5 ${p.y < 0.12 ? "pt-1" : "-translate-y-full pb-1"}`}
                        style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}>
                        {m.status !== "confirmed" ? (
                          <button type="button" disabled={busy}
                            onClick={() => patchMark(m.id, { status: "confirmed" })}
                            title={`Confirm this ${kindLabel(m)} — it is provisional until you do, and a provisional mark carries no quantity to the estimate`}
                            className="flex items-center gap-1 px-2 py-0.5 bg-[var(--success)] text-white text-[9px] font-bold uppercase tracking-wide shadow border border-white disabled:opacity-50"
                            data-testid={`photo-takeoff-canvas-confirm-${m.id}`}>
                            <Check className="w-3 h-3" /> confirm this {kindLabel(m)}
                          </button>
                        ) : null}
                        {/* SEND-319 — ROTATE A PLACED SHEET 90°. One tap on the
                            box selected it; this control turns it 90° (4'×8' →
                            8'×4'), another turns it back. The box keeps its real
                            size, still counts as one panel, and its battens
                            follow the rotated box. Placed sheets only. */}
                        {m.placed ? (
                          <button type="button" disabled={busy}
                            onClick={() => rotateSheet(m)}
                            title="Rotate this sheet 90° — a 4'×8' box becomes 8'×4' (and back). The box keeps its real size, counts as one panel, and the battens follow the turn"
                            className="flex items-center gap-1 px-2 py-0.5 bg-[var(--ink)] text-white text-[9px] font-bold uppercase tracking-wide shadow border border-white disabled:opacity-50"
                            data-testid={`photo-takeoff-canvas-rotate-${m.id}`}>
                            <RotateCw className="w-3 h-3" /> rotate 90°
                          </button>
                        ) : null}
                        <button type="button" disabled={busy}
                          onClick={() => delMark(m.id)}
                          title={`Delete this ${kindLabel(m)} — it is removed from this photo and its quantity goes with it. Nothing else is touched and you stay on this step`}
                          className="flex items-center gap-1 px-2 py-0.5 bg-[var(--danger)] text-white text-[9px] font-bold uppercase tracking-wide shadow border border-white disabled:opacity-50"
                          data-testid={`photo-takeoff-canvas-delete-${m.id}`}>
                          <Trash2 className="w-3 h-3" /> delete this {kindLabel(m)}
                        </button>
                      </div>
                    );
                  })}
                </div>
                </div>
              </div>
            </div>
            <div className="absolute top-2 right-2 z-30 flex flex-col gap-1 w-fit">
              {/* SEND-303 — ONE TAP TURNS THE SHEET 90°, ANOTHER TURNS IT
                  AGAIN, FOUR TAPS ARE BACK TO UPRIGHT. It sits with zoom, on
                  the walk and on the free editor — the same screen, because he
                  ruled there is no second screen for this. */}
              <button type="button" onClick={turnSheet} disabled={busy}
                title={`Turn this sheet 90° clockwise${turn ? ` — it is at ${turn}° now` : ""}. The marks turn with it, the scale stays with the sheet, and this page opens this way up next time`}
                className="w-8 h-8 bg-white/95 border border-[#27272A] flex items-center justify-center shadow-md disabled:opacity-40"
                data-testid="photo-takeoff-turn"><RotateCw className="w-4 h-4" /></button>
              <span className="px-1 h-4 bg-white/95 border border-[#27272A] text-[9px] font-bold shadow-md text-center leading-4"
                data-testid="photo-takeoff-turn-state">{turn}°</span>
              <button type="button" onClick={() => bumpZoom(1.25)} className="w-8 h-8 bg-white/95 border border-[#27272A] flex items-center justify-center shadow-md" data-testid="photo-takeoff-zoom-in"><ZoomIn className="w-4 h-4" /></button>
              <button type="button" onClick={() => setZoom(1)} className="px-1 h-8 bg-white/95 border border-[#27272A] text-[9px] font-bold shadow-md" data-testid="photo-takeoff-zoom-reset">{Math.round(zoom * 100)}%</button>
              <button type="button" onClick={() => bumpZoom(1 / 1.25)} className="w-8 h-8 bg-white/95 border border-[#27272A] flex items-center justify-center shadow-md" data-testid="photo-takeoff-zoom-out"><ZoomOut className="w-4 h-4" /></button>
            </div>
            {/* tools
                SEND-215 — THE PHOTO IS JUST THE PHOTO WHILE THE WALK RUNS.
                The 14-button bar is not shown; the STEP'S OWN tool sits in
                the right pane, rendered by this very same component with a
                filter, so there is ONE source for a tool chip and it cannot
                drift between the two views. */}
            {/* SEND-245 — AFTER THE WALK, THIS ROW IS LINKS, NOT A PILE OF
                CHIPS: one name per walk section, and the name opens that
                step. Before the walk has been finished the free bar is
                exactly what it always was — the same `ToolChips`, one
                source, every tool on it, including gable, dormer and coil,
                which get no link by his ruling. */}
            {!walking && (
            <div className="absolute bottom-2 left-2 z-30 flex flex-wrap gap-1 bg-white/95 border border-[#27272A] p-1.5">
              {walkDone
                ? <JumpRow steps={WALK_STEPS} stepIdx={stepIdx}
                    navBlock={navBlock} onJump={walkJump} />
                : <ToolChips />}
            </div>
            )}
          </div>

          {/* SEND-215 — RIGHT: THIS STEP ONLY, while the walk runs. The
              whole grouped rail below comes back the moment he presses
              FINISHED or EXIT — nothing in it was deleted or moved. */}
          {walking ? (
            <WalkPane steps={WALK_STEPS} stepIdx={stepIdx} navBlock={navBlock}
              onBack={walkBack} onNext={walkNext} onSkip={walkSkip}
              onFinish={walkFinish} onExit={walkExit}>
              {/* SEND-240 (Howard ruled 2026-08-14) — LABELED SECTIONS, THEN
                  CONFIRM ALL AT THE BOTTOM OF THE RAIL. The chips are the
                  same component the free bar renders — `sectioned` only asks
                  it to stack its groups under their labels: Type · Product ·
                  Exposure · Profile · Kind. The wall tags are step 1's own
                  row and they keep their label too. */}
              <div className="flex flex-col gap-3">
                {/* SEND-304 — the words off while he draws, at the top of the
                    rail, the same one component the grouped rail renders. */}
                <TagsToggle />
                <MeasureChip />{/* SEND-318 — Measure on every walk step */}
                {WALK_STEPS[stepIdx].key === "photo" ? (
                  <div className="w-full" data-testid="photo-takeoff-walk-section-wall">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-1">Wall</div>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <ElevationChips />
                    </div>
                    {/* SEND-290 — and the COLOR row right under them, the
                        same one component the free bar renders. */}
                    <JobColorRow />
                  </div>
                ) : null}
                {/* SEND-298c — a step with TWO tools shows both chips, so the
                    gutter step can switch between the gutter run and the
                    downspout line WITHOUT a step of its own. Every other step
                    still shows exactly one. */}
                {WALK_STEPS[stepIdx].tool
                  ? <ToolChips only={[WALK_STEPS[stepIdx].tool,
                    WALK_STEPS[stepIdx].tool2].filter(Boolean)} sectioned /> : null}
              </div>
              {/* the scale step's own tool IS the tape entry */}
              {WALK_STEPS[stepIdx].key === "scale" ? (
                <div className="-mx-3 mt-2">
                  <ScalePanel sc={sc} scale={scale} qty={qty} setTool={setTool}
                    setScaleDraft={setScaleDraft} clearScale={clearScale}
                    tapeFt={tapeFt} setTapeFt={setTapeFt} tapeIn={tapeIn}
                    setTapeIn={setTapeIn} commitTape={commitTape} />
                </div>
              ) : null}
              {/* SEND-238 — ONE BUTTON, THIS STEP. It confirms every COMPLETE
                  mark of this step's kind on THIS photo and reaches nothing
                  else: no other step, no other photo, no money, and no
                  incomplete mark. The photo and scale steps have no kind of
                  their own, so no dead button is drawn there.
                  SEND-240 — it is LAST in the rail, under every section. */}
              {stepKind ? (
                <div className="mt-3 -mx-3 px-3 pt-3 border-t border-[var(--border)]">
                  <button type="button" disabled={busy || !stepPending.length}
                    onClick={confirmAllOnStep}
                    title={`Confirm every complete ${stepLaneWords} mark on THIS photo. Other steps, other photos and incomplete marks are not touched, and no price is written`}
                    className="w-full px-3 py-2 bg-[var(--success)] text-white text-[11px] font-bold uppercase tracking-wider border-2 border-white shadow disabled:opacity-40"
                    data-testid="photo-takeoff-walk-confirm-all">
                    Confirm all on this step{stepPending.length ? ` (${stepPending.length})` : ""}
                  </button>
                  <div className="mt-1 text-[9px] text-[var(--muted)]"
                    data-testid="photo-takeoff-walk-confirm-all-basis">
                    {stepPending.length
                      ? `${stepPending.length} complete ${stepLaneWords} mark(s) on this photo are still provisional — this step only, this photo only, no price`
                      : "nothing complete and unconfirmed of this kind on this photo — a line with one tap or a box with one corner is not a mark yet"}
                  </div>
                </div>
              ) : null}
              {/* SEND-252 (Howard ruled 2026-08-14) — WIDTH AND HEIGHT ON A
                  SELECTED OPENING, ON THE OPENINGS STEP. "The yellow plane box
                  already asks for this. Do not invent new plane math. Use the
                  check that box already describes." So these are the SAME two
                  boxes the rail's opening row carries — one component, one
                  `patchMark`, the same `width_in` / `height_in` the server
                  already prefers over its own read and already feeds to the
                  plane classifier. NOTHING about drawing a new opening moved,
                  DELETE THIS OPENING is untouched, and no mark is wiped: only
                  the selected box's two typed numbers are written. */}
              {WALK_STEPS[stepIdx].key === "openings" && selOpening ? (
                <div className="mt-3 -mx-3 px-3 pt-3 border-t border-[var(--border)]"
                  data-testid="photo-takeoff-walk-section-opening-size">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-1">
                    Size — {kindLabel(selOpening)}{selOpening.name ? ` · ${selOpening.name}` : ""}
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <OpeningSizeFields m={selOpening} patchMark={patchMark} />
                  </div>
                </div>
              ) : WALK_STEPS[stepIdx].key === "openings" ? (
                <div className="mt-3 -mx-3 px-3 pt-3 border-t border-[var(--border)] text-[9px] text-[var(--muted)]"
                  data-testid="photo-takeoff-walk-opening-size-none">
                  Click a boxed opening on the photo and its WIDTH and HEIGHT ask for your tape here. Nothing is guessed from the box.
                </div>
              ) : null}
              {/* SEND-298b (Howard ruled 2026-08-14) — THE DROPS AND THE
                  ELBOWS ARE COUNTS ON THIS STEP, NOT STEPS OF THEIR OWN.
                  SEND-298b FOLLOW-UP, HIS DEFECT: "clicking a gutter line
                  does not open the count boxes — tapping it does nothing, so
                  DROPS and ELBOWS never appear and Downspout, elbow and Pipe
                  Clips stay 0." TWO CAUSES, BOTH FIXED:
                   1. the whole mark overlay is `pointer-events-none`, so a
                      LINE could never be tapped at all — only its small
                      label chip could. A transparent HIT STROKE now sits on
                      every gutter line on this step and selects that run
                      (see `photo-takeoff-gutter-hit-*` on the canvas).
                   2. the boxes no longer WAIT to be found: EVERY gutter run
                      on this photo lists here with its own two boxes, so the
                      count he types always has somewhere to go.
                  Nothing about the math moved: LF, hangers and end caps are
                  untouched, no LF becomes a stick, and the elbow count is
                  his, never the drops doubled. */}
              {WALK_STEPS[stepIdx].key === "gutter" ? (
                <div className="mt-3 -mx-3 px-3 pt-3 border-t border-[var(--border)]"
                  data-testid="photo-takeoff-walk-section-gutter-counts">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-1">
                    Each run — elbows
                  </div>
                  {gutterRuns.length === 0 ? (
                    <div className="text-[9px] text-[var(--muted)]"
                      data-testid="photo-takeoff-walk-gutter-counts-none">
                      Draw a gutter line along this run and its ELBOWS ask for your count here. Nothing is counted from the feet.
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {gutterRuns.map((m, i) => (
                        <div key={m.id}
                          onClick={() => setSelectedId(m.id)}
                          className={`flex flex-wrap items-end gap-2 p-1.5 border cursor-pointer ${m.id === selectedId ? "border-[var(--ink)] bg-[var(--surface-muted)]" : "border-[var(--border)]"}`}
                          data-testid={`photo-takeoff-gutter-run-${m.id}`}>
                          <div className="w-full text-[9px] font-bold uppercase tracking-wider"
                            style={{ color: GUTTER }}>
                            Run {i + 1} · {qtyCell(m, null)}
                            {m.status === "confirmed" ? "" : " · not confirmed yet"}
                          </div>
                          <GutterCountFields m={m} patchMark={patchMark} />
                        </div>
                      ))}
                    </div>
                  )}
                  {/* SEND-298c — THE DOWNSPOUTS HE HAS DRAWN, counted as
                      lines. Not a box: a RECEIPT. One confirmed line is one
                      stick, and a line he has not confirmed yet says so
                      rather than being counted early. */}
                  <div className="mt-2 text-[9px] font-bold uppercase tracking-wider"
                    style={{ color: DOWNSPOUT }}
                    data-testid="photo-takeoff-gutter-downspout-tally">
                    {downspoutLines.length === 0
                      ? "No downspout drawn on this photo yet"
                      : `${downspoutLines.filter((m) => m.status === "confirmed").length} confirmed downspout line(s) = that many STICK · ${downspoutLines.length} drawn`}
                  </div>
                  <span className="mt-1 block text-[9px] text-[var(--muted)]"
                    data-testid="photo-takeoff-gutter-counts-basis">
                    {GUTTER_COUNTS_BASIS}
                  </span>
                </div>
              ) : null}
              {/* SEND-245 (Howard ruled 2026-08-14) — THE SAME LINKS, IN THE
                  RAIL: "click SIDING → siding step; the links stay in the
                  rail, so J, SOFFIT and SCALE are one click each. The photo
                  stays clean — links live in the rail, not on the photo."
                  SEND-215 therefore still holds: nothing was put back on the
                  photo while the walk runs. It is the SAME component the
                  bottom row renders — one source — and it still opens a step
                  and nothing else: no mark is wiped, the walk is not
                  restarted, and the SEND-244 gate is untouched. */}
              <div className="mt-3 -mx-3 px-3 pt-3 border-t border-[var(--border)]">
                <JumpRow steps={WALK_STEPS} stepIdx={stepIdx}
                  navBlock={navBlock} onJump={walkJump} />
              </div>
            </WalkPane>
          ) : (
          <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-[var(--border)] flex flex-col overflow-y-auto">
            {/* SEND-302c — HIDDEN, NOT DELETED: THE RAIL KEEPS NO SCALE PANEL.
                "Scale is already a step on the walk. Move those controls onto
                the Scale this photo step. They show only when that step is
                open." The walk's scale step renders THIS VERY SAME component
                (one source, see the `key === "scale"` branch above), so the
                two taps, the clear and the tape figure are untouched — and so
                is the scale math: a printed scale is still never trusted. */}
            {RAIL_SCALE_PANEL_SHOWN ? (
            <ScalePanel sc={sc} scale={scale} qty={qty} setTool={setTool}
              setScaleDraft={setScaleDraft} clearScale={clearScale}
              tapeFt={tapeFt} setTapeFt={setTapeFt} tapeIn={tapeIn}
              setTapeIn={setTapeIn} commitTape={commitTape} />
            ) : null}
            {/* SEND-304 — and the SAME toggle at the top of the grouped rail,
                so the words can go off outside the walk too. */}
            <div className="px-3 pt-3"><TagsToggle /></div>
            {/* SEND-318 — Measure on the grouped rail too (a page is a photo) */}
            <div className="px-3 pt-2"><MeasureChip /></div>

            <QuantitiesPanel qty={qty} orderAll={orderAll} estId={est.id} />

            {qty?.trim_rows?.length ? <TrimPanel qty={qty} /> : null}

            {/* SEND-255 — THE PANELS READ THE FROZEN MARKS FOR THE LENGTH OF
                A NODE DRAG: no ft², no plane, no live quantity and no row is
                recomputed on a mousemove. They pick the move up on mouseup,
                with the server's own figures. */}
            <LayersPanel marks={panelMarks} hidden={hidden}
              toggleLayer={toggleLayer} showAllLayers={() => setHidden([])} />

            <MarksPanel marks={panelMarks} qty={qty} selectedId={selectedId}
              setSelectedId={setSelectedId} busy={busy} products={products}
              productsNote={productsNote} sqftOf={sqftOf} qtyCell={qtyCell}
              receiptFor={receiptFor} gDims={gDims} ft2={ft2}
              patchMark={patchMark} delMark={delMark}
              importAnnotations={noAi ? null : importAnnotations}
              toggleSymmetric={toggleSymmetric}
              applyGablePitch={applyGablePitch} />

            <div className="p-3 border-t border-[var(--border)]">
              <button type="button" onClick={apply} disabled={busy}
                className="w-full px-3 py-2 bg-[var(--ai)] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
                data-testid="photo-takeoff-apply-btn">
                Write quantities to the estimate
              </button>
              <div className="text-[9px] text-[var(--muted)] mt-1 leading-snug">
                ft², counts — the photo lane only. No price, no priced line, no money. Protected estimates refuse (423).
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* the two-tap span asks for its real length */}
      {scaleAsk && (
        <div className="absolute inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid="photo-takeoff-scale-ask">
          <div className="bg-white border border-[var(--border)] max-w-sm w-full p-4">
            <div className="font-heading text-sm mb-1">How long is that span, really?</div>
            <div className="text-[11px] text-[var(--muted)] mb-3">
              This is the scale for THIS photo only. Nothing is carried from a blueprint or another photo.
            </div>
            <div className="flex items-center gap-1">
              <input value={spanFt} onChange={(e) => setSpanFt(e.target.value)} placeholder="ft" inputMode="decimal"
                className="w-16 border border-[var(--border)] px-2 py-1.5 text-sm" data-testid="photo-takeoff-span-ft" autoFocus />
              <input value={spanIn} onChange={(e) => setSpanIn(e.target.value)} placeholder="in" inputMode="decimal"
                className="w-16 border border-[var(--border)] px-2 py-1.5 text-sm" data-testid="photo-takeoff-span-in" />
              <button type="button"
                onClick={() => {
                  const inches = ftin(spanFt, spanIn);
                  if (!inches) { toast.error("Type the real length of that span — it is never guessed"); return; }
                  commitScale(scaleAsk.p1, scaleAsk.p2, inches);
                }}
                className="px-3 py-1.5 bg-[var(--ai)] text-white text-[11px] font-bold uppercase" data-testid="photo-takeoff-span-commit">set scale</button>
              <button type="button" onClick={() => setScaleAsk(null)} className="px-2 py-1.5 border border-[var(--border)] text-[11px] uppercase" data-testid="photo-takeoff-span-cancel">cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
